import { GeoUtils } from '../../utils/geo-utils';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import * as maplibregl from 'maplibre-gl';
import type { LngLatLike, Map as MapLibreMap, Marker as MapLibreMarker, PointLike } from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { environment } from 'src/environments/environment';
import { RequestService } from '../../services/request.service';
import { MessagingService } from '../../services/messaging.service';
import { Subscription } from 'rxjs';
import { getCameraMarkerIconUrl, getDefaultIconForMarkerType } from '../google-map/data-prepare';
import { TranslateService } from '@ngx-translate/core';
import { GeocodingService } from '../../services/geocoding.service';
import { LayoutUtilsService } from '../../services/utils/layout-utils.service';
import { MapService } from '../../services/map.service';
import html2canvas from 'html2canvas';
import { CaseService } from '../../services/case.service';

const OFFLINE_PMTILES_URL = environment.offlinePmtilesUrl;
const OFFLINE_PMTILES_FONTS_URL = 'assets/fonts/{fontstack}/{range}.pbf';
const OFFLINE_PMTILES_SPRITES_URL = 'assets/sprites/v3/light';

/** Data URIs for POI marker icons (hospital, police, clinic, doctors, fire station, court, military, transportation, government, education, services) */
const POI_ICON_SVG = {
  'poi-hospital': '<svg width="13px" height="13px" viewBox="0 0 0.813 0.813" version="1.2" baseProfile="tiny" xmlns="http://www.w3.org/2000/svg" overflow="inherit"><path fill="#c62828" d="M0.601 0.016a0.017 0.017 0 0 0 -0.016 0.016v0.228Q0.584 0.276 0.569 0.277H0.244A0.017 0.017 0 0 1 0.227 0.261V0.032A0.017 0.017 0 0 0 0.211 0.016H0.065a0.017 0.017 0 0 0 -0.016 0.016v0.747q0.001 0.015 0.016 0.016h0.146C0.22 0.796 0.228 0.789 0.228 0.779V0.52c0 -0.009 0.007 -0.016 0.016 -0.016h0.325Q0.584 0.504 0.586 0.52v0.26c0 0.009 0.007 0.016 0.016 0.016h0.146C0.757 0.796 0.765 0.79 0.765 0.78V0.032A0.017 0.017 0 0 0 0.749 0.016z"/></svg>',
  'poi-police': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 6l1.5 3 3 .5-2.2 2.1.5 3-2.8-1.5-2.8 1.5.5-3L7.5 9.5l3-.5z" stroke="#1565c0" stroke-width="2" fill="none"/></svg>',
  'poi-clinic': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M11 7h2v4h4v2h-4v4h-2v-4H7v-2h4z" stroke="#2e7d32" stroke-width="2" fill="none"/></svg>',
  'poi-doctors': '<svg fill="#6a1b9a" width="24px" height="24px" viewBox="-0.075 0 0.57 0.57" xmlns="http://www.w3.org/2000/svg" class="cf-icon-svg"><path d="M0.347 0.303v0.061a0.115 0.115 0 1 1 -0.23 0v-0.013A0.108 0.108 0 0 1 0.026 0.244V0.158a0.05 0.05 0 0 1 0.023 -0.042 0.036 0.036 0 1 1 0.014 0.031 0.017 0.017 0 0 0 -0.004 0.011v0.087a0.075 0.075 0 0 0 0.15 0V0.158a0.017 0.017 0 0 0 -0.003 -0.01 0.036 0.036 0 1 1 0.016 -0.031 0.05 0.05 0 0 1 0.02 0.041v0.087a0.108 0.108 0 0 1 -0.092 0.107v0.013a0.082 0.082 0 0 0 0.163 0V0.303a0.064 0.064 0 1 1 0.033 0zm0.014 -0.062a0.031 0.031 0 1 0 -0.031 0.031 0.031 0.031 0 0 0 0.031 -0.031z"/></svg>',
  'poi-fire': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 6c2 3-1 4 1 7 1 1 2 0 2-2 2 3 0 7-3 7s-5-3-3-6c1-2 2-3 3-6z" stroke="#ef6c00" stroke-width="2" fill="none"/></svg>',
  'poi-court': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#795548" xmlns:xlink="http://www.w3.org/1999/xlink" enable-background="new 0 0 280.245 280.245" width="24" height="24"><g><path d="M22.455 8.499c0.471 0 0.849 -0.407 0.849 -0.878V4.795c0 -0.471 -0.358 -0.988 -0.801 -1.15L12.831 0.121c-0.442 -0.161 -1.166 -0.161 -1.609 0L1.518 3.646c-0.443 0.161 -0.822 0.678 -0.822 1.149v2.826c0 0.471 0.421 0.878 0.892 0.878h0.478v12.675h-0.485c-0.473 0 -0.885 0.404 -0.885 0.877v1.113c0 0.473 0.412 0.835 0.885 0.835h20.896c0.473 0 0.828 -0.362 0.828 -0.835v-1.113c0 -0.473 -0.355 -0.877 -0.828 -0.877h-0.628v-12.675zm-8.999 0v12.675h-2.826v-12.675zm-8.564 0h2.912v12.675h-2.912zm14.13 12.675h-2.74v-12.675h2.74z"/></g></svg>',
  'poi-military': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 6l2 4 4 .5-3 3 .8 4-3.8-2-3.8 2 .8-4-3-3 4-.5z" stroke="#424242" stroke-width="2" fill="none"/></svg>',

  'poi-train': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="8" y="7" width="8" height="7" rx="1" stroke="#1976d2" stroke-width="2" fill="none"/><circle cx="10" cy="12" r="1" fill="#1976d2"/><circle cx="14" cy="12" r="1" fill="#1976d2"/><path d="M9 15l-1 2M15 15l1 2" stroke="#1976d2" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>',

  'poi-airport': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 5v14M6 12l6-2 6 2" stroke="#00897b" stroke-width="2" fill="none"/></svg>',

  'poi-school': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M6 11l6-3 6 3-6 3-6-3z" stroke="#f57c00" stroke-width="2" fill="none"/><path d="M9 14v2c2 1 4 1 6 0v-2" stroke="#f57c00" stroke-width="2" fill="none"/></svg>',

  'poi-townhall': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M7 10h10M8 10v6M12 10v6M16 10v6" stroke="#5d4037" stroke-width="1.5" fill="none"/></svg>',

  'poi-post': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M8 10h8v4H8zM8 10l4 3 4-3" stroke="#d84315" stroke-width="1.5" fill="none"/></svg>',

  'poi-library': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M7 9c2-1 4-1 5 0v6c-1-1-3-1-5 0zM17 9c-2-1-4-1-5 0v6c1-1 3-1 5 0z" stroke="#7b1fa2" stroke-width="2" fill="none"/></svg>',

  'poi-pharmacy': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M11 7h2v4h3v2h-3v4h-2v-4H8v-2h3z" stroke="#009688" stroke-width="2" fill="none"/></svg>',

  'poi-bank': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M7 10h10v6H7z" stroke="#3f51b5" stroke-width="2" fill="none"/><path d="M10 11.5h2.2a1.3 1.3 0 1 1 0 2.6H10m0-3.9h2.1" stroke="#3f51b5" stroke-width="1.3" fill="none"/></svg>',

  'poi-fuel': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="8" y="7" width="5" height="9" rx="1" stroke="#ff6f00" stroke-width="2" fill="none"/><path d="M14 9l2 2v4a1 1 0 0 1-1 1" stroke="#ff6f00" stroke-width="1.5" fill="none"/></svg>',

  'poi-bus': '<svg width="15px" height="15px" viewBox="0 0 0.281 0.281" version="1.1" id="bus" xmlns="http://www.w3.org/2000/svg"><path fill="#00796b" id="path8043" d="M0.075 0C0.049 0 0.019 0.014 0.019 0.051V0.225s0 0.019 0.019 0.019v0.019s0 0.019 0.019 0.019 0.019 -0.019 0.019 -0.019v-0.019h0.131v0.019s0 0.019 0.019 0.019 0.019 -0.019 0.019 -0.019v-0.019s0.019 0 0.019 -0.019V0.051C0.263 0.013 0.239 0 0.214 0zm0.005 0.028h0.122a0.005 0.005 0 1 1 0 0.009h-0.122a0.005 0.005 0 1 1 0 -0.009M0.056 0.056h0.169c0.019 0 0.019 0.018 0.019 0.018V0.131s0 0.019 -0.019 0.019H0.056C0.037 0.15 0.037 0.131 0.037 0.131V0.075s0 -0.019 0.019 -0.019m0 0.131a0.019 0.019 0 1 1 0 0.037 0.019 0.019 0 0 1 0 -0.037m0.169 0a0.019 0.019 0 1 1 0 0.037 0.019 0.019 0 0 1 0 -0.037"/></svg>',

  'poi-tram': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="8" y="8" width="8" height="6" rx="1" stroke="#512da8" stroke-width="2" fill="none"/><path d="M9 15l-1 2M15 15l1 2" stroke="#512da8" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>',

  'poi-parking': '<svg width="15px" height="15px" viewBox="0 0 0.282 0.282" version="1.1" id="parking" xmlns="http://www.w3.org/2000/svg"><path fill="#1e88e5" d="M0.222 0.157a0.1 0.1 0 0 1 -0.063 0.019h-0.05v0.087H0.057V0.019h0.108a0.083 0.083 0 0 1 0.058 0.018 0.083 0.083 0 0 1 0.021 0.058 0.083 0.083 0 0 1 -0.021 0.062M0.183 0.07a0.042 0.042 0 0 0 -0.029 -0.008H0.109V0.133h0.048A0.042 0.042 0 0 0 0.184 0.124 0.042 0.042 0 0 0 0.193 0.097 0.042 0.042 0 0 0 0.184 0.071"/></svg>',

  'poi-polyclinic': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M9 9h2V7h2v2h2v2h-2v2h-2v-2H9z" stroke="#43a047" stroke-width="2" fill="none"/></svg>',
  'poi-embassy': '<svg fill="#6d4c41" width="24px" height="24px" viewBox="0 0 1.5 1.5" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M0.69 0.336v0.162c0.162 0 0.108 0.054 0.216 0.054H0.96V0.39h-0.054c-0.108 0 -0.054 -0.054 -0.216 -0.054M0.27 0.63c-0.017 0 -0.03 0.013 -0.03 0.03v0.09h1.02v-0.09a0.03 0.03 0 0 0 -0.03 -0.03Zm-0.24 0.18a0.03 0.03 0 0 0 -0.03 0.03v0.63a0.03 0.03 0 0 0 0.03 0.03h1.44c0.017 0 0.03 -0.013 0.03 -0.03V0.84c0 -0.017 -0.013 -0.03 -0.03 -0.03Zm0.15 0.15h0.09v0.15H0.18Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09ZM0.18 1.2h0.09v0.15H0.18Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Zm0.21 0h0.09v0.15h-0.09Z"/></svg>'
};

/** Custom map area: persisted shape with color and optional label */
export interface CustomMapAreaData {
  id?: string;
  color: string;
  shape: 'polygon' | 'box' | 'fan';
  coordinates: [number, number][];
  label?: string;
}

const METERS_PER_YARD = 0.9144;
const YARDS_PER_MILE = 1760;

function getRoundScaleDistance(num: number): number {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
  let d = num / pow10;
  d = d >= 10 ? 10 : d >= 5 ? 5 : d >= 3 ? 3 : d >= 2 ? 2 : 1;
  return pow10 * d;
}

function setScaleBar(container: HTMLElement, maxWidth: number, maxDistance: number, unit: string): void {
  const distance = getRoundScaleDistance(maxDistance);
  const ratio = maxDistance > 0 ? (maxWidth / maxDistance) * distance : maxWidth;
  container.style.width = `${ratio}px`;
  container.innerHTML = `${distance}${unit}`;
}

/** MapLibre ScaleControl only supports imperial/metric; English UI uses yards (see i18n Length (m) → yd). */
class YardsScaleControl implements maplibregl.IControl {
  private map?: MapLibreMap;
  private container?: HTMLElement;
  private readonly onMove = () => this.update();
  private readonly maxWidth: number;

  constructor(options?: { maxWidth?: number }) {
    this.maxWidth = options?.maxWidth ?? 100;
  }

  onAdd(map: MapLibreMap): HTMLElement {
    this.map = map;
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-scale';
    this.update();
    map.on('move', this.onMove);
    map.on('zoom', this.onMove);
    return this.container;
  }

  onRemove(): void {
    this.map?.off('move', this.onMove);
    this.map?.off('zoom', this.onMove);
    this.map = undefined;
    this.container = undefined;
  }

  getDefaultPosition(): maplibregl.ControlPosition {
    return 'bottom-left';
  }

  private update(): void {
    const map = this.map;
    const container = this.container;
    if (!map || !container) {
      return;
    }

    const y = map.getContainer().clientHeight / 2;
    const x = map.getContainer().clientWidth / 2;
    const left = map.unproject([x - this.maxWidth / 2, y]);
    const right = map.unproject([x + this.maxWidth / 2, y]);
    const globeWidth = Math.round(map.project(right).x - map.project(left).x);
    const barWidth = Math.min(this.maxWidth, globeWidth, map.getContainer().clientWidth);
    const maxMeters = left.distanceTo(right);
    const maxYards = maxMeters / METERS_PER_YARD;

    if (maxYards > YARDS_PER_MILE) {
      setScaleBar(container, barWidth, maxYards / YARDS_PER_MILE, ' mi');
    } else {
      setScaleBar(container, barWidth, maxYards, ' yd');
    }
  }
}

@Component({
  selector: 'app-offline-map',
  templateUrl: './offline-map.component.html',
  styleUrls: ['./offline-map.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class OfflineMapComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef<HTMLDivElement>;

  // Maximum zoom level for offline/online MapLibre map to avoid blank tiles
  private readonly MAX_ZOOM = 19;

  private subscriptions: Subscription[] = [];
  display: any;
  @Input() lnglatdefault: any = environment.lnglatdefault;
  public center: any = this.lnglatdefault;
  @Input() zoom: number = 15;
  options = {
    streetViewControl: false,
    mapTypeControl: true,
    fullscreenControl: false,
    minZoom: 3,
    disableDoubleClickZoom: false,
    panControl: true,
    draggable: true,
    clickableIcons: false,
    keyboardShortcuts: false,
    scaleControl: true,
    styles: []
  };
  markersOriginal: any[] = [];
  markersOriginalAdjusted: any[] = [];
  markers: any[] = [];
  usersMarkers: any[] = [];
  marker: any = {};
  markerClustererImagePath: string = 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m';
  showCluster: boolean = false;
  geoLocationInit: boolean = false;
  defaultMarkerIcon: string = undefined;
  private currentUser: any = undefined;
  public isAdmin: boolean = false;
  public isSuperAdmin: boolean = false;
  public isCommander: boolean = false;
  polylines: Map<string, any> = new Map();
  private polylineClickListeners: Map<string, { line?: (e: any) => void; points?: (e: any) => void }> = new Map();
  private polylineDraftEditHandles: Map<string, maplibregl.Marker[]> = new Map();
  private polylineDraftActiveDrags: Map<string, number> = new Map();
  private readonly draftSafeRoutePolylineIds = new Set(['case-map-draw-polyline', 'dashboard-map-draw-polyline']);
  polylinesMain: any[][] = [];
  polylinePath: any[] = [];
  polylineOptions: any = {
    path: this.polylinePath,
    visible: true
  };
  observer: IntersectionObserver;
  private mapResizeObserver: ResizeObserver | null = null;

  @Input() lazyLoad: boolean = false;
  @Input() forBuildingFlow: boolean = false;
  @Input() isCasePage: boolean = false;
  // Track markers that were recently manually updated to prevent Firebase from overwriting them
  private recentlyUpdatedMarkers: Map<string, { timestamp: number, position: { lat: number, lng: number } }> = new Map();
  private readonly MANUAL_UPDATE_TIMEOUT = 2000; // 2 seconds
  private natsListeners: Map<string, any> = new Map(); // Track NATS listeners for cleanup
  private cameraStreamChangedSubscribed = false;
  private natsSubjectPrefix: string | undefined;

  public selectedMarker: any = 'Info Window content';

  @Input()
  set styles(styles: any) {
    if (this.markersElem) {
      if (this.options) {
        this.options.styles = styles;
      }
      // For MapLibre, we can update the style
      if (this.map && styles) {
        this.map.setStyle(this.buildStyle(styles, true));
        // Re-add custom map area layers and POI layers after style load (setStyle removes them)
        this.map.once('style.load', () => {
          if (!this.map) return;
          this.addPoiLayersWhenReady();
          this.addCustomAreaLayersIfNeeded();
          this.updateCustomAreasSource();
          if (this.editMode) {
            this.updatePreviewGeometry();
            this.syncEditPointMarkers();
          }
        });
      }
    }
  }

  @Input() currentGeoLocation: any = undefined;
  @Input() controlPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' = 'bottom-left';
  @Output() shapeClick = new EventEmitter<any>(); // Emits ID or Data of clicked shape
  /** Right-click menu: `centerHere` or `copyCoordinates` at the map point. */
  @Output() mapContextMenuAction = new EventEmitter<{
    action: 'centerHere' | 'copyCoordinates';
    lngLat: { lng: number; lat: number };
  }>();
  /** When false, hides timeline note actions (Add Note, POI, car, threat) from the right-click menu. */
  @Input() showContextMenuNoteActions: boolean = true;
  /** When true, show Dispatch action in context menu even when not in case-page mode. */
  @Input() showContextMenuDispatchAction: boolean = false;
  /** When true, Dispatch in the context menu is visible but not clickable (e.g. threat already resolved). */
  @Input() contextMenuDispatchDisabled: boolean = false;
  @Input() width: string = '100%';
  @Input() height: string = '30vh';
  @Input() enableTrackMarkers: boolean = false;
  @Input() caseId: string = undefined;
  private trackingMarkers: string[] = ['investigator', 'investigator-group', 'camera'];
  @Input() addMyMarker: boolean = true;
  @Input() isClickable: boolean = true;
  @Input() enableAddessOnClick: boolean = false; // enable here needed
  @Input() mapDragGeoEnabled: boolean = false;
  @Input() isHoverable: boolean = false;
  @Input() isIconLabelsOn: boolean = true;
  @Input() goToLocationMarker: boolean = false;
  @Input()
  set isDraggable(isDraggable: boolean) {
    this.options.draggable = isDraggable;
    if (this.map) {
      if (isDraggable) {
        this.map.dragPan.enable();
      } else {
        this.map.dragPan.disable();
      }
    }
  }

  @Input()
  set setGeoLocation(geoLocation: any) {
    if (geoLocation && geoLocation !== '' && geoLocation !== undefined) {
      if (typeof geoLocation === 'string') {
        try {
          this.currentGeoLocation = JSON.parse(geoLocation);
        } catch (e) {
          console.error('Error parsing geoLocation', e);
          return;
        }
      } else {
        this.currentGeoLocation = geoLocation;
      }
      if (!this.geoLocationInit) {
        this.center = this.currentGeoLocation;
        this.zoom = 15;
        this.geoLocationInit = true;
      }
      this.setPin(this.currentGeoLocation);
      if (this.goToLocationMarker) {
        this.easeToPinLocation();
      }
    } else {
      this.clearLocationMarker();
      this.currentGeoLocation = undefined;
    }
  }

  @Input()
  set setCenter(geoLocation: any) {
    if (geoLocation && geoLocation !== '') {
      if (typeof geoLocation === 'string') {
        try {
          this.currentGeoLocation = JSON.parse(geoLocation);
        } catch (e) {
          console.error('Error parsing setCenter geoLocation', e);
          return;
        }
      } else {
        this.currentGeoLocation = geoLocation;
      }

      if (!this.geoLocationInit) {
        this.center = this.currentGeoLocation;
        this.geoLocationInit = true;
      }
    }
  }

  @Input()
  set changeCenter(geoLocation: any) {
    if (geoLocation && geoLocation !== '') {
      if (typeof geoLocation === 'string') {
        try {
          this.currentGeoLocation = JSON.parse(geoLocation);
        } catch (e) {
          console.error('Error parsing changeCenter geoLocation', e);
          return;
        }
      } else {
        this.currentGeoLocation = geoLocation;
      }

      this.center = this.currentGeoLocation;
      this.zoom = 15;
      if (this.map) {
        this.map.setCenter(this.normalizeCenter(this.currentGeoLocation));
        this.map.setZoom(this.zoom);
        this.refreshViewportAfterCameraMove();
      }
    }
  }

  @Input()
  set changeCenterAddPin(geoLocation: any) {
    if (geoLocation && geoLocation !== '') {
      if (typeof geoLocation === 'string') {
        try {
          this.currentGeoLocation = JSON.parse(geoLocation);
        } catch (e) {
          console.error('Error parsing changeCenterAddPin geoLocation', e);
          return;
        }
      } else {
        this.currentGeoLocation = geoLocation;
      }

      this.center = this.currentGeoLocation;
      this.zoom = 15;
      if (this.map) {
        this.map.setCenter(this.normalizeCenter(this.currentGeoLocation));
        this.map.setZoom(this.zoom);
        this.refreshViewportAfterCameraMove();
      }
      this.addPin(this.currentGeoLocation);
    }
  }

  @Input()
  set markerIcon(icon: string) {
    if (icon) {
      this.defaultMarkerIcon = icon;
      if (this.currentGeoLocation) {
        this.setPin(this.currentGeoLocation);
      }
    }
  }

  @Input()
  set setMapTypeControl(mapTypeControl: boolean) {
    this.options.mapTypeControl = mapTypeControl;
  }

  @Input()
  set setScaleControl(scaleControl: boolean) {
    this.options.scaleControl = scaleControl;
  }

  @Output() address = new EventEmitter<string>();
  @Output() geoLocation = new EventEmitter<any>();
  @Output() markerSelect = new EventEmitter<any>();
  @Output() markerInfoSelect = new EventEmitter<any>();
  @Output() markerDelete = new EventEmitter<any>();
  @Output() markerDragEnd = new EventEmitter<any>();
  @Output() markerRightClick = new EventEmitter<any>();
  @Output() mapSelect = new EventEmitter<any>();
  @Output() mapInitialized = new EventEmitter<any>();
  @Output() mapReady = new EventEmitter<any>();
  @Output() tilesloaded = new EventEmitter<any>();
  @Output() addressPinData = new EventEmitter<any>();
  @Output() addressJPPinData = new EventEmitter<any>();
  @Output() mapDragGeo = new EventEmitter<any>();
  /** Emitted when polygon undo stack changes (add/drag/delete vertex or undo) so parents can refresh UI. */
  @Output() polygonUndoStateChanged = new EventEmitter<void>();
  @Output() customAreaCreated = new EventEmitter<CustomMapAreaData>();

  @Input() editMode: boolean = false;
  /**
   * Map-tools slider: icon scale as % of intrinsic size (0–100, step 10). Debounced before refresh.
   * 0 = hidden; 100 = original/natural icon size. Applied via CSS transform (no fixed px on images).
   */
  @Input() cameraIconScalePercent: number = 100;
  @Input() investigatorIconScalePercent: number = 100;
  @Input() investigatorGroupIconScalePercent: number = 100;
  @Input() noteIconScalePercent: number = 100;
  @Input() carOfInterestIconScalePercent: number = 100;
  @Input() annotationIconScalePercent: number = 100;
  @Input() personOfInterestIconScalePercent: number = 100;
  @Input() locationIconScalePercent: number = 100;
  @Input() threatIconScalePercent: number = 100;
  @Input() customMarkerIconScalePercent: number = 100;
  @Input() buildingIconScalePercent: number = 100;
  @Input() cameraSectorOpacityPercent: number = 100;
  @Input() isShowDispatch: boolean = false;
  @Input() isShowAddPatrolCamera: boolean = false;
  private readonly markerIconScaleMax = 100;
  private readonly sectorOpacityMax = 0.2;
  /** Cluster nearby markers of same category into one pin. */
  @Input() clusterMarkersByCategory: boolean = true;
  /** Toggle combining nearby markers/icons on offline map. */
  @Input() combineIconsOnOfflineMap: boolean = true;
  /** Keep false for maps that need every loaded marker rendered after fit, even when initial bounds are unstable. */
  @Input() filterMarkersToViewport: boolean = true;
  /** Disable clustering at and above this zoom. */
  @Input() markerClusterMaxZoom: number = 17;
  /** Screen-pixel radius used to cluster nearby markers. */
  @Input() markerClusterRadiusPx: number = 56;
  /** Skip O(n²) overlap nudging when marker count exceeds this (clustering handles density). */
  private readonly adjustPointsSkipThreshold = 120;
  /** Camera FOV wedges are expensive; omit when zoomed out past this level. */
  private readonly cameraSectorMinZoom = 13;
  /** Selected camera mongo ids (from dashboard camera selection mode). */
  @Input() selectedCamsIds: string[] = [];
  private markerClusterSerial: number = 0;

  // Edit mode: draw custom areas (polygons) with points
  private readonly CUSTOM_AREAS_SOURCE_ID = 'custom-areas';
  private readonly CUSTOM_AREA_PREVIEW_SOURCE_ID = 'custom-area-preview';
  private readonly PENDING_AREAS_SOURCE_ID = 'pending-areas';
  private readonly EDIT_POINT_MARKER_CLASS = 'offline-map-edit-point';
  private readonly EDGE_MIDPOINT_MARKER_CLASS = 'offline-map-edge-midpoint';
  /** Midpoint handles on each edge (semi-transparent); drag inserts a vertex and moves it. */
  private edgeMidpointMarkers: maplibregl.Marker[] = [];
  /** Right-click menu position (viewport px) and point (for actions). */
  contextMenuVisible = false;
  /** Bottom-right MapTiler layer picker. Shown only while the MapTiler basemap is active. */
  showMapTilerOptions = false;
  mapTilerOptionsOpen = false;
  mapTilerStyleSectionOpen = false;
  mapTilerLayersSectionOpen = true;
  readonly mapTilerStyles: { id: string; labelKey: string }[] = [
    { id: 'streets-v2', labelKey: 'Streets' },
    { id: 'satellite-v4', labelKey: 'Satellite' },
    { id: 'hybrid-v4', labelKey: 'Hybrid' },
    { id: 'outdoor-v4', labelKey: 'Outdoor' },
    { id: 'topo-v4', labelKey: 'Topo' },
    { id: 'winter-v4', labelKey: 'Winter' },
    { id: 'landscape-v4', labelKey: 'Landscape' },
    { id: 'bright-v2', labelKey: 'Bright' },
    { id: 'openstreetmap', labelKey: 'OpenStreetMap' },
    { id: 'ocean-v4', labelKey: 'Ocean' },
    { id: 'aquarelle-v4', labelKey: 'Aquarelle' },
    { id: 'dataviz-v4', labelKey: 'Dataviz' },
    { id: 'backdrop-v4', labelKey: 'Backdrop' },
    { id: 'base-v4', labelKey: 'Base' },
  ];
  mapTilerStyleId = 'streets-v2';
  contextMenuLeft = 0;
  contextMenuTop = 0;
  /** Track right-click drag to close/suppress context menu on drag. */
  private rightMouseDownStartPoint: { x: number; y: number } | null = null;
  private rightMouseDragDetected = false;
  /** Reverse-geocoded label for the context-menu point (from GeocodingService.resolveAddress). */
  contextMenuAddress: string | null = null;
  contextMenuAddressLoading = false;
  private contextMenuLngLat: { lng: number; lat: number } | null = null;
  /** Camera records (`_id`, `camera_id`) whose FOV sectors contain the context-menu point (right-click). */
  contextMenuCoverageCameras: { _id: string; camera_id: string }[] = [];
  /** Case map / dashboard: building id when right-click hit a building footprint polygon (not user-drawn areas). */
  contextMenuCaseBuildingFootprintId: string | null = null;
  /** Coalesce zoomend+moveend bursts; each new event cancels the pending frame so only the last runs. */
  private clusterViewportRefreshRaf: number | null = null;
  private markersVisualRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly markersVisualRefreshDebounceMs = 150;
  /** Hard cap on HTML markers in the DOM (viewport + clustering should stay well below this). */
  private readonly maxRenderedMarkers = 350;
  private readonly onClusterViewportChanged = () => {
    if (!this.mapLoaded || !this.map) {
      return;
    }
    const needsRefresh =
      this.filterMarkersToViewport ||
      (this.combineIconsOnOfflineMap && this.clusterMarkersByCategory);
    if (!needsRefresh) {
      return;
    }
    if (this.clusterViewportRefreshRaf != null) {
      cancelAnimationFrame(this.clusterViewportRefreshRaf);
      this.clusterViewportRefreshRaf = null;
    }
    this.clusterViewportRefreshRaf = requestAnimationFrame(() => {
      this.clusterViewportRefreshRaf = null;
      // Re-enter Angular so debounced marker refresh timers run reliably after runOutsideAngular init.
      this.zone.run(() => this.scheduleMarkersVisualRefresh());
    });
  };
  private readonly onMapPolygonBodyMousedown = (e: maplibregl.MapMouseEvent) => {
    try {
      if (!this.editMode || !this.map || this.currentPolygonPoints.length < 3) {
        return;
      }
      if (e.originalEvent?.button !== 0) {
        return;
      }
      if (e.originalEvent?.target) {
        const target = e.originalEvent.target as HTMLElement;
        if (
          target.closest('.offline-marker') ||
          target.closest('.maplibregl-marker') ||
          target.closest('.' + this.EDIT_POINT_MARKER_CLASS) ||
          target.closest('.' + this.EDGE_MIDPOINT_MARKER_CLASS)
        ) {
          return;
        }
      }
      if (!e.lngLat) {
        return;
      }
      if (!this.isPreviewFillHit(e)) {
        return;
      }
      e.preventDefault();
      if (e.originalEvent) {
        e.originalEvent.preventDefault();
      }
      this.zone.run(() => this.beginPolygonBodyDrag(e.lngLat));
    } catch (err) {
      console.warn('Polygon body mousedown error:', err);
    }
  };

  /** Topmost custom-area fill feature id at a map point, if any. */
  private getCustomAreaPolygonIdAtPoint(point: PointLike): string | null {
    const customFillLayerId = this.CUSTOM_AREAS_SOURCE_ID + '-fill';
    if (!this.map?.getLayer(customFillLayerId)) {
      return null;
    }
    try {
      const hits = this.map!.queryRenderedFeatures(point, {
        layers: [customFillLayerId]
      });
      const first = hits[0] as GeoJSON.Feature<GeoJSON.Polygon> | undefined;
      const rawId = first?.properties && (first.properties as { id?: string }).id;
      if (typeof rawId === 'string' && rawId.length > 0) {
        return rawId;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  /**
   * Building/site click should match "Enter Site" context-menu behavior.
   * Returns true when the click was consumed by opening floor plans.
   */
  private handleEnterSiteLeftClick(point?: PointLike): boolean {
    if (!point || this.editMode) {
      return false;
    }
    const rawId = this.getCustomAreaPolygonIdAtPoint(point);
    if (!rawId) {
      return false;
    }
    if (this.forBuildingFlow) {
      this.openFloorPlansByBuildingId(rawId);
      return true;
    }
    if (this.isCasePage && !rawId.startsWith('user-drawn-')) {
      this.openFloorPlansByBuildingId(rawId);
      return true;
    }
    return false;
  }

  /** Emits the same command used by "Enter Site" based on current map mode. */
  private openFloorPlansByBuildingId(buildingId: string): void {
    const id = String(buildingId || '').trim();
    if (!id) {
      return;
    }
    if (this.forBuildingFlow) {
      this.mapSelect.emit({ openFloorPlansViewer: true, buildingId: id });
      return;
    }
    if (this.isCasePage) {
      this.mapService.mapContextMenuCommand.next({ command: 'floorPlansViewer', buildingId: id });
    }
  }

  /** Extract building/site id from a building marker payload. */
  private getBuildingIdFromMarker(mark: any): string | null {
    const candidates = [mark?.id, mark?._id, mark?.buildingId, mark?.building_id];
    for (const candidate of candidates) {
      const value = String(candidate ?? '').trim();
      if (value) {
        return value;
      }
    }
    return null;
  }

  private readonly mapContextMenuHandler = (e: maplibregl.MapMouseEvent) => {
    try {
      e.preventDefault();
      if (e.originalEvent) {
        e.originalEvent.preventDefault();
      }
      // Right-button drag should close menu and not reopen on mouseup/contextmenu.
      if (this.rightMouseDragDetected) {
        this.rightMouseDragDetected = false;
        return;
      }
      if (e.originalEvent && e.originalEvent.target) {
        const target = e.originalEvent.target as HTMLElement;
        if (
          target.closest('.offline-marker') ||
          target.closest('.maplibregl-marker') ||
          target.closest('.' + this.EDIT_POINT_MARKER_CLASS) ||
          target.closest('.' + this.EDGE_MIDPOINT_MARKER_CLASS)
        ) {
          return;
        }
      }
      if (!e.lngLat || !e.originalEvent) {
        return;
      }
      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;
      const oe = e.originalEvent;
      let savedPolygonId: string | null = null;
      let caseBuildingFootprintId: string | null = null;
      if (this.forBuildingFlow && !this.editMode) {
        const rawId = this.getCustomAreaPolygonIdAtPoint(e.point);
        if (rawId) {
          savedPolygonId = rawId;
        }
      }
      if (this.isCasePage && !this.forBuildingFlow && !this.editMode) {
        const rawId = this.getCustomAreaPolygonIdAtPoint(e.point);
        if (rawId && !rawId.startsWith('user-drawn-')) {
          caseBuildingFootprintId = rawId;
        }
      }
      this.zone.run(() => {
        this.contextMenuSavedPolygonBuildingId = savedPolygonId;
        this.contextMenuCaseBuildingFootprintId = caseBuildingFootprintId;
        this.contextMenuLngLat = { lng, lat };
        this.contextMenuLeft = oe.clientX;
        this.contextMenuTop = oe.clientY;
        this.contextMenuVisible = true;
        this.contextMenuAddress = null;
        this.contextMenuAddressLoading = true;
        this.contextMenuCoverageCameras = this.computeCamerasInSectorAt(lng, lat);
        this.loadContextMenuAddress(lat, lng);
        this.rightMouseDragDetected = false;
      });
    } catch (err) {
      console.warn('Map context menu error:', err);
    }
  };
  private readonly onMapRightMouseDown = (e: maplibregl.MapMouseEvent) => {
    const oe = e.originalEvent as MouseEvent | undefined;
    if (!oe || oe.button !== 2) {
      return;
    }
    this.rightMouseDownStartPoint = { x: oe.clientX, y: oe.clientY };
    this.rightMouseDragDetected = false;
  };
  private readonly onMapRightMouseMove = (e: maplibregl.MapMouseEvent) => {
    if (!this.rightMouseDownStartPoint) {
      return;
    }
    const oe = e.originalEvent as MouseEvent | undefined;
    if (!oe) {
      return;
    }
    const dx = oe.clientX - this.rightMouseDownStartPoint.x;
    const dy = oe.clientY - this.rightMouseDownStartPoint.y;
    // Close immediately on first actual movement while right button is held.
    if (dx === 0 && dy === 0) {
      return;
    }
    this.rightMouseDragDetected = true;
    if (this.contextMenuVisible) {
      this.zone.run(() => this.clearContextMenuState());
    }
  };
  private readonly onMapMoveStartCloseContextMenu = (e: any) => {
    if (!this.contextMenuVisible) {
      return;
    }
    const oe = e?.originalEvent as MouseEvent | undefined;
    if (oe?.button === 2 || this.rightMouseDownStartPoint) {
      this.rightMouseDragDetected = true;
    }
    this.zone.run(() => this.clearContextMenuState());
  };
  private currentPolygonPoints: [number, number][] = [];
  /** Snapshots before each polygon edit (add point, drag vertex, delete vertex) for undo. */
  private readonly polygonUndoStack: [number, number][][] = [];
  private static readonly POLYGON_UNDO_MAX = 50;
  /** Max screen distance (px) from click to edge to insert a vertex on that edge instead of appending. */
  private static readonly POLYGON_EDGE_INSERT_PX = 18;
  /** Site mapping: user is drawing a new building outline after "Add Building Here". */
  buildingFootprintDraftActive = false;
  private buildingDraftAnchor: { lat: number; lng: number; address?: string } | null = null;
  /** When editing an existing saved footprint, save updates this building id instead of opening the new-building dialog. */
  private savedFootprintEditBuildingId: string | null = null;
  /** Right-click hit a saved custom-area polygon (`forBuildingFlow`); show edit/delete actions. */
  contextMenuSavedPolygonBuildingId: string | null = null;
  /** Custom map areas (persisted) with color and shape metadata */
  private customMapAreasData: CustomMapAreaData[] = [];
  /** Areas drawn in current edit session; added to customMapAreasData only when user confirms "Finish area" */
  private pendingAreas: CustomMapAreaData[] = [];
  private editModeDblclickListener: (() => void) | null = null;
  private editPointMarkers: maplibregl.Marker[] = [];
  /** Drag whole polygon by grabbing the filled interior (closed ring only). */
  private polygonBodyDragActive = false;
  private polygonBodyDragStartLngLat: { lng: number; lat: number } | null = null;
  private polygonBodyDragSnapshot: [number, number][] | null = null;
  private polygonBodyDragMoved = false;
  private suppressNextMapClick = false;
  private polygonBodyDragMoveListener: ((ev: MouseEvent) => void) | null = null;
  private polygonBodyDragUpListener: ((ev: MouseEvent) => void) | null = null;
  private polygonBodyDragUndoPushed = false;

  // Sector (FOV) properties
  private sectorSourceId = 'sector-source';
  private sectorLayerId = 'sector-layer';
  private sectorOutlineLayerId = 'sector-outline-layer';
  private sectors = new Map<string, {
    id: string;
    center: { lat: number, lng: number };
    radius: number;
    azimuth: number;
    fov: number;
    /** Original marker payload when sector is from `addMarkers` (e.g. camera FOV). */
    data?: any;
    centerMarker?: MapLibreMarker;
    controlMarker?: MapLibreMarker;
  }>();

  private boxes = new Map<string, {
    id: string;
    center: { lat: number, lng: number };
    width: number;
    height: number;
    azimuth: number;
    centerMarker?: MapLibreMarker;
    controlMarker?: MapLibreMarker;
  }>();

  // Internal state
  private map: MapLibreMap | null = null;
  private markerRefs = new Map<string, MapLibreMarker>();
  /** Detect cluster/pin visual changes when the same DOM id is reused after zoom. */
  private markerRenderedFingerprints = new Map<string, string>();
  /** Coalesce full marker DOM rebuilds so repeated map events do not leave a blank frame. */
  private markerVisualRefreshRaf: number | null = null;
  /** True once at least one data marker pin has been rendered (excludes the optional single "my pin"). */
  private hasRenderedDataMarkers: boolean = false;
  /** Mission control: keep markers and overlays queued until the basemap has painted. */
  private holdOverlaysUntilPaint = false;
  private initialOverlayReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  /** Initial-load recovery when first render paints zero data markers until manual zoom. */
  private initialMarkerRenderRetryTimer: any = null;
  private initialMarkerRenderRetryCount: number = 0;
  private readonly maxInitialMarkerRenderRetries = 3;
  private mapInit: boolean = false;
  private mapLoaded: boolean = false;
  private isShowingMarkers: boolean = false;
  private pendingShowMarkers:
    | {
      typeToHide: string[];
      calculateCenterZoom: boolean;
    }
    | null = null;
  private currentStyles: any = undefined;
  private pmtilesProtocolRegistered: boolean = false;
  private infoPopup: maplibregl.Popup | null = null;
  private popupCloseTimeout: any = null;
  private currentPopupMarker: any = null;
  private pmtilesAvailable: boolean | null = null; // null = not checked yet, true/false = checked
  private pmtilesCheckPromise: Promise<boolean> | null = null;
  /** True when MapTiler quota/rate-limit was detected; online basemap should avoid it. */
  private mapTilerLimited = false;
  private mapTilerCheckPromise: Promise<boolean> | null = null;
  /** Organization-configured MapTiler API key (falls back to environment key). */
  private configuredMapTilerApiKey: string = environment.mapTilerApiKey || '';
  /** Organization setting: show MapTiler Building 3D extrusion when MapTiler is the map tool. */
  private mapTiler3dEnabled = false;
  /** Organization setting: raise the ground with MapTiler terrain elevation. */
  private mapTilerElevationEnabled = false;
  /** Organization setting: shade slopes with MapTiler hillshade. */
  private mapTilerHillshadeEnabled = false;
  /** True once the live basemap is the MapTiler vector style, not an OpenStreetMap fallback. */
  private usingMapTilerBasemap = false;
  private readonly MAPTILER_TERRAIN_SOURCE_ID = 'maptiler-terrain-dem';
  private readonly MAPTILER_HILLSHADE_LAYER_ID = 'maptiler-hillshade';
  /** Preferred basemap tool from organization settings: 'pmtiles' | 'maptiler' | 'googlemaps' | 'openstreetmap'. OSM is also the fallback (and temporary googlemaps stand-in). */
  private preferredMapTool: 'pmtiles' | 'maptiler' | 'googlemaps' | 'openstreetmap' = 'pmtiles';
  /** Organization-configured PMTiles base URL (falls back to environment.offlinePmtilesUrl). */
  private configuredPmtilesUrl: string = environment.offlinePmtilesUrl || '';
  private viewportRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private mapScaleControl: maplibregl.IControl | null = null;

  constructor(private layoutUtilsService: LayoutUtilsService, private mapService: MapService, private caseService: CaseService,
    private zone: NgZone,
    private requestService: RequestService,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private geocodingService: GeocodingService,
    private natsMessaging: MessagingService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.requestService.currentUserSubject.subscribe((data) => {
        if (data) {
          const previousUserId = this.mapTilerOptionsUserId(this.currentUser);
          this.currentUser = data;
          this.isSuperAdmin = this.requestService.isUserRoleSuperAdmin();
          this.isAdmin = this.requestService.isUserRoleAdmin();
          this.isCommander = this.requestService.isUserRoleCommander();
          const nextUserId = this.mapTilerOptionsUserId(data);
          if (this.map && nextUserId && previousUserId !== nextUserId) {
            this.loadMapTilerUserOptions();
            this.applyMapTilerExtras();
            this.syncMapTilerCameraForOptions();
          }
        }
      }),
      this.translate.onLangChange.subscribe(() => {
        this.refreshMapScaleControl();
        this.refreshMapTilerLanguage();
      })
    );
    this.initialize();
    if (!this.lazyLoad) {
      this.initMap();
    }
  }

  ngAfterViewInit() {
    if (this.map && this.mapDragGeoEnabled) {
      const center = this.map.getCenter();
      this.mapDragGeo.emit({ lat: center.lat, lng: center.lng });
    }
    if (this.lazyLoad) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              this.lazyLoad = false;
              this.initMap();
            }, 300);
            this.observer.disconnect();
          }
        });
      }, {
        threshold: 0.1
      });

      this.observer.observe(this.elementRef.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.map) {
      if (changes['center'] && this.center) {
        this.map.setCenter(this.normalizeCenter(this.center));
      }
      if (changes['zoom']) {
        this.map.setZoom(this.zoom);
      }
      if (changes['markers'] || changes['marker']) {
        this.showMarkersIfReady();
      }
      if (changes['editMode']) {
        this.updateEditModeCursor();
        if (!this.editMode) {
          this.removeEditModeDblclick();
        }
        if (this.editMode && this.buildingFootprintDraftActive && this.currentPolygonPoints.length && this.map) {
          this.zone.run(() => {
            this.syncEditPointMarkers();
            this.updatePreviewGeometry();
          });
        }
      }
      // Hover listeners are attached in addMarker only when isHoverable is true; if markers
      // were first rendered with isHoverable false, toggling on must re-render markers.
      if (changes['isHoverable'] && !changes['isHoverable'].firstChange) {
        this.clearAllMarkerRefs();
        this.updateMarkersVisual();
      }
      if (changes['isIconLabelsOn'] && !changes['isIconLabelsOn'].firstChange) {
        this.clearAllMarkerRefs();
        this.updateMarkersVisual();
      }
      const changedSliderCategories = this.getChangedIconSliderCategories(changes);
      if (changedSliderCategories.size > 0) {
        const needsFullRefresh = this.applyIconScaleToRenderedMarkerElements(changedSliderCategories);
        if (needsFullRefresh && this.map) {
          this.showMarkersIfReady([], false);
        }
      }
      if (changes['cameraSectorOpacityPercent'] && !changes['cameraSectorOpacityPercent'].firstChange) {
        this.updateCameraSectorOpacity();
      }
      if (changes['selectedCamsIds'] && !changes['selectedCamsIds'].firstChange) {
        this.applySelectedCameraStylesToRenderedMarkers();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.unTrackMarkers(this.trackingMarkers);
    // Clean up all NATS listeners
    this.natsListeners.forEach((subject: string) => {
      this.natsMessaging.unsubscribe('', subject);
    });
    this.natsListeners.clear();
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.mapResizeObserver) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
    if (this.popupCloseTimeout) {
      clearTimeout(this.popupCloseTimeout);
      this.popupCloseTimeout = null;
    }
    if (this.viewportRefreshTimer) {
      clearTimeout(this.viewportRefreshTimer);
      this.viewportRefreshTimer = null;
    }
    if (this.initialOverlayReleaseTimer) {
      clearTimeout(this.initialOverlayReleaseTimer);
      this.initialOverlayReleaseTimer = null;
    }
    if (this.initialMarkerRenderRetryTimer) {
      clearTimeout(this.initialMarkerRenderRetryTimer);
      this.initialMarkerRenderRetryTimer = null;
    }
    if (this.clusterViewportRefreshRaf != null) {
      cancelAnimationFrame(this.clusterViewportRefreshRaf);
      this.clusterViewportRefreshRaf = null;
    }
    if (this.markerVisualRefreshRaf != null) {
      cancelAnimationFrame(this.markerVisualRefreshRaf);
      this.markerVisualRefreshRaf = null;
    }
    if (this.markersVisualRefreshTimer) {
      clearTimeout(this.markersVisualRefreshTimer);
      this.markersVisualRefreshTimer = null;
    }
    this.markerRefs.forEach((m) => m.remove());
    this.markerRefs.clear();
    if (this.infoPopup) {
      this.infoPopup.remove();
      this.infoPopup = null;
    }
    this.currentPopupMarker = null;
    this.removeEditModeDblclick();
    this.endPolygonBodyDragIfActive();
    this.clearEditPointMarkers();
    if (this.map) {
      try {
        this.map.off('mousedown', this.onMapPolygonBodyMousedown);
        this.map.off('mousedown', this.onMapRightMouseDown);
        this.map.off('mousemove', this.onMapRightMouseMove);
        this.map.off('movestart', this.onMapMoveStartCloseContextMenu);
        this.map.off('contextmenu', this.mapContextMenuHandler);
        this.map.off('zoomend', this.onClusterViewportChanged);
        this.map.off('moveend', this.onClusterViewportChanged);
      } catch {
        /* ignore */
      }
      if (this.map.doubleClickZoom) {
        this.map.doubleClickZoom.enable();
      }
      this.map.remove();
      this.map = null;
    }
  }

  initialize(): void {
    if (this.currentGeoLocation) {
      this.center = this.currentGeoLocation;
      return;
    }
    if (this.geoLocationInit) {
      return;
    }
    if (navigator.geolocation && !environment.disableUserLocation) {
      try {
        navigator.geolocation.getCurrentPosition(
          (position: any) => {
            if (!this.currentGeoLocation) {
              this.center = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              if (this.addMyMarker) {
                this.marker = {
                  position: this.center,
                  title: this.translate.instant('My Pin'),
                  info: 'My Marker info',
                  options: {}
                };

                if (this.defaultMarkerIcon) {
                  this.marker.icon = this.defaultMarkerIcon;
                }
              }
              if (this.map && this.mapDragGeoEnabled) {
                const center = this.map.getCenter();
                this.mapDragGeo.emit({ lat: center.lat, lng: center.lng });
              }
            }
          },
          (error: any) => {
            this.makeNoLocationReady(error);
          }
        );
      } catch (e) {
        this.makeNoLocationReady();
      }
    } else {
      this.makeNoLocationReady();
    }
  }

  makeNoLocationReady(error = undefined) {
    let lng = this.lnglatdefault.lng;
    let lat = this.lnglatdefault.lat;

    this.center = {
      lat: lat,
      lng: lng
    };
    this.zoom = this.zoom || 3;
    if (error) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          console.info("User denied the request for Geolocation.");
          break;
        case error.POSITION_UNAVAILABLE:
          console.error("Location information is unavailable.");
          break;
        case error.TIMEOUT:
          console.error("The request to get user location timed out.");
          break;
        case error.UNKNOWN_ERROR:
        default:
          console.error("An unknown error occurred.");
          break;
      }
    }
  }

  private initMap(): void {
    if (this.mapInit || this.lazyLoad) {
      return;
    }

    // Prefer the configured map tool. OpenStreetMap is also the fallback when the selected tool is unavailable.
    this.resolvePreferredBasemapStyle().then((style) => {
      setTimeout(() => {
        const el = this.mapContainer?.nativeElement;
        if (!el) {
          console.warn('Map container not found');
          return;
        }

        const rect = el.getBoundingClientRect();

        try {
          this.mapInit = true;
          const center = this.normalizeCenter(this.center || this.lnglatdefault);

          // Keep MapLibre render loop outside Angular; app hooks re-enter via zone.run where needed.
          this.zone.runOutsideAngular(() => {
            this.map = new maplibregl.Map({
              container: el,
              style: style,
              center: center,
              zoom: Math.min(this.zoom || 15, this.MAX_ZOOM),
              maxZoom: this.MAX_ZOOM,
              // Building extrusion and terrain elevation show once the camera is tilted.
              pitch: this.preferredMapTool === 'maptiler' && (this.mapTiler3dEnabled || this.mapTilerElevationEnabled) ? 45 : 0,
              maxPitch: this.mapTilerElevationEnabled ? 70 : 60,
              dragPan: this.options.draggable,
              attributionControl: false,
              preserveDrawingBuffer: true
            });

            this.map.on('error', (e) => {
              console.warn('Map error:', e);
            });

            this.map.on('style.load', () => {
              this.syncBasemapBuildingLabelLayersVisibility();
              this.applyMapTilerExtras();
            });

            this.map.on('load', () => {
              setTimeout(() => {
                if (!this.map) return;
                this.mapLoaded = true;
                this.addPoiLayersWhenReady();

                if (this.options.scaleControl) {
                  this.addMapScaleControl();
                }

                this.map.on('click', () => {
                  if (!this.mapTilerOptionsOpen) {
                    return;
                  }
                  this.zone.run(() => {
                    this.mapTilerOptionsOpen = false;
                  });
                });

                this.zone.run(() => {
                  this.showMapTilerOptions = this.usingMapTilerBasemap;
                  this.mapReady.emit(this.map);
                  this.mapInitialized.emit(this.map);
                  this.tilesloaded.emit();
                });

                this.getUserLocation();
                this.syncBasemapBuildingLabelLayersVisibility();
                this.applyMapTilerExtras();
                this.attachEventHandlers();
                this.updateEditModeCursor();
                this.scheduleViewportRefresh();
                // Mission control can have thousands of pins. Paint the basemap first,
                // then add markers, footprints, and other overlays.
                if (this.isCasePage) {
                  this.holdOverlaysUntilPaint = true;
                  this.scheduleInitialOverlayRelease();
                } else {
                  this.addCustomAreaLayersIfNeeded();
                  this.updateCustomAreasSource();
                  setTimeout(() => this.showMarkersIfReady(), 0);
                }
              }, 100);
            });

            // Keep drawing buffer for dispatch/context-menu snapshots (canvas.toDataURL).
            if (this.mapResizeObserver) {
              this.mapResizeObserver.disconnect();
              this.mapResizeObserver = null;
            }
            this.mapResizeObserver = new ResizeObserver(() => {
              if (this.map) {
                try {
                  const checkRect = el.getBoundingClientRect();
                  if (checkRect.width > 10 && checkRect.height > 10) {
                    this.map.resize();
                    this.scheduleViewportRefresh(80);
                  }
                } catch (err) {
                  console.warn('Resize error:', err);
                }
              }
            });
            this.mapResizeObserver.observe(el);
          });

        } catch (err) {
          console.error('Map initialization error:', err);
          this.mapInit = false;
        }
      }, 100);
    });
  }

  private async checkPmtilesAvailability(): Promise<boolean> {
    // Return cached result if already checked
    if (this.pmtilesAvailable !== null) {
      return this.pmtilesAvailable;
    }

    // Return existing promise if check is in progress
    if (this.pmtilesCheckPromise) {
      return this.pmtilesCheckPromise;
    }

    // Start new check
    this.pmtilesCheckPromise = new Promise<boolean>((resolve) => {
      const pmtilesUrl = this.getPmtilesUrl();
      if (!pmtilesUrl) {
        this.pmtilesAvailable = false;
        resolve(false);
        return;
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

      fetch(pmtilesUrl + '0/0/0.mvt', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache'
      })
        .then((response) => {
          clearTimeout(timeoutId);
          const available = response.ok;
          this.pmtilesAvailable = available;
          resolve(available);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          this.pmtilesAvailable = false;
          resolve(false);
        });
    });

    return this.pmtilesCheckPromise;
  }

  /**
   * Force a delayed resize/repaint after layout or camera changes so initial visible tiles are requested.
   */
  private scheduleViewportRefresh(delayMs: number = 0): void {
    if (this.viewportRefreshTimer) {
      clearTimeout(this.viewportRefreshTimer);
      this.viewportRefreshTimer = null;
    }

    this.viewportRefreshTimer = setTimeout(() => {
      this.viewportRefreshTimer = null;
      if (!this.map) {
        return;
      }

      try {
        this.map.resize();
      } catch {
        /* ignore */
      }

      try {
        this.map.triggerRepaint();
      } catch {
        /* ignore */
      }
    }, delayMs);
  }

  private refreshViewportAfterCameraMove(): void {
    if (!this.map) {
      return;
    }

    this.map.once('moveend', () => {
      this.scheduleViewportRefresh();
      if ((this.markers?.length || 0) > 0) {
        this.scheduleMarkersVisualRefresh();
      }
    });
    this.scheduleViewportRefresh(180);
  }

  /** Debounced marker DOM sync — avoids full teardown on every pan/zoom burst. */
  private scheduleMarkersVisualRefresh(delayMs: number = this.markersVisualRefreshDebounceMs): void {
    if (this.markersVisualRefreshTimer) {
      clearTimeout(this.markersVisualRefreshTimer);
    }
    this.markersVisualRefreshTimer = setTimeout(() => {
      this.markersVisualRefreshTimer = null;
      this.updateMarkersVisual();
    }, delayMs);
  }

  /**
   * Helper to ensure markers are only rendered after the map has finished loading.
   */
  /**
   * After the first idle frame (or a short fallback), release queued markers and overlays.
   */
  private scheduleInitialOverlayRelease(): void {
    if (!this.map) {
      this.releaseInitialOverlays();
      return;
    }
    let released = false;
    const release = () => {
      if (released) {
        return;
      }
      released = true;
      if (this.initialOverlayReleaseTimer) {
        clearTimeout(this.initialOverlayReleaseTimer);
        this.initialOverlayReleaseTimer = null;
      }
      this.releaseInitialOverlays();
    };
    this.initialOverlayReleaseTimer = setTimeout(release, 1200);
    this.map.once('idle', () => {
      // Yield so the painted basemap frame is on screen before pin work starts.
      setTimeout(release, 0);
    });
  }

  private releaseInitialOverlays(): void {
    this.holdOverlaysUntilPaint = false;
    if (!this.map || !this.mapLoaded) {
      return;
    }
    this.addCustomAreaLayersIfNeeded();
    this.updateCustomAreasSource();
    this.showMarkersIfReady();
  }

  public showMarkersIfReady(typeToHide: string[] = [], calculateCenterZoom: boolean = true): void {
    if (!this.mapLoaded || this.holdOverlaysUntilPaint) {
      this.pendingShowMarkers = { typeToHide, calculateCenterZoom };
      return;
    }
    const pending = this.pendingShowMarkers;
    this.pendingShowMarkers = null;
    this.scheduleShowMarkers(
      pending?.typeToHide ?? typeToHide,
      pending?.calculateCenterZoom ?? calculateCenterZoom
    );
  }

  /**
   * Queue-aware wrapper around showMarkers to avoid overlapping unTrack/track cycles.
   * If showMarkers is already running, we remember the latest requested arguments
   * and run it once more after the current execution finishes.
   */
  private scheduleShowMarkers(typeToHide: string[] = [], calculateCenterZoom: boolean = true): void {
    // If a run is already in progress, just remember the latest request
    if (this.isShowingMarkers) {
      this.pendingShowMarkers = { typeToHide, calculateCenterZoom };
      return;
    }

    this.isShowingMarkers = true;

    // Make it effectively async to batch multiple rapid calls into one execution
    Promise.resolve().then(async () => {
      try {
        await this.showMarkers(typeToHide, calculateCenterZoom);
      } finally {
        this.isShowingMarkers = false;

        // If another call came in while we were running, process it once with the latest args
        if (this.pendingShowMarkers) {
          const next = this.pendingShowMarkers;
          this.pendingShowMarkers = null;
          this.scheduleShowMarkers(next.typeToHide, next.calculateCenterZoom);
        }
      }
    });
  }

  private buildStyle(styles?: any, usePmtiles: boolean = false): any {
    if (styles) {
      this.currentStyles = styles;
      return styles;
    }

    if (this.currentStyles) {
      return this.currentStyles;
    }

    // If usePmtiles is true and PMTiles is available, use offline style
    // Otherwise, use online style
    if (usePmtiles && this.pmtilesAvailable === true) {
      const offlineStyle = this.buildOfflinePmtilesStyle();
      if (offlineStyle) {
        this.currentStyles = offlineStyle;
        return offlineStyle;
      }
    }

    // Fall back to online style based on preferred tool (OSM is last resort).
    const onlineStyle = this.buildOnlineRasterStyle();
    this.currentStyles = onlineStyle;
    return onlineStyle;
  }

  /** Read preferred map tool + MapTiler key + PMTiles URL from organization settings. */
  private loadMapToolSettings(): void {
    const organization = this.requestService.pageOrganization?.getValue?.();
    const settings = organization ? this.requestService.getSettings(organization) : null;
    this.preferredMapTool =
      settings?.mapTool === 'maptiler' || settings?.mapTool === 'googlemaps' || settings?.mapTool === 'openstreetmap'
        ? settings.mapTool
        : 'pmtiles';
    const orgKey = (settings?.mapTilerApiKey || '').trim();
    this.configuredMapTilerApiKey = orgKey || environment.mapTilerApiKey || '';
    this.loadMapTilerUserOptions();
    this.configuredPmtilesUrl = this.normalizePmtilesUrl(
      (settings?.pmtilesUrl || '').trim() || environment.offlinePmtilesUrl || ''
    );
  }

  private normalizePmtilesUrl(url: string): string {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
  }

  private getPmtilesUrl(): string {
    return this.configuredPmtilesUrl || this.normalizePmtilesUrl(environment.offlinePmtilesUrl || '') || OFFLINE_PMTILES_URL || '';
  }

  /** Wait briefly for organization so map-tool settings are available before style selection. */
  private waitForOrganization(timeoutMs: number = 2000): Promise<any> {
    const current = this.requestService.pageOrganization?.getValue?.();
    if (current) {
      return Promise.resolve(current);
    }
    return new Promise((resolve) => {
      let settled = false;
      const sub = this.requestService.pageOrganization.subscribe((data) => {
        if (data && !settled) {
          settled = true;
          sub.unsubscribe();
          resolve(data);
        }
      });
      setTimeout(() => {
        if (!settled) {
          settled = true;
          sub.unsubscribe();
          resolve(this.requestService.pageOrganization?.getValue?.());
        }
      }, timeoutMs);
    });
  }

  /**
   * Resolve basemap style from org preference:
   * - PMTiles → try PMTiles, else OpenStreetMap
   * - MapTiler → try MapTiler (requires key), else OpenStreetMap
   * - OpenStreetMap → OpenStreetMap raster tiles
   * - Google Maps → OpenStreetMap for now (until Google Maps basemap is wired)
   */
  private async resolvePreferredBasemapStyle(): Promise<any> {
    if (this.currentStyles) {
      return this.currentStyles;
    }

    await this.waitForOrganization();
    this.loadMapToolSettings();
    this.usingMapTilerBasemap = false;

    if (this.preferredMapTool === 'openstreetmap' || this.preferredMapTool === 'googlemaps') {
      if (this.preferredMapTool === 'googlemaps') {
        console.warn('[Map] Google Maps selected; using OpenStreetMap until Google Maps basemap is handled');
      }
      const osmStyle = this.buildOpenStreetMapStyle();
      this.currentStyles = osmStyle;
      return osmStyle;
    }

    if (this.preferredMapTool === 'pmtiles') {
      const pmtilesAvailable = await this.checkPmtilesAvailability();
      if (pmtilesAvailable) {
        const offlineStyle = this.buildOfflinePmtilesStyle();
        if (offlineStyle) {
          this.currentStyles = offlineStyle;
          return offlineStyle;
        }
      }
      console.warn('[Map] PMTiles unavailable; falling back to OpenStreetMap');
      const osmStyle = this.buildOpenStreetMapStyle();
      this.currentStyles = osmStyle;
      return osmStyle;
    }

    // Preferred: MapTiler
    if (this.configuredMapTilerApiKey) {
      const mapTilerOk = await this.checkMapTilerAvailability();
      if (mapTilerOk) {
        const mapTilerStyle = this.buildMapTilerStyle(this.configuredMapTilerApiKey);
        this.currentStyles = mapTilerStyle;
        this.usingMapTilerBasemap = true;
        return mapTilerStyle;
      }
    } else {
      console.warn('[Map] MapTiler selected but no API key configured; falling back to OpenStreetMap');
    }

    console.warn('[Map] MapTiler unavailable; falling back to OpenStreetMap');
    const osmStyle = this.buildOpenStreetMapStyle();
    this.currentStyles = osmStyle;
    return osmStyle;
  }

  /**
   * Probe MapTiler with one tile request.
   * Treats 401/402/403/429 (and missing key) as limited so it is only used when healthy.
   */
  private checkMapTilerAvailability(): Promise<boolean> {
    if (this.mapTilerCheckPromise) {
      return this.mapTilerCheckPromise;
    }

    const key = this.configuredMapTilerApiKey || environment.mapTilerApiKey;
    if (!key) {
      this.mapTilerLimited = true;
      return Promise.resolve(false);
    }

    this.mapTilerCheckPromise = (async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const res = await fetch(
          `https://api.maptiler.com/maps/streets-v2/style.json?key=${encodeURIComponent(key)}`,
          { method: 'GET', signal: controller.signal, cache: 'no-store' }
        );
        clearTimeout(timeoutId);

        // Rate limit / quota exhausted / suspended / bad key
        if (res.status === 401 || res.status === 402 || res.status === 403 || res.status === 429) {
          console.warn(`[MapTiler] Limit detected (HTTP ${res.status}); using OpenStreetMap fallback`);
          this.mapTilerLimited = true;
          return false;
        }

        if (!res.ok) {
          console.warn(`[MapTiler] Unavailable (HTTP ${res.status}); using OpenStreetMap fallback`);
          this.mapTilerLimited = true;
          return false;
        }

        this.mapTilerLimited = false;
        return true;
      } catch (err) {
        console.warn('[MapTiler] Probe failed; using OpenStreetMap fallback', err);
        this.mapTilerLimited = true;
        return false;
      }
    })();

    return this.mapTilerCheckPromise;
  }

  private buildOpenStreetMapStyle(): any {
    console.log('Using OpenStreetMap');
    return {
      version: 8,
      sources: {
        'osm-tiles': {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors'
        }
      },
      layers: [
        {
          id: 'osm-layer',
          type: 'raster',
          source: 'osm-tiles',
          minzoom: 0,
          maxzoom: 20
        }
      ]
    };
  }

  /**
   * MapTiler vector style. Raster streets tiles flatten the map into one image and drop
   * the "Building 3D" fill-extrusion layer that ships in this style (visible from zoom 15).
   */
  /** Japanese map labels only while the app language is Japanese. */
  private mapTilerLanguage(): string {
    const lang = (this.getPreferredLanguage() || '').toLowerCase();
    return lang === 'ja' || lang === 'jp' ? 'ja' : 'en';
  }

  private buildMapTilerStyle(key: string): string {
    console.log('Using MapTiler');
    const styleId = this.mapTilerStyleById(this.mapTilerStyleId)?.id || 'streets-v2';
    const params = new URLSearchParams({
      key,
      language: this.mapTilerLanguage(),
    });
    return `https://api.maptiler.com/maps/${styleId}/style.json?${params.toString()}`;
  }

  /** Reload the MapTiler style so labels follow a language change. */
  private refreshMapTilerLanguage(): void {
    if (!this.map || !this.usingMapTilerBasemap) {
      return;
    }
    const key = (this.configuredMapTilerApiKey || '').trim();
    if (!key) {
      return;
    }
    this.map.setStyle(this.buildMapTilerStyle(key), {
      transformStyle: (previousStyle, nextStyle) => this.mergeCustomStyleLayers(previousStyle, nextStyle),
    });
  }

  private mapTilerStyleById(styleId: string): { id: string; labelKey: string } | undefined {
    return this.mapTilerStyles.find((style) => style.id === styleId);
  }

  get mapTilerStyleLabelKey(): string {
    return this.mapTilerStyleById(this.mapTilerStyleId)?.labelKey || 'Streets';
  }

  /** Switch the MapTiler basemap and keep app-drawn areas, sectors, and routes. */
  selectMapTilerStyle(styleId: string): void {
    if (!this.mapTilerStyleById(styleId) || styleId === this.mapTilerStyleId) {
      return;
    }
    this.mapTilerStyleId = styleId;
    this.persistMapTilerUserOptions();
    if (!this.map || !this.usingMapTilerBasemap) {
      return;
    }
    const key = (this.configuredMapTilerApiKey || '').trim();
    if (!key) {
      return;
    }
    this.map.setStyle(this.buildMapTilerStyle(key), {
      transformStyle: (previousStyle, nextStyle) => this.mergeCustomStyleLayers(previousStyle, nextStyle),
    });
  }

  /** Carry geojson overlays (sectors, areas, routes) onto a newly selected MapTiler style. */
  private mergeCustomStyleLayers(previousStyle: any, nextStyle: any): any {
    if (!previousStyle || !nextStyle) {
      return nextStyle;
    }
    const extraSources: Record<string, any> = {};
    const previousSources = previousStyle.sources || {};
    for (const [id, source] of Object.entries(previousSources) as [string, any][]) {
      if (source?.type === 'geojson') {
        extraSources[id] = source;
      }
    }
    const extraSourceIds = new Set(Object.keys(extraSources));
    const extraLayers = (previousStyle.layers || []).filter((layer: any) => extraSourceIds.has(layer?.source));
    return {
      ...nextStyle,
      sources: { ...(nextStyle.sources || {}), ...extraSources },
      layers: [...(nextStyle.layers || []), ...extraLayers],
    };
  }

  /** Bottom-right MapTiler control. Choices are stored per user in localStorage. */
  toggleMapTilerOption(option: '3d' | 'elevation' | 'hillshade'): void {
    if (option === '3d') {
      this.mapTiler3dEnabled = !this.mapTiler3dEnabled;
    } else if (option === 'elevation') {
      this.mapTilerElevationEnabled = !this.mapTilerElevationEnabled;
    } else {
      this.mapTilerHillshadeEnabled = !this.mapTilerHillshadeEnabled;
    }
    this.persistMapTilerUserOptions();
    this.applyMapTilerExtras();
    this.syncMapTilerCameraForOptions();
  }

  private mapTilerOptionsUserId(user: any = this.currentUser): string {
    const id = user?._id || user?.id;
    return id != null && String(id).trim() !== '' ? String(id) : '';
  }

  private mapTilerOptionsStorageKey(): string {
    const userId = this.mapTilerOptionsUserId()
      || this.mapTilerOptionsUserId(this.requestService.currentUserSubject?.getValue?.());
    return `maptiler-map-options:${userId || 'anonymous'}`;
  }

  private loadMapTilerUserOptions(): void {
    this.mapTiler3dEnabled = false;
    this.mapTilerElevationEnabled = false;
    this.mapTilerHillshadeEnabled = false;
    this.mapTilerStyleId = 'streets-v2';
    try {
      const raw = localStorage.getItem(this.mapTilerOptionsStorageKey());
      if (!raw) {
        return;
      }
      const saved = JSON.parse(raw) || {};
      this.mapTiler3dEnabled = saved.enable3d === true;
      this.mapTilerElevationEnabled = saved.enableElevation === true;
      this.mapTilerHillshadeEnabled = saved.enableHillshade === true;
      if (this.mapTilerStyleById(saved.styleId)) {
        this.mapTilerStyleId = saved.styleId;
      }
    } catch {
      /* ignore invalid stored options */
    }
  }

  private persistMapTilerUserOptions(): void {
    try {
      localStorage.setItem(this.mapTilerOptionsStorageKey(), JSON.stringify({
        enable3d: this.mapTiler3dEnabled,
        enableElevation: this.mapTilerElevationEnabled,
        enableHillshade: this.mapTilerHillshadeEnabled,
        styleId: this.mapTilerStyleId,
      }));
    } catch {
      /* ignore storage failures */
    }
  }

  /** Tilt the camera when 3D buildings or elevation are on so the change is visible. */
  private syncMapTilerCameraForOptions(): void {
    if (!this.map) {
      return;
    }
    const wantsPitch = this.mapTiler3dEnabled || this.mapTilerElevationEnabled;
    try {
      this.map.setMaxPitch(this.mapTilerElevationEnabled ? 70 : 60);
    } catch {
      /* ignore */
    }
    const pitch = this.map.getPitch();
    if (wantsPitch && pitch < 20) {
      this.map.easeTo({ pitch: 45, duration: 400 });
    } else if (!wantsPitch && pitch > 1) {
      this.map.easeTo({ pitch: 0, duration: 400 });
    }
  }

  /** Apply MapTiler checkboxes: 3D buildings, terrain elevation, and hillshade. */
  private applyMapTilerExtras(): void {
    this.applyMapTilerLabelLanguage();
    this.enableMapTilerBuilding3dLayer();
    this.applyMapTilerTerrain();
  }

  /**
   * MapTiler's style.json ignores the language query and ships English (`name:en`) labels.
   * When the app language is Japanese, point those label fields at `name:ja`.
   */
  private applyMapTilerLabelLanguage(): void {
    if (!this.map || this.mapTilerLanguage() !== 'ja') {
      return;
    }
    const style = this.map.getStyle();
    const sources = style?.sources || {};
    for (const layer of style?.layers || []) {
      if (layer.type !== 'symbol' || !('source' in layer) || !layer.source) {
        continue;
      }
      const source = sources[layer.source] as { url?: string; tiles?: string[] } | undefined;
      const sourceUrl = `${source?.url || ''} ${(source?.tiles || []).join(' ')}`;
      if (!sourceUrl.includes('maptiler.com')) {
        continue;
      }
      const textField = this.map.getLayoutProperty(layer.id, 'text-field');
      if (!this.textFieldUsesPlaceName(textField)) {
        continue;
      }
      try {
        this.map.setLayoutProperty(layer.id, 'text-field', this.withJapanesePlaceName(textField) as any);
      } catch {
        /* ignore layers that reject the replacement */
      }
    }
  }

  private textFieldUsesPlaceName(value: unknown): boolean {
    const raw = typeof value === 'string' ? value : JSON.stringify(value ?? '');
    return raw.includes('name:en') || raw.includes('name:latin') || raw.includes('{name}') || raw.includes('"name"');
  }

  private withJapanesePlaceName(value: unknown): unknown {
    if (typeof value === 'string') {
      if (value === '{name:en}' || value === '{name:latin}' || value === '{name}') {
        return ['coalesce', ['get', 'name:ja'], ['get', 'name:en'], ['get', 'name']];
      }
      return value.replace(/\{name:en\}/g, '{name:ja}').replace(/\{name:latin\}/g, '{name:ja}');
    }
    if (Array.isArray(value)) {
      if (value[0] === 'get' && (value[1] === 'name:en' || value[1] === 'name:latin')) {
        return ['get', 'name:ja'];
      }
      return value.map((part) => this.withJapanesePlaceName(part));
    }
    return value;
  }

  /** Show or hide MapTiler's Building 3D extrusion based on the configuration checkbox. */
  private enableMapTilerBuilding3dLayer(): void {
    if (!this.map || this.preferredMapTool !== 'maptiler') {
      return;
    }
    const layerId = 'Building 3D';
    if (!this.map.getLayer(layerId)) {
      return;
    }
    try {
      this.map.setLayoutProperty(layerId, 'visibility', this.mapTiler3dEnabled ? 'visible' : 'none');
    } catch {
      /* ignore */
    }
  }

  /** Terrain elevation raises mountains; hillshade paints slope shading from the same elevation tiles. */
  private applyMapTilerTerrain(): void {
    if (!this.map || this.preferredMapTool !== 'maptiler') {
      return;
    }
    const needsDem = this.mapTilerElevationEnabled || this.mapTilerHillshadeEnabled;
    if (!needsDem) {
      this.clearMapTilerTerrain();
      return;
    }
    const key = (this.configuredMapTilerApiKey || '').trim();
    if (!key) {
      return;
    }
    try {
      if (!this.map.getSource(this.MAPTILER_TERRAIN_SOURCE_ID)) {
        this.map.addSource(this.MAPTILER_TERRAIN_SOURCE_ID, {
          type: 'raster-dem',
          url: `https://api.maptiler.com/tiles/terrain-rgb-v2/tiles.json?key=${encodeURIComponent(key)}`,
          tileSize: 256,
        });
      }
      if (this.mapTilerElevationEnabled) {
        this.map.setTerrain({ source: this.MAPTILER_TERRAIN_SOURCE_ID, exaggeration: 1 });
      } else {
        this.map.setTerrain(null);
      }
      this.applyMapTilerHillshadeLayer();
    } catch (err) {
      console.warn('[MapTiler] Terrain setup failed', err);
    }
  }

  private applyMapTilerHillshadeLayer(): void {
    if (!this.map || !this.map.getSource(this.MAPTILER_TERRAIN_SOURCE_ID)) {
      return;
    }
    if (!this.mapTilerHillshadeEnabled) {
      if (this.map.getLayer(this.MAPTILER_HILLSHADE_LAYER_ID)) {
        this.map.setLayoutProperty(this.MAPTILER_HILLSHADE_LAYER_ID, 'visibility', 'none');
      }
      return;
    }
    if (!this.map.getLayer(this.MAPTILER_HILLSHADE_LAYER_ID)) {
      const layers = this.map.getStyle()?.layers || [];
      const beforeId = layers.find((layer) => layer.type === 'symbol')?.id;
      this.map.addLayer({
        id: this.MAPTILER_HILLSHADE_LAYER_ID,
        type: 'hillshade',
        source: this.MAPTILER_TERRAIN_SOURCE_ID,
        paint: {
          'hillshade-exaggeration': 0.5,
          'hillshade-shadow-color': '#473B24',
          'hillshade-highlight-color': '#FFFFFF',
          'hillshade-accent-color': '#5a4a32',
        },
      }, beforeId);
      return;
    }
    this.map.setLayoutProperty(this.MAPTILER_HILLSHADE_LAYER_ID, 'visibility', 'visible');
  }

  private clearMapTilerTerrain(): void {
    if (!this.map) {
      return;
    }
    try {
      this.map.setTerrain(null);
    } catch {
      /* ignore */
    }
    if (this.map.getLayer(this.MAPTILER_HILLSHADE_LAYER_ID)) {
      this.map.setLayoutProperty(this.MAPTILER_HILLSHADE_LAYER_ID, 'visibility', 'none');
    }
  }

  private buildOnlineRasterStyle(): any {
    if (this.preferredMapTool === 'maptiler' && !this.mapTilerLimited && this.configuredMapTilerApiKey) {
      return this.buildMapTilerStyle(this.configuredMapTilerApiKey);
    }
    return this.buildOpenStreetMapStyle();
  }

  private buildOfflinePmtilesStyle(): any {
    const pmtilesUrl = this.getPmtilesUrl();
    return {
      version: 8,
      glyphs: OFFLINE_PMTILES_FONTS_URL,
      sprite: OFFLINE_PMTILES_SPRITES_URL,
      sources: {
        protomaps: {
          type: 'vector',
          tiles: [pmtilesUrl + '{z}/{x}/{y}.mvt'],
          minzoom: 0,
          maxzoom: 15
        }
      },
      layers: [
        // Background - light beige
        {
          id: 'background',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landuse',
          paint: { 'fill-color': '#f5f5f3' }
        },
        // Parks and green spaces
        {
          id: 'parks',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landuse',
          filter: ['any',
            ['==', ['get', 'type'], 'park'],
            ['==', ['get', 'type'], 'cemetery'],
            ['==', ['get', 'type'], 'golf_course'],
            ['==', ['get', 'type'], 'grass'],
            ['==', ['get', 'type'], 'green'],
            ['==', ['get', 'type'], 'recreation_ground'],
            ['==', ['get', 'subclass'], 'park'],
            ['==', ['get', 'class'], 'park']
          ],
          paint: { 'fill-color': '#d5e8d4' }
        },
        // Forests
        {
          id: 'forests',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landuse',
          filter: ['any',
            ['==', ['get', 'type'], 'forest'],
            ['==', ['get', 'type'], 'wood'],
            ['==', ['get', 'subclass'], 'forest'],
            ['==', ['get', 'class'], 'forest']
          ],
          paint: { 'fill-color': '#c8e6c9' }
        },
        // Hospital landuse (polygons - hospital grounds)
        {
          id: 'landuse-hospital',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landuse',
          filter: ['==', ['get', 'kind'], 'hospital'],
          paint: { 'fill-color': '#ffcdd2', 'fill-opacity': 0.6 }
        },
        // Military landuse (polygons - military bases/installations)
        {
          id: 'landuse-military',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landuse',
          filter: ['==', ['get', 'kind'], 'military'],
          paint: { 'fill-color': '#b0bec5', 'fill-opacity': 0.5 }
        },
        // Optional debug greens
        {
          id: 'green-debug',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landuse',
          paint: { 'fill-color': '#77c387', 'fill-opacity': 0.4 }
        },
        {
          id: 'green-debug2',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'landcover',
          paint: { 'fill-color': '#77c387', 'fill-opacity': 0.4 }
        },
        {
          id: 'green-debug3',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'natural',
          paint: { 'fill-color': '#77c387', 'fill-opacity': 0.4 }
        },
        // Water
        {
          id: 'water',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'water',
          paint: { 'fill-color': '#a3d9f5' }
        },
        {
          id: 'water-outline',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'water',
          paint: { 'line-color': '#9bc4e2', 'line-width': 1 }
        },
        // Roads - base
        {
          id: 'roads-base',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'roads',
          paint: {
            'line-color': '#cccccc',
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 3, 16, 5, 18, 7]
          }
        },
        // Motorways
        {
          id: 'roads-motorway',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'roads',
          filter: ['any',
            ['==', ['get', 'type'], 'motorway'],
            ['==', ['get', 'type'], 'motorway_link'],
            ['==', ['get', 'class'], 'motorway']
          ],
          paint: {
            'line-color': '#a3d9f5',
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 2, 8, 4, 12, 8, 16, 14, 18, 18]
          }
        },
        // Trunk roads
        {
          id: 'roads-trunk',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'roads',
          filter: ['any',
            ['==', ['get', 'type'], 'trunk'],
            ['==', ['get', 'type'], 'trunk_link'],
            ['==', ['get', 'class'], 'trunk']
          ],
          paint: {
            'line-color': '#f5c97a',
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 2, 10, 4, 14, 8, 18, 12]
          }
        },
        // Primary roads
        {
          id: 'roads-primary',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'roads',
          filter: ['any',
            ['==', ['get', 'type'], 'primary'],
            ['==', ['get', 'type'], 'primary_link'],
            ['==', ['get', 'class'], 'primary']
          ],
          paint: {
            'line-color': '#f5c97a',
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 10, 3, 14, 7, 18, 11]
          }
        },
        // Secondary roads
        {
          id: 'roads-secondary',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'roads',
          filter: ['any',
            ['==', ['get', 'type'], 'secondary'],
            ['==', ['get', 'type'], 'secondary_link'],
            ['==', ['get', 'class'], 'secondary']
          ],
          paint: {
            'line-color': '#fef3c0',
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 12, 3, 16, 7, 18, 9]
          }
        },
        // Buildings
        {
          id: 'buildings-outline',
          type: 'line',
          source: 'protomaps',
          'source-layer': 'buildings',
          paint: { 'line-color': '#d0d0d0', 'line-width': 0.5, 'line-opacity': 0.5 }
        },
        {
          id: 'buildings',
          type: 'fill',
          source: 'protomaps',
          'source-layer': 'buildings',
          paint: { 'fill-color': '#e8e6e3', 'fill-outline-color': '#d0d0d0' }
        },
        // Building labels
        {
          id: 'building-labels',
          type: 'symbol',
          source: 'protomaps',
          'source-layer': 'buildings',
          minzoom: 14,
          layout: {
            'text-field': ['coalesce', ['get', 'name'], ['get', 'name_en'], ['get', 'name_ja'], ['get', 'ref'], ['get', 'id'], ''],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 14, 11, 16, 13, 18, 15],
            'text-offset': [0, 1.2],
            'text-anchor': 'top',
            'text-optional': true
          },
          paint: {
            'text-color': '#333333',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.2,
            'text-halo-blur': 0.8
          }
        },
        // Road labels
        {
          id: 'road-labels',
          type: 'symbol',
          source: 'protomaps',
          'source-layer': 'roads',
          filter: ['has', 'name'],
          layout: {
            'text-field': ['coalesce', ['get', 'name'], ['get', 'ref'], ['get', 'name_en'], ['get', 'name_ja'], ''],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 10, 8, 14, 10, 18, 12],
            'symbol-placement': 'line',
            'text-rotation-alignment': 'map',
            'text-pitch-alignment': 'viewport'
          },
          paint: {
            'text-color': '#787878',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
            'text-halo-blur': 1
          }
        },
        // Place labels
        {
          id: 'place-labels',
          type: 'symbol',
          source: 'protomaps',
          'source-layer': 'places',
          filter: ['has', 'name'],
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 8, 12, 12, 14, 16, 16],
            'text-anchor': 'center'
          },
          paint: {
            'text-color': '#333333',
            'text-halo-color': '#ffffff',
            'text-halo-width': 2,
            'text-halo-blur': 1
          }
        }
        // POI icon and label layers (hospital, police, etc.) are added at runtime in addPoiLayersWhenReady()
      ]
    };
  }
  private buildOfflinePmtilesStyleOld(): any {
    console.log('using offline style');
    this.ensurePmtilesProtocol();

    const pmtilesUrl = this.getPmtilesUrl();

    return {
      version: 8,
      glyphs: OFFLINE_PMTILES_FONTS_URL,
      sprite: OFFLINE_PMTILES_SPRITES_URL,
      sources: {
        "protomaps": {
          type: "vector",
          url: `pmtiles://${pmtilesUrl}`
        }
      },
      layers: [
        // Background - light beige similar to Google Maps
        {
          "id": "background",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "landuse",
          "paint": {
            "fill-color": "#f5f5f3"
          }
        },
        // Parks and green spaces - check multiple possible property names
        {
          "id": "parks",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "landuse",
          "filter": ["any",
            ["==", ["get", "type"], "park"],
            ["==", ["get", "type"], "cemetery"],
            ["==", ["get", "type"], "golf_course"],
            ["==", ["get", "type"], "grass"],
            ["==", ["get", "type"], "green"],
            ["==", ["get", "type"], "recreation_ground"],
            ["==", ["get", "subclass"], "park"],
            ["==", ["get", "class"], "park"]
          ],
          "paint": {
            "fill-color": "#d5e8d4"
          }
        },
        // Forests
        {
          "id": "forests",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "landuse",
          "filter": ["any",
            ["==", ["get", "type"], "forest"],
            ["==", ["get", "type"], "wood"],
            ["==", ["get", "subclass"], "forest"],
            ["==", ["get", "class"], "forest"]
          ],
          "paint": {
            "fill-color": "#c8e6c9"
          }
        },
        {
          "id": "green-debug",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "landuse",
          "paint": { "fill-color": "#77c387", "fill-opacity": 0.4 }
        },
        {
          "id": "green-debug2",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "landcover",
          "paint": { "fill-color": "#77c387", "fill-opacity": 0.4 }
        },
        {
          "id": "green-debug3",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "natural",
          "paint": { "fill-color": "#77c387", "fill-opacity": 0.4 }
        },
        // Water bodies
        {
          "id": "water",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "water",
          "paint": {
            "fill-color": "#a3d9f5"
          }
        },
        // Water outlines
        {
          "id": "water-outline",
          "type": "line",
          "source": "protomaps",
          "source-layer": "water",
          "paint": {
            "line-color": "#9bc4e2",
            "line-width": 1
          }
        },
        // Roads - base layer showing all roads (darker color for visibility)
        {
          "id": "roads-base",
          "type": "line",
          "source": "protomaps",
          "source-layer": "roads",
          "paint": {
            "line-color": "#cccccc",
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8, 2,
              12, 3,
              16, 5,
              18, 7
            ]
          }
        },
        // Motorways/freeways - light blue
        {
          "id": "roads-motorway",
          "type": "line",
          "source": "protomaps",
          "source-layer": "roads",
          "filter": ["any",
            ["==", ["get", "type"], "motorway"],
            ["==", ["get", "type"], "motorway_link"],
            ["==", ["get", "class"], "motorway"]
          ],
          "paint": {
            "line-color": "#a3d9f5",
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              4, 2,
              8, 4,
              12, 8,
              16, 14,
              18, 18
            ]
          }
        },
        // Trunk roads - orange
        {
          "id": "roads-trunk",
          "type": "line",
          "source": "protomaps",
          "source-layer": "roads",
          "filter": ["any",
            ["==", ["get", "type"], "trunk"],
            ["==", ["get", "type"], "trunk_link"],
            ["==", ["get", "class"], "trunk"]
          ],
          "paint": {
            "line-color": "#f5c97a",
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              6, 2,
              10, 4,
              14, 8,
              18, 12
            ]
          }
        },
        // Primary roads - orange
        {
          "id": "roads-primary",
          "type": "line",
          "source": "protomaps",
          "source-layer": "roads",
          "filter": ["any",
            ["==", ["get", "type"], "primary"],
            ["==", ["get", "type"], "primary_link"],
            ["==", ["get", "class"], "primary"]
          ],
          "paint": {
            "line-color": "#f5c97a",
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              6, 1.5,
              10, 3,
              14, 7,
              18, 11
            ]
          }
        },
        // Secondary roads - yellow
        {
          "id": "roads-secondary",
          "type": "line",
          "source": "protomaps",
          "source-layer": "roads",
          "filter": ["any",
            ["==", ["get", "type"], "secondary"],
            ["==", ["get", "type"], "secondary_link"],
            ["==", ["get", "class"], "secondary"]
          ],
          "paint": {
            "line-color": "#fef3c0",
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8, 1.5,
              12, 3,
              16, 7,
              18, 9
            ]
          }
        },
        // Buildings - light gray with subtle outline
        {
          "id": "buildings-outline",
          "type": "line",
          "source": "protomaps",
          "source-layer": "buildings",
          "paint": {
            "line-color": "#d0d0d0",
            "line-width": 0.5,
            "line-opacity": 0.5
          }
        },
        {
          "id": "buildings",
          "type": "fill",
          "source": "protomaps",
          "source-layer": "buildings",
          "paint": {
            "fill-color": "#e8e6e3",
            "fill-outline-color": "#d0d0d0"
          }
        },
        // Building names + icons
        {
          "id": "building-labels",
          "type": "symbol",
          "source": "protomaps",
          "source-layer": "buildings",
          "minzoom": 14,
          "layout": {
            "text-field": [
              "coalesce",
              ["get", "name"],
              ["get", "name_en"],
              ["get", "name_ja"],
              ["get", "ref"],
              ["get", "id"],
              ""
            ],
            "text-font": ["Noto Sans Regular"],
            "text-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              14, 11,
              16, 13,
              18, 15
            ],
            "text-offset": [0, 1.2],
            "text-anchor": "top",
            "text-optional": true
          },
          "paint": {
            "text-color": "#333333",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.2,
            "text-halo-blur": 0.8
          }
        },
        // Building labels fallback (some pmtiles store names in the labels layer)
        {
          "id": "building-labels-fallback",
          "type": "symbol",
          "source": "protomaps",
          "source-layer": "labels",
          "minzoom": 13,
          "filter": [
            "any",
            ["==", ["get", "class"], "building"],
            ["==", ["get", "kind"], "building"],
            ["==", ["get", "kind_detail"], "building"]
          ],
          "layout": {
            "text-field": [
              "coalesce",
              ["get", "name"],
              ["get", "name_en"],
              ["get", "name_ja"],
              ["get", "ref"],
              ["get", "id"],
              ""
            ],
            "text-font": ["Noto Sans Regular"],
            "text-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              14, 11,
              16, 13,
              18, 15
            ],
            "text-anchor": "center",
            "text-optional": true
          },
          "paint": {
            "text-color": "#333333",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.2,
            "text-halo-blur": 0.8
          }
        },
        // Road labels
        {
          "id": "road-labels",
          "type": "symbol",
          "source": "protomaps",
          "source-layer": "roads",
          "filter": ["has", "name"],
          "layout": {
            "text-field": [
              "coalesce",
              ["get", "name"],
              ["get", "ref"],
              ["get", "name_en"],
              ["get", "name_ja"],
              ""
            ],
            "text-font": ["Noto Sans Regular"],
            "text-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              10, 8,
              14, 10,
              18, 12
            ],
            "symbol-placement": "line",
            "text-rotation-alignment": "map",
            "text-pitch-alignment": "viewport"
          },
          "paint": {
            "text-color": "#787878",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5,
            "text-halo-blur": 1
          }
        },
        // Place labels - cities, towns, etc.
        {
          "id": "place-labels",
          "type": "symbol",
          "source": "protomaps",
          "source-layer": "places",
          "filter": ["has", "name"],
          "layout": {
            "text-field": ["get", "name"],
            "text-font": ["Noto Sans Regular"],
            "text-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              4, 10,
              8, 12,
              12, 14,
              16, 16
            ],
            "text-anchor": "center"
          },
          "paint": {
            "text-color": "#333333",
            "text-halo-color": "#ffffff",
            "text-halo-width": 2,
            "text-halo-blur": 1
          }
        },
        // General labels fallback
        {
          "id": "labels",
          "type": "symbol",
          "source": "protomaps",
          "source-layer": "labels",
          "filter": ["has", "name"],
          "layout": {
            "text-field": ["get", "name"],
            "text-font": ["Noto Sans Regular"],
            "text-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8, 10,
              12, 12,
              16, 14
            ]
          },
          "paint": {
            "text-color": "#333333",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5,
            "text-halo-blur": 1
          }
        }
      ]
    };
  }

  private ensurePmtilesProtocol(): void {
    if (this.pmtilesProtocolRegistered) {
      return;
    }

    const protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);
    this.pmtilesProtocolRegistered = true;
  }

  /**
   * Load POI icons, add them to the map, then add hospital/police icon and label layers.
   * Called after style load so the style never references missing images (which triggers errors in the worker).
   */
  private addPoiLayersWhenReady(): void {
    if (!this.map) {
      console.log('[POI] Skipping - map not available');
      return;
    }

    // Check if offline style is active
    const protomapsSource = this.map.getSource('protomaps');
    if (!protomapsSource) {
      console.log('[POI] Skipping - protomaps source not found (online style active?)');
      return;
    }

    if (!this.map.getLayer('place-labels')) {
      console.log('[POI] Skipping - place-labels layer not found, style may not be ready yet');
      // Retry after a short delay
      setTimeout(() => this.addPoiLayersWhenReady(), 500);
      return;
    }

    if (this.map.getLayer('pois-hospital-police-icon')) {
      console.log('[POI] Layers already added');
      return;
    }

    // Check if 'pois' source-layer exists in the source
    const source = protomapsSource as any;
    if (source.type === 'vector' && source.vectorLayerIds) {
      const hasPoisLayer = source.vectorLayerIds.includes('pois');
      console.log('[POI] Source-layer check:', { hasPoisLayer, vectorLayerIds: source.vectorLayerIds });
      if (!hasPoisLayer) {
        console.warn('[POI] Warning: "pois" source-layer not found in protomaps source. POI icons will not appear.');
      }
    }

    const ids = Object.keys(POI_ICON_SVG) as (keyof typeof POI_ICON_SVG)[];
    const missingIds = ids.filter((id) => !this.map!.hasImage(id));

    if (missingIds.length === 0) {
      // All images already loaded, just add layers
      console.log('[POI] All images already loaded, adding layers');
      this.addPoiLayers();
      return;
    }

    // Load images using HTMLImageElement approach (more reliable than loadImage for data URIs)
    const loadPromises = missingIds.map((id) => {
      return new Promise<{ id: string; img: HTMLImageElement | null }>((resolve) => {
        const dataUrl = 'data:image/svg+xml,' + encodeURIComponent(POI_ICON_SVG[id]);
        const img = new Image();
        img.onload = () => {
          resolve({ id, img });
        };
        img.onerror = (err) => {
          console.warn('[POI] Failed to load image', id, err);
          resolve({ id, img: null });
        };
        img.src = dataUrl;
      });
    });

    Promise.all(loadPromises).then((results) => {
      if (!this.map) {
        console.warn('[POI] Map no longer available after image load');
        return;
      }
      let added = 0;
      let failed = 0;
      results.forEach(({ id, img }) => {
        if (img && img.complete && img.naturalWidth > 0) {
          try {
            if (!this.map.hasImage(id)) {
              this.map.addImage(id, img);
              added++;
            } else {
              console.log('[POI] Image already exists:', id);
            }
          } catch (err) {
            console.error('[POI] Error adding image', id, err);
            failed++;
          }
        } else {
          failed++;
        }
      });
      if (added > 0) {
        // Small delay to ensure images are registered
        setTimeout(() => {
          this.addPoiLayers();
        }, 100);
      } else {
        console.error('[POI] No images were added, cannot create layers');
      }
    }).catch((err) => {
      console.error('[POI] Error in Promise.all', err);
    });
  }

  private addPoiLayers(): void {
    if (!this.map) {
      console.warn('[POI] Cannot add layers - map not available');
      return;
    }
    if (this.map.getLayer('pois-hospital-police-icon')) {
      console.log('[POI] Layers already exist');
      return;
    }

    // Verify images are loaded before adding layers
    const requiredImages = [
      'poi-hospital',
      'poi-police',
      'poi-clinic',
      'poi-doctors',
      'poi-fire',
      'poi-court',
      'poi-military',
      'poi-train',
      'poi-airport',
      'poi-school',
      'poi-townhall',
      'poi-post',
      'poi-library',
      'poi-pharmacy',
      'poi-bank',
      'poi-fuel',
      'poi-bus',
      'poi-tram',
      'poi-parking',
      'poi-polyclinic',
      'poi-embassy'
    ];
    const missingImages = requiredImages.filter((id) => !this.map.hasImage(id));
    if (missingImages.length > 0) {
      console.warn('[POI] Missing images:', missingImages, '- cannot add layers yet');
      return;
    }
    // Add layers on top so POI icons and labels are visible above base map
    try {
      // First, add a debug layer to see if ANY POIs exist (uncomment to test)
      // this.map.addLayer({
      //   id: 'pois-debug-all',
      //   type: 'circle',
      //   source: 'protomaps',
      //   'source-layer': 'pois',
      //   minzoom: 10,
      //   paint: {
      //     'circle-radius': 5,
      //     'circle-color': '#ff0000',
      //     'circle-stroke-width': 1,
      //     'circle-stroke-color': '#ffffff'
      //   }
      // });

      this.map.addLayer({
        id: 'pois-hospital-police-icon',
        type: 'symbol',
        source: 'protomaps',
        'source-layer': 'pois',
        minzoom: 10,
        filter: ['in', ['get', 'kind'], ['literal', [
          'hospital',
          'clinic',
          'doctors',
          'police',
          'fire_station',
          'courthouse',
          'court',
          'military',
          'naval_base',
          'train_station',
          'station',
          'aerodrome',
          'airfield',
          'school',
          'college',
          'university',
          'townhall',
          'post_office',
          'library',
          'pharmacy',
          'bank',
          'atm',
          'fuel',
          'bus_station',
          'bus_stop',
          'tram_stop',
          'parking',
          'parking_entrance',
          'polyclinic',
          'embassy',
          'consulate'
        ]]],
        layout: {
          'icon-image': ['match', ['get', 'kind'],
            'hospital', 'poi-hospital',
            'clinic', 'poi-clinic',
            'doctors', 'poi-doctors',
            'police', 'poi-police',
            'fire_station', 'poi-fire',
            'courthouse', 'poi-court',
            'court', 'poi-court',
            'military', 'poi-military',
            'naval_base', 'poi-military',
            'train_station', 'poi-train',
            'station', 'poi-train',
            'aerodrome', 'poi-airport',
            'airfield', 'poi-airport',
            'school', 'poi-school',
            'college', 'poi-school',
            'university', 'poi-school',
            'townhall', 'poi-townhall',
            'post_office', 'poi-post',
            'library', 'poi-library',
            'pharmacy', 'poi-pharmacy',
            'bank', 'poi-bank',
            'atm', 'poi-bank',
            'fuel', 'poi-fuel',
            'bus_station', 'poi-bus',
            'bus_stop', 'poi-bus',
            'tram_stop', 'poi-tram',
            'parking', 'poi-parking',
            'parking_entrance', 'poi-parking',
            'polyclinic', 'poi-polyclinic',
            'embassy', 'poi-embassy',
            'consulate', 'poi-embassy',
            ''
          ],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.6, 12, 0.8, 14, 1.0, 18, 1.2],
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
      this.map.addLayer({
        id: 'pois-hospital-police-labels',
        type: 'symbol',
        source: 'protomaps',
        'source-layer': 'pois',
        minzoom: 12,
        filter: ['in', ['get', 'kind'], ['literal', [
          'hospital',
          'clinic',
          'doctors',
          'police',
          'fire_station',
          'courthouse',
          'court',
          'military',
          'naval_base',
          'train_station',
          'station',
          'aerodrome',
          'airfield',
          'school',
          'college',
          'university',
          'townhall',
          'post_office',
          'library',
          'pharmacy',
          'bank',
          'atm',
          'fuel',
          'bus_station',
          'bus_stop',
          'tram_stop',
          'parking',
          'parking_entrance',
          'polyclinic',
          'embassy',
          'consulate'
        ]]],
        layout: {
          'text-field': ['coalesce', ['get', 'name'], ['get', 'name_en'], ['get', 'name_ja'], ''],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 16, 12, 18, 14],
          'text-offset': [0, 1.4],
          'text-anchor': 'top',
          'text-optional': true
        },
        paint: {
          'text-color': '#333333',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
          'text-halo-blur': 0.8
        }
      });
    } catch (err) {
      console.error('[POI] Error adding layers', err);
    }
  }

  private normalizeCenter(center: any): [number, number] {
    if (Array.isArray(center)) {
      return [center[0], center[1]];
    }
    if (center && typeof center === 'object') {
      if ('lng' in center && 'lat' in center) {
        return [center.lng, center.lat];
      }
      if ('lon' in center && 'lat' in center) {
        return [center.lon, center.lat];
      }
    }
    return [0, 0];
  }

  private getUserLocation(): void {
    if (this.currentGeoLocation) {
      const latlng = Array.isArray(this.currentGeoLocation)
        ? { lat: this.currentGeoLocation[1], lng: this.currentGeoLocation[0] }
        : this.currentGeoLocation;
      this.zone.run(() => {
        this.geoLocation.emit(latlng);
      });
      return;
    }

    if (navigator.geolocation && this.addMyMarker && !environment.disableUserLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latlng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.zone.run(() => {
            this.geoLocation.emit(latlng);
          });
          if (this.addMyMarker) {
            this.marker = {
              position: latlng,
              title: this.translate.instant('My Pin'),
              info: 'My Marker info'
            };
            if (this.defaultMarkerIcon) {
              this.marker.icon = this.defaultMarkerIcon;
            }
            this.showMarkersIfReady();
          }
        },
        (error) => {
          console.warn('Geolocation error:', error);
          const defaultLatlng = Array.isArray(this.center)
            ? { lat: this.center[1], lng: this.center[0] }
            : this.center;
          this.zone.run(() => {
            this.geoLocation.emit(defaultLatlng);
          });
        }
      );
    } else {
      const defaultLatlng = Array.isArray(this.center)
        ? { lat: this.center[1], lng: this.center[0] }
        : this.center;
      this.zone.run(() => {
        this.geoLocation.emit(defaultLatlng);
      });
    }
  }

  tilesload(event: any) {
    this.tilesloaded.emit(event);
  }

  mapInitialize(event: any) {
    this.mapInitialized.emit(event);
  }

  onMapReady(event: any) {
    this.mapReady.emit(event);
  }

  /**
   * Check if the map has finished loading
   * @returns true if the map has loaded, false otherwise
   */
  public isMapLoaded(): boolean {
    return this.mapLoaded && !!this.map;
  }

  /**
   * Check if the map style has finished loading
   * @returns true if the map style is loaded, false otherwise
   */
  public isMapStyleLoaded(): boolean {
    if (!this.map) return false;
    const loaded = this.map.isStyleLoaded();
    return loaded === true;
  }

  /**
   * Get the map instance (if loaded)
   * @returns the MapLibre map instance or null if not initialized
   */
  public getMap(): MapLibreMap | null {
    return this.map;
  }

  mapClick(event: any, gotoLocation: boolean = false) {
    if (this.editMode && this.map) {
      if (this.suppressNextMapClick) {
        this.suppressNextMapClick = false;
        return;
      }
      let lng: number;
      let lat: number;
      if (event.lngLat) {
        lat = event.lngLat.lat;
        lng = event.lngLat.lng;
      } else {
        lat = event.latLng ? event.latLng.lat() : event.lat;
        lng = event.latLng ? event.latLng.lng() : event.lng;
      }
      this.pushPolygonUndoState();
      const insertedOnEdge = this.tryInsertPolygonVertexOnEdge(lng, lat);
      if (!insertedOnEdge) {
        this.currentPolygonPoints.push([lng, lat]);
      }
      this.updatePreviewGeometry();
      this.syncEditPointMarkers();
      return;
    }
    if (this.isClickable) {
      let lat = event.latLng ? event.latLng.lat() : event.lat;
      let lng = event.latLng ? event.latLng.lng() : event.lng;
      if (event.lngLat) {
        lat = event.lngLat.lat;
        lng = event.lngLat.lng;
      }
      if (this.handleEnterSiteLeftClick(event.point)) {
        return;
      }
      if (this.enableAddessOnClick) {
        this.reverseGeocode(lat, lng);
      }
      if (gotoLocation) {
        // this.map.flyTo({ center: { lat: lat, lng: lng }, zoom: 15 });
        this.dragToLocation(undefined, { lat: lat, lng: lng });
      }
      // Floor plans: add building only from context menu (right-click), not left-click on map.
      if (!this.forBuildingFlow) {
        this.mapSelect.emit({ lat: lat, lng: lng, point: event?.point });
      }
    }
  }

  mapDrag(event: any) {
    if (this.map && this.mapDragGeoEnabled) {
      const center = this.map.getCenter();
      this.mapDragGeo.emit({ lat: center.lat, lng: center.lng });
    }
  }

  private attachEventHandlers(): void {
    if (!this.map) return;

    if (this.isClickable) {
      this.map.on('click', (e) => {
        try {
          // Ignore clicks that originated from marker elements
          if (e.originalEvent && e.originalEvent.target) {
            const target = e.originalEvent.target as HTMLElement;
            // Check if the click target is a marker element or inside a marker
            if (
              target.closest('.offline-marker') ||
              target.closest('.maplibregl-marker') ||
              target.closest('.' + this.EDIT_POINT_MARKER_CLASS) ||
              target.closest('.' + this.EDGE_MIDPOINT_MARKER_CLASS)
            ) {
              return; // Don't handle map click if it's on a marker
            }
          }
          if (e.lngLat) {
            this.zone.run(() => {
              this.mapClick({ lngLat: e.lngLat, point: e.point });
            });
          }
        } catch (err) {
          console.warn('Map click error:', err);
        }
      });
    }

    if (this.mapDragGeoEnabled) {
      this.map.on('dragend', () => {
        try {
          if (this.map) {
            this.zone.run(() => {
              this.mapDrag({});
            });
          }
        } catch (err) {
          console.warn('Map dragend error:', err);
        }
      });
    }

    this.map.on('mousedown', this.onMapPolygonBodyMousedown);
    this.map.on('mousedown', this.onMapRightMouseDown);
    this.map.on('mousemove', this.onMapRightMouseMove);
    this.map.on('movestart', this.onMapMoveStartCloseContextMenu);
    this.map.on('contextmenu', this.mapContextMenuHandler);
    this.map.on('zoomend', this.onClusterViewportChanged);
    this.map.on('moveend', this.onClusterViewportChanged);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydownPolygonUndo(ev: KeyboardEvent): void {
    if (!this.editMode || this.polygonUndoStack.length === 0) {
      return;
    }
    const isUndo = (ev.ctrlKey || ev.metaKey) && (ev.key === 'z' || ev.key === 'Z') && !ev.shiftKey;
    if (!isUndo) {
      return;
    }
    const t = ev.target as HTMLElement | null;
    if (t?.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }
    if (this.undoPolygonEdit()) {
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClickCloseContextMenu(ev: MouseEvent): void {
    if (!this.contextMenuVisible) {
      return;
    }
    const t = ev.target as HTMLElement | null;
    if (t?.closest('.offline-map-context-menu')) {
      return;
    }
    this.clearContextMenuState();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedownTrackRightButton(ev: MouseEvent): void {
    if (ev.button !== 2 || !this.isEventInsideComponent(ev)) {
      return;
    }
    this.rightMouseDownStartPoint = { x: ev.clientX, y: ev.clientY };
    this.rightMouseDragDetected = false;
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMousemoveDetectRightDrag(ev: MouseEvent): void {
    if (!this.rightMouseDownStartPoint) {
      return;
    }
    const dx = ev.clientX - this.rightMouseDownStartPoint.x;
    const dy = ev.clientY - this.rightMouseDownStartPoint.y;
    if (dx === 0 && dy === 0) {
      return;
    }
    this.rightMouseDragDetected = true;
    if (this.contextMenuVisible) {
      this.clearContextMenuState();
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseupResetRightDragTracking(ev: MouseEvent): void {
    if (ev.button !== 2 && !this.rightMouseDownStartPoint) {
      return;
    }
    this.rightMouseDownStartPoint = null;
  }

  private isEventInsideComponent(ev: MouseEvent): boolean {
    const host = this.elementRef?.nativeElement as HTMLElement | undefined;
    if (!host) {
      return false;
    }
    const path = typeof ev.composedPath === 'function' ? ev.composedPath() : undefined;
    if (Array.isArray(path) && path.length > 0) {
      return path.includes(host);
    }
    const target = ev.target as Node | null;
    return !!target && host.contains(target);
  }

  /** Same reverse lookup path as map search / pins: Google when available, else Nominatim. */
  private loadContextMenuAddress(lat: number, lng: number): void {
    const lang = this.getPreferredLanguage();
    this.geocodingService
      .resolveAddress(lat, lng, lang)
      .then((addr) => {
        this.zone.run(() => {
          this.contextMenuAddressLoading = false;
          const trimmed = addr?.trim();
          this.contextMenuAddress = trimmed || this.formatLatLngLine(lat, lng);
        });
      })
      .catch(() => {
        this.zone.run(() => {
          this.contextMenuAddressLoading = false;
          this.contextMenuAddress = this.formatLatLngLine(lat, lng);
        });
      });
  }

  private formatLatLngLine(lat: number, lng: number): string {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }

  /** Floor plan: start editable footprint polygon; parent sets [editMode] so smart-map does not reset it to false. */
  emitAddBuildingFromContextMenu(): void {
    const pt = this.contextMenuLngLat;
    if (!pt) {
      return;
    }
    const address = (this.contextMenuAddress || '').trim();
    this.clearContextMenuState();
    /** Parent opens building dialog with embedded map; no draft on main map. */
    this.mapSelect.emit({
      openAddBuildingDialog: true,
      lat: pt.lat,
      lng: pt.lng,
      address: address || undefined
    });
  }

  /**
   * Site mapping (dialog map): seed a new building footprint and enter polygon edit mode.
   * Call after map is ready with [editMode] true on smart-map.
   */
  startNewBuildingFootprintDraft(anchor: { lat: number; lng: number; address?: string }): void {
    if (!this.forBuildingFlow || !this.map) {
      return;
    }
    this.buildingDraftAnchor = {
      lat: anchor.lat,
      lng: anchor.lng,
      address: anchor.address
    };
    this.seedBuildingFootprintAroundPoint(anchor.lng, anchor.lat);
    this.buildingFootprintDraftActive = true;
    this.setEditMode(true);
    this.updatePreviewGeometry();
    this.syncEditPointMarkers();
    this.fitMapToCurrentDraftFootprintBounds();
  }

  /** Current draft footprint as a closed [lng,lat] ring, or null if invalid. */
  getBuildingFootprintDraftRing(): [number, number][] | null {
    if (!this.buildingFootprintDraftActive || this.currentPolygonPoints.length < 3) {
      return null;
    }
    return this.ensureClosedRing(
      this.currentPolygonPoints.map((p) => [p[0], p[1]] as [number, number])
    );
  }

  /** After seeding the draft square, zoom/pan so the footprint is centered with padding (site mapping add-building). */
  private fitMapToCurrentDraftFootprintBounds(): void {
    if (!this.forBuildingFlow || !this.map || this.currentPolygonPoints.length < 3) {
      return;
    }
    try {
      this.map.resize();
    } catch {
      /* ignore */
    }
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (const p of this.currentPolygonPoints) {
      if (!p || p.length < 2) {
        continue;
      }
      const lng = p[0];
      const lat = p[1];
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    }
    if (minLat === Infinity) {
      return;
    }
    try {
      const bounds: [[number, number], [number, number]] = [
        [minLng, minLat],
        [maxLng, maxLat]
      ];
      this.map.fitBounds(bounds, { padding: 100, duration: 400, maxZoom: this.MAX_ZOOM });
      this.refreshViewportAfterCameraMove();
    } catch {
      /* ignore */
    }
  }

  /** Default square footprint (~90 m side) so the user can drag corners and add vertices by clicking. */
  private seedBuildingFootprintAroundPoint(lng: number, lat: number, halfSizeM = 45): void {
    this.clearPolygonUndoStack();
    const latRad = (lat * Math.PI) / 180;
    const dLat = halfSizeM / 111320;
    const cos = Math.cos(latRad);
    const dLng = cos > 1e-6 ? halfSizeM / (111320 * cos) : dLat;
    this.currentPolygonPoints = [
      [lng - dLng, lat - dLat],
      [lng + dLng, lat - dLat],
      [lng + dLng, lat + dLat],
      [lng - dLng, lat + dLat]
    ];
  }

  /** Centroid of a closed [lng,lat] ring (for pin / metadata when draft anchor is missing). */
  private ringCentroidFromCoords(ring: [number, number][]): { lat: number; lng: number } {
    const pts =
      ring.length > 1 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : [...ring];
    let slng = 0;
    let slat = 0;
    for (const [lng, lat] of pts) {
      slng += lng;
      slat += lat;
    }
    const n = Math.max(pts.length, 1);
    return { lng: slng / n, lat: slat / n };
  }

  /** User chose "Save building" from map context menu while editing the footprint. */
  saveBuildingFootprintDraft(): void {
    if (this.currentPolygonPoints.length < 3) {
      return;
    }
    const ring = this.ensureClosedRing(
      this.currentPolygonPoints.map((p) => [p[0], p[1]] as [number, number])
    );
    const c = this.ringCentroidFromCoords(ring);
    const anchor = this.buildingDraftAnchor;
    const editId = this.savedFootprintEditBuildingId;
    this.mapSelect.emit({
      buildingFootprintSave: true,
      footprint: ring,
      lat: anchor?.lat ?? c.lat,
      lng: anchor?.lng ?? c.lng,
      address: anchor?.address,
      isEdit: !!editId,
      buildingId: editId || undefined
    });
    this.savedFootprintEditBuildingId = null;
    this.clearContextMenuState();
    /** Keep edit mode so dialog map can keep editing until the user saves or cancels the dialog. */
  }

  /** Site mapping: right-click on footprint polygon — open floor plan viewer for that building. */
  emitViewFloorPlansFromPolygonContextMenu(): void {
    const id = this.contextMenuSavedPolygonBuildingId;
    this.clearContextMenuState();
    if (!id) {
      return;
    }
    this.openFloorPlansByBuildingId(id);
  }

  /** Site mapping: right-click on footprint polygon — locate a room in that building. */
  emitLocateRoomFromPolygonContextMenu(): void {
    const id = this.contextMenuSavedPolygonBuildingId;
    this.clearContextMenuState();
    if (!id) {
      return;
    }
    this.mapSelect.emit({ locateRoom: true, buildingId: id });
  }

  /** Case map / dashboard: open Site Mapping floor plans for the footprint that was right-clicked. */
  viewFloorPlansFromContextMenu(): void {
    const id = this.contextMenuCaseBuildingFootprintId;
    this.clearContextMenuState();
    if (!id) {
      return;
    }
    this.openFloorPlansByBuildingId(id);
  }

  /** Case map / dashboard: open room locator for the footprint that was right-clicked. */
  locateRoomFromContextMenu(): void {
    const id = this.contextMenuCaseBuildingFootprintId;
    this.clearContextMenuState();
    if (!id) {
      return;
    }
    this.mapService.mapContextMenuCommand.next({ command: 'locateRoom', buildingId: id });
  }

  /** Case map / dashboard: open site-dispatch flow at the right-clicked point (with optional site context). */
  async openDispatchFromContextMenu(): Promise<void> {
    if (this.contextMenuDispatchDisabled) {
      return;
    }
    const buildingId = String(this.contextMenuCaseBuildingFootprintId || '').trim();
    const lngLat = this.contextMenuLngLat;
    const address = (this.contextMenuAddress || '').trim();
    this.clearContextMenuState();
    if (!lngLat) {
      return;
    }
    const snapshotDataUrl = await this.captureMapSnapshotDataUrl();
    this.mapService.mapContextMenuCommand.next({
      command: 'dispatch',
      buildingId: buildingId || undefined,
      point: { lat: lngLat.lat, lng: lngLat.lng, address },
      snapshotDataUrl: snapshotDataUrl || undefined,
    });
  }

  private async captureMapSnapshotDataUrl(): Promise<string | null> {
    // Allow the context menu to disappear before capture.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve(true))));
    try {
      const canvas = this.map?.getCanvas?.();
      if (canvas && typeof (canvas as any).toDataURL === 'function') {
        return (canvas as any).toDataURL('image/png');
      }
    } catch {
      // fall through to html2canvas
    }
    const el = this.mapContainer?.nativeElement;
    if (!el) {
      return null;
    }
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  /** Site mapping: right-click menu — edit an existing saved footprint (parent opens dialog with map). */
  emitEditSavedPolygonFromContextMenu(): void {
    const id = this.contextMenuSavedPolygonBuildingId;
    this.clearContextMenuState();
    if (!id) {
      return;
    }
    this.mapSelect.emit({ openEditBuildingDialog: true, buildingId: id });
  }

  /** Site mapping: right-click menu — delete the building whose footprint was clicked. */
  emitDeleteSavedPolygonFromContextMenu(): void {
    const id = this.contextMenuSavedPolygonBuildingId;
    this.clearContextMenuState();
    if (!id) {
      return;
    }
    this.mapSelect.emit({ buildingFootprintDeleteFromMap: true, buildingId: id });
  }

  /**
   * Load a saved building footprint into the polygon editor (parent removes that area from the overlay first).
   */
  loadFootprintForEdit(params: {
    buildingId: string;
    footprint: [number, number][];
    anchor: { lat: number; lng: number; address?: string };
  }): void {
    this.savedFootprintEditBuildingId = params.buildingId;
    this.buildingDraftAnchor = {
      lat: params.anchor.lat,
      lng: params.anchor.lng,
      address: params.anchor.address
    };
    const ring = params.footprint;
    const closed =
      ring.length > 1 &&
      ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1];
    const open = closed ? ring.slice(0, -1) : [...ring];
    this.currentPolygonPoints = open.map((p) => [p[0], p[1]] as [number, number]);
    this.clearPolygonUndoStack();
    this.buildingFootprintDraftActive = true;
    this.setEditMode(true);
    this.updatePreviewGeometry();
    this.syncEditPointMarkers();
    this.fitMapToCurrentDraftFootprintBounds();
  }

  /** Zoom/pan so the current building footprint draft fills the map (e.g. after dialog layout settles). */
  fitBuildingFootprintDraftToView(): void {
    this.fitMapToCurrentDraftFootprintBounds();
  }

  /** Cancel footprint drawing (context menu or parent). */
  cancelBuildingFootprintDraft(): void {
    this.mapSelect.emit({ buildingFootprintCancel: true });
    this.clearContextMenuState();
    this.setEditMode(false);
  }

  /** @deprecated alias */
  exitBuildingFootprintDraft(): void {
    this.cancelBuildingFootprintDraft();
  }

  get buildingFootprintCanSave(): boolean {
    return this.currentPolygonPoints.length >= 3;
  }

  get polygonCanUndo(): boolean {
    return this.polygonUndoStack.length > 0;
  }

  private clonePolygonPoints(pts: [number, number][]): [number, number][] {
    return pts.map((p) => [p[0], p[1]] as [number, number]);
  }

  private pushPolygonUndoState(): void {
    this.polygonUndoStack.push(this.clonePolygonPoints(this.currentPolygonPoints));
    while (this.polygonUndoStack.length > OfflineMapComponent.POLYGON_UNDO_MAX) {
      this.polygonUndoStack.shift();
    }
    this.polygonUndoStateChanged.emit();
  }

  /** Distance from click to segment in screen pixels; `proj` is the closest point on the segment in lng/lat. */
  private distancePointToSegmentScreenPx(
    clickLng: number,
    clickLat: number,
    a: [number, number],
    b: [number, number]
  ): { dist: number; proj: { lng: number; lat: number }; t: number } {
    const map = this.map!;
    const P = map.project([clickLng, clickLat]);
    const A = map.project(a);
    const B = map.project(b);
    const vx = B.x - A.x;
    const vy = B.y - A.y;
    const wx = P.x - A.x;
    const wy = P.y - A.y;
    const c2 = vx * vx + vy * vy;
    const t = c2 < 1e-10 ? 0 : Math.max(0, Math.min(1, (vx * wx + vy * wy) / c2));
    const projX = A.x + t * vx;
    const projY = A.y + t * vy;
    const dist = Math.hypot(P.x - projX, P.y - projY);
    const projLngLat = map.unproject([projX, projY]);
    return { dist, proj: { lng: projLngLat.lng, lat: projLngLat.lat }, t };
  }

  /**
   * If the click is near an existing edge, insert a vertex at the closest point on that edge.
   * Returns true if a vertex was inserted (caller should not append).
   */
  private tryInsertPolygonVertexOnEdge(lng: number, lat: number): boolean {
    const pts = this.currentPolygonPoints;
    const n = pts.length;
    if (n < 2 || !this.map) {
      return false;
    }
    const maxPx = OfflineMapComponent.POLYGON_EDGE_INSERT_PX;
    const tMin = 0.03;
    const tMax = 0.97;

    let bestDist = Infinity;
    let bestInsertIndex = -1;
    let bestLng = 0;
    let bestLat = 0;

    for (let i = 0; i < n - 1; i++) {
      const { dist, proj, t } = this.distancePointToSegmentScreenPx(lng, lat, pts[i], pts[i + 1]);
      if (dist < bestDist && t > tMin && t < tMax) {
        bestDist = dist;
        bestInsertIndex = i + 1;
        bestLng = proj.lng;
        bestLat = proj.lat;
      }
    }

    if (n >= 3) {
      const { dist, proj, t } = this.distancePointToSegmentScreenPx(lng, lat, pts[n - 1], pts[0]);
      if (dist < bestDist && t > tMin && t < tMax) {
        bestDist = dist;
        bestInsertIndex = n;
        bestLng = proj.lng;
        bestLat = proj.lat;
      }
    }

    if (bestDist > maxPx || bestInsertIndex < 0) {
      return false;
    }

    pts.splice(bestInsertIndex, 0, [bestLng, bestLat]);
    return true;
  }

  private clearPolygonUndoStack(): void {
    if (this.polygonUndoStack.length === 0) {
      return;
    }
    this.polygonUndoStack.length = 0;
    this.polygonUndoStateChanged.emit();
  }

  /** Restore previous polygon state (add vertex, drag handle, or delete vertex). */
  undoPolygonEdit(): boolean {
    if (!this.editMode || this.polygonUndoStack.length === 0) {
      return false;
    }
    const prev = this.polygonUndoStack.pop();
    if (!prev) {
      return false;
    }
    this.currentPolygonPoints = this.clonePolygonPoints(prev);
    this.updatePreviewGeometry();
    this.syncEditPointMarkers();
    this.polygonUndoStateChanged.emit();
    return true;
  }

  private clearContextMenuState(): void {
    this.contextMenuVisible = false;
    this.contextMenuLngLat = null;
    this.contextMenuAddress = null;
    this.contextMenuAddressLoading = false;
    this.contextMenuCoverageCameras = [];
    this.contextMenuCaseBuildingFootprintId = null;
    this.contextMenuSavedPolygonBuildingId = null;
  }

  /** Returns distinct cameras (`_id`, `camera_id`) whose sector polygon contains the point. */
  private computeCamerasInSectorAt(lng: number, lat: number): { _id: string; camera_id: string }[] {
    const byKey = new Map<string, { _id: string; camera_id: string }>();
    this.sectors.forEach((sector) => {
      const data = sector.data;
      if (!data || data.markerType !== 'camera') {
        return;
      }
      const mongoId = data._id != null && data._id !== '' ? String(data._id) : '';
      const cameraId = data.camera_id != null && data.camera_id !== '' ? String(data.camera_id) : '';
      if (!mongoId && !cameraId) {
        return;
      }
      const ring = GeoUtils.calculateSectorPolygon(
        sector.center,
        sector.radius,
        sector.azimuth,
        sector.fov
      );
      if (GeoUtils.pointInPolygonLngLat({ lng, lat }, ring)) {
        const key = mongoId || `camera_id:${cameraId}`;
        if (!byKey.has(key)) {
          byKey.set(key, { _id: mongoId, camera_id: cameraId });
        }
      }
    });
    return Array.from(byKey.values());
  }

  findCameraCoverageFromContextMenu(): void {
    const cameras = [...this.contextMenuCoverageCameras];
    this.clearContextMenuState();
    // console.log('Find Camera Coverage — cameras at click:', cameras);
    this.mapService.openCameras.next(cameras);
  }

  onMapContextMenuOption(action: 'centerHere' | 'copyCoordinates'): void {
    const pt = this.contextMenuLngLat;
    this.clearContextMenuState();
    if (!pt || !this.map) {
      return;
    }
    const lngLat = { lng: pt.lng, lat: pt.lat };
    this.mapContextMenuAction.emit({ action, lngLat: { ...lngLat } });
    if (action === 'centerHere') {
      this.map.easeTo({
        center: [pt.lng, pt.lat],
        zoom: Math.max(this.map.getZoom(), 14),
        duration: 600,
      });
    } else if (action === 'copyCoordinates') {
      const text = `${pt.lat.toFixed(6)}, ${pt.lng.toFixed(6)}`;
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.zone.run(() => {
            this.layoutUtilsService.showNotification(
              this.translate.instant('Coordinates copied'),
              this.translate.instant('Dismiss')
            );
          });
        }).catch(() => {
          this.zone.run(() => {
            this.layoutUtilsService.showNotification(
              this.translate.instant('Could not copy coordinates'),
              this.translate.instant('Dismiss')
            );
          });
        });
      }
    }
  }

  resetMapViewFromContextMenu(): void {
    this.clearContextMenuState();
    if (!this.map) {
      return;
    }
    const refocus = () => {
      // Site-building dialog: when editing a draft footprint there are often no markers,
      // so marker-based reset won't recenter the map.
      if (this.forBuildingFlow && this.buildingFootprintDraftActive) {
        this.fitMapToCurrentDraftFootprintBounds();
        return;
      }
      // this.showMarkersIfReady([], true);
      // Fit to currently visible markers only. Do not call showMarkersIfReady —
      // that rebuilds from markersOriginal and re-tracks live NATS, which can
      // resurrect pins that were intentionally hidden/removed.
      const visibleMarkers = (this.markers || []).filter(
        (mark) =>
          mark &&
          !mark.hideIt &&
          !(mark.markerType === 'camera' && mark.mapCameraFilterHidden) &&
          mark.lat !== undefined &&
          mark.lat !== null &&
          mark.lat !== '' &&
          mark.lng !== undefined &&
          mark.lng !== null &&
          mark.lng !== ''
      );
      const hasFootprintsForFit =
        this.forBuildingFlow &&
        !this.buildingFootprintDraftActive &&
        this.customMapAreasData?.length > 0;
      if (visibleMarkers.length > 0 || hasFootprintsForFit) {
        this.calculateZoomLevel(visibleMarkers);
      }
    };

    const shouldResetCameraAngle =
      Math.abs(this.map.getBearing()) > 0.001 ||
      Math.abs(this.map.getPitch()) > 0.001;

    // Reset angle first, then refocus (or refocus immediately if angle is already reset).
    if (shouldResetCameraAngle) {
      this.map.once('moveend', refocus);
      this.map.easeTo({
        bearing: 0,
        pitch: 0,
        duration: 300
      });
    } else {
      refocus();
    }
  }

  private addCustomAreaLayersIfNeeded(): void {
    if (!this.map || !this.map.getStyle()) return;
    const addWhenStyleLoaded = () => {
      if (!this.map) return;
      if (this.map.getSource(this.CUSTOM_AREAS_SOURCE_ID)) return;
      this.map.addSource(this.CUSTOM_AREAS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
      this.map.addLayer({
        id: this.CUSTOM_AREAS_SOURCE_ID + '-fill',
        type: 'fill',
        source: this.CUSTOM_AREAS_SOURCE_ID,
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.42
        }
      });
      this.map.addLayer({
        id: this.CUSTOM_AREAS_SOURCE_ID + '-line',
        type: 'line',
        source: this.CUSTOM_AREAS_SOURCE_ID,
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.5, 10, 2, 14, 2.5, 18, 3.5]
        }
      });
      this.map.addSource(this.CUSTOM_AREA_PREVIEW_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
      this.map.addLayer({
        id: this.CUSTOM_AREA_PREVIEW_SOURCE_ID + '-fill',
        type: 'fill',
        source: this.CUSTOM_AREA_PREVIEW_SOURCE_ID,
        paint: {
          'fill-color': '#3388ff',
          'fill-opacity': 0.15
        }
      });
      this.map.addLayer({
        id: this.CUSTOM_AREA_PREVIEW_SOURCE_ID + '-line',
        type: 'line',
        source: this.CUSTOM_AREA_PREVIEW_SOURCE_ID,
        paint: {
          'line-color': '#3388ff',
          'line-width': 2,
          'line-dasharray': [2, 1]
        }
      });
      this.map.addSource(this.PENDING_AREAS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
      this.map.addLayer({
        id: this.PENDING_AREAS_SOURCE_ID + '-fill',
        type: 'fill',
        source: this.PENDING_AREAS_SOURCE_ID,
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#3388ff'],
          'fill-opacity': 0.2
        }
      });
      this.map.addLayer({
        id: this.PENDING_AREAS_SOURCE_ID + '-line',
        type: 'line',
        source: this.PENDING_AREAS_SOURCE_ID,
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#3388ff'],
          'line-width': 2,
          'line-dasharray': [2, 1]
        }
      });
      this.updateCustomAreasSource();
    };
    if (this.map.isStyleLoaded()) {
      addWhenStyleLoaded();
    } else {
      this.map.once('style.load', addWhenStyleLoaded);
    }
  }

  private updatePreviewGeometry(): void {
    if (!this.map) return;
    const src = this.map.getSource(this.CUSTOM_AREA_PREVIEW_SOURCE_ID) as maplibregl.GeoJSONSource;
    if (!src) return;
    const pts = this.currentPolygonPoints;
    if (pts.length === 0) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const feature =
      pts.length >= 3
        ? ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [[...pts, pts[0]]] }
        } as GeoJSON.Feature<GeoJSON.Polygon>)
        : ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: pts }
        } as GeoJSON.Feature<GeoJSON.LineString>);
    src.setData({ type: 'FeatureCollection', features: [feature as any] });
  }

  private completeCurrentPolygon(): void {
    if (this.currentPolygonPoints.length < 3) return;
    this.clearEditPointMarkers();
    const ring = [...this.currentPolygonPoints, this.currentPolygonPoints[0]];
    const area: CustomMapAreaData = {
      color: '#3388ff',
      shape: 'polygon',
      coordinates: ring,
      label: ''
    };
    this.pendingAreas.push(area);
    this.updatePendingAreasSource();
    this.clearPolygonUndoStack();
    this.currentPolygonPoints = [];
    this.updatePreviewGeometry();
  }

  private updatePendingAreasSource(): void {
    if (!this.map) return;
    const src = this.map.getSource(this.PENDING_AREAS_SOURCE_ID) as maplibregl.GeoJSONSource;
    if (!src) return;
    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = this.pendingAreas.map(area => {
      const ring = this.ensureClosedRing(area.coordinates);
      return {
        type: 'Feature',
        properties: { color: area.color, shape: area.shape, id: area.id || '', label: area.label || '' },
        geometry: { type: 'Polygon', coordinates: [ring] }
      } as GeoJSON.Feature<GeoJSON.Polygon>;
    });
    src.setData({ type: 'FeatureCollection', features });
  }

  private updateCustomAreasSource(): void {
    if (!this.map) return;
    const src = this.map.getSource(this.CUSTOM_AREAS_SOURCE_ID) as maplibregl.GeoJSONSource;
    if (!src) return;
    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = this.customMapAreasData.map(area => {
      const ring = this.ensureClosedRing(area.coordinates);
      return {
        type: 'Feature',
        properties: { color: area.color, shape: area.shape, id: area.id || '', label: area.label || '' },
        geometry: { type: 'Polygon', coordinates: [ring] }
      } as GeoJSON.Feature<GeoJSON.Polygon>;
    });
    src.setData({ type: 'FeatureCollection', features });
  }

  private ensureClosedRing(coords: [number, number][]): [number, number][] {
    if (!coords.length) return coords;
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return coords;
    return [...coords, first];
  }

  private updateEditModeCursor(): void {
    if (!this.map) return;
    const canvas = this.map.getCanvas();
    if (canvas) {
      canvas.style.cursor = this.editMode ? 'crosshair' : '';
    }
    // Allow double-click zoom in edit mode (we no longer use double-click to complete polygon)
    if (this.map.doubleClickZoom && !this.editMode) {
      this.map.doubleClickZoom.enable();
    }
  }

  private removeEditModeDblclick(): void {
    if (this.editModeDblclickListener) {
      this.editModeDblclickListener();
      this.editModeDblclickListener = null;
    }
  }

  setEditMode(value: boolean): void {
    this.editMode = value;
    if (!value) {
      this.endPolygonBodyDragIfActive();
      this.suppressNextMapClick = false;
      this.savedFootprintEditBuildingId = null;
      this.buildingFootprintDraftActive = false;
      this.buildingDraftAnchor = null;
      this.clearPolygonUndoStack();
      this.clearEditPointMarkers();
      this.currentPolygonPoints = [];
      this.updatePreviewGeometry();
      this.clearPendingAreas();
    }
    if (this.map) {
      this.updateEditModeCursor();
      // No double-click completion: area is added only when user confirms "Finish area"
      if (!this.editMode) {
        this.removeEditModeDblclick();
      }
    }
  }

  getDrawnAreas(): Array<[number, number][]> {
    return this.customMapAreasData.map(a => this.ensureClosedRing(a.coordinates));
  }

  /** Get all custom map areas with color and shape metadata */
  getCustomMapAreas(): CustomMapAreaData[] {
    return [...this.customMapAreasData];
  }

  /** Areas drawn in current edit session (not yet confirmed). */
  getPendingAreas(): CustomMapAreaData[] {
    return [...this.pendingAreas];
  }

  /** Add current drawn polygon and any pending areas into customMapAreasData (called when user confirms "Finish area"). */
  confirmPendingAreas(): void {
    // Add current polygon (points being drawn) if it has at least 3 points
    if (this.currentPolygonPoints.length >= 3) {
      const ring = [...this.currentPolygonPoints, this.currentPolygonPoints[0]];
      this.customMapAreasData.push({
        color: '#3388ff',
        shape: 'polygon',
        coordinates: ring,
        label: ''
      });
    }
    this.clearEditPointMarkers();
    this.clearPolygonUndoStack();
    this.currentPolygonPoints = [];
    this.customMapAreasData.push(...this.pendingAreas);
    this.pendingAreas = [];
    if (this.map) {
      this.updatePreviewGeometry();
      this.updateCustomAreasSource();
      this.updatePendingAreasSource();
    }
  }

  /** Discard pending areas without adding to customMapAreasData. */
  clearPendingAreas(): void {
    this.pendingAreas = [];
    if (this.map) {
      const src = this.map.getSource(this.PENDING_AREAS_SOURCE_ID) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
    }
  }

  /** Type guard: true if areas is legacy array of rings (Array<[number, number][]>). */
  private isLegacyRingsArray(areas: CustomMapAreaData[] | Array<[number, number][]>): boolean {
    if (!areas?.length) return false;
    const first = areas[0];
    if (!Array.isArray(first) || first.length === 0) return false;
    const pt = first[0];
    return Array.isArray(pt) && typeof pt[0] === 'number' && typeof pt[1] === 'number';
  }

  /**
   * Set custom map areas. Accepts full CustomMapAreaData[] or legacy array of rings (default color applied).
   */
  setCustomMapAreas(areas: CustomMapAreaData[] | Array<[number, number][]>): void {
    if (!areas || !areas.length) {
      this.customMapAreasData = [];
    } else if (this.isLegacyRingsArray(areas)) {
      this.customMapAreasData = (areas as Array<[number, number][]>).map(ring => ({
        color: '#3388ff',
        shape: 'polygon',
        coordinates: this.ensureClosedRing(ring)
      }));
    } else {
      this.customMapAreasData = (areas as CustomMapAreaData[]).map(a => ({
        ...a,
        coordinates: this.ensureClosedRing(a.coordinates)
      }));
    }
    if (this.map) {
      this.addCustomAreaLayersIfNeeded();
      this.updateCustomAreasSource();
      this.applySavedCustomAreasLayerPaint();
    }
  }

  /** Stronger blue fill + outline at all zooms (also upgrades paint on maps created before these values). */
  private applySavedCustomAreasLayerPaint(): void {
    if (!this.map?.getLayer(this.CUSTOM_AREAS_SOURCE_ID + '-fill')) {
      return;
    }
    try {
      this.map.setPaintProperty(this.CUSTOM_AREAS_SOURCE_ID + '-fill', 'fill-opacity', 0.42);
      this.map.setPaintProperty(
        this.CUSTOM_AREAS_SOURCE_ID + '-line',
        'line-width',
        ['interpolate', ['linear'], ['zoom'], 4, 1.5, 10, 2, 14, 2.5, 18, 3.5]
      );
    } catch {
      /* ignore */
    }
  }

  /** Add or update a single custom map area (if id matches existing). */
  addCustomMapArea(area: CustomMapAreaData): void {
    const ring = this.ensureClosedRing(area.coordinates);
    const idx = area.id ? this.customMapAreasData.findIndex(a => a.id === area.id) : -1;
    const toAdd: CustomMapAreaData = { ...area, coordinates: ring };
    if (idx >= 0) {
      this.customMapAreasData[idx] = toAdd;
    } else {
      this.customMapAreasData.push(toAdd);
    }
    if (this.map) {
      this.addCustomAreaLayersIfNeeded();
      this.updateCustomAreasSource();
      this.applySavedCustomAreasLayerPaint();
    }
  }

  removeCustomMapArea(id: string): void {
    this.customMapAreasData = this.customMapAreasData.filter(a => a.id !== id);
    if (this.map) {
      this.updateCustomAreasSource();
    }
  }

  clearDrawnAreas(): void {
    this.clearEditPointMarkers();
    this.clearPolygonUndoStack();
    this.customMapAreasData = [];
    this.pendingAreas = [];
    this.currentPolygonPoints = [];
    if (this.map) {
      const src = this.map.getSource(this.CUSTOM_AREAS_SOURCE_ID) as maplibregl.GeoJSONSource;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
      this.updatePendingAreasSource();
      this.updatePreviewGeometry();
    }
  }

  private clearEditPointMarkers(): void {
    this.editPointMarkers.forEach((m) => m.remove());
    this.editPointMarkers = [];
    this.edgeMidpointMarkers.forEach((m) => m.remove());
    this.edgeMidpointMarkers = [];
  }

  private createEditPointElement(index: number): HTMLDivElement {
    const el = document.createElement('div');
    el.className = this.EDIT_POINT_MARKER_CLASS;
    el.style.width = '14px';
    el.style.height = '14px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = '#3388ff';
    el.style.border = '2px solid #fff';
    el.style.cursor = 'move';
    el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
    el.setAttribute('data-edit-point-index', String(index));
    el.title = this.translate.instant('Drag to move, right-click to delete');
    return el;
  }

  private createEdgeMidpointElement(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = this.EDGE_MIDPOINT_MARKER_CLASS;
    el.style.width = '12px';
    el.style.height = '12px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = 'rgba(51, 136, 255, 0.38)';
    el.style.border = '1px solid rgba(255, 255, 255, 0.75)';
    el.style.cursor = 'move';
    el.style.boxShadow = '0 1px 2px rgba(0,0,0,0.28)';
    el.title = this.translate.instant('Edge midpoint drag hint');
    return el;
  }

  /**
   * Draggable dot on an edge midpoint: on dragstart inserts a vertex at the geometric midpoint,
   * then drag updates that vertex until dragend (then markers are rebuilt).
   */
  private addEdgeMidpointMarker(
    edgeStartIdx: number,
    isClosingEdge: boolean,
    midLng: number,
    midLat: number
  ): void {
    if (!this.map) {
      return;
    }
    const el = this.createEdgeMidpointElement();
    const marker = new maplibregl.Marker({ element: el, draggable: true })
      .setLngLat([midLng, midLat])
      .addTo(this.map);

    marker.on('dragstart', () => {
      this.zone.run(() => {
        const pts = this.currentPolygonPoints;
        const n = pts.length;
        if (n < 2 || edgeStartIdx >= n) {
          return;
        }
        const endIdx = isClosingEdge ? 0 : edgeStartIdx + 1;
        if (!isClosingEdge && endIdx >= n) {
          return;
        }
        if (isClosingEdge && n < 3) {
          return;
        }
        const a = pts[edgeStartIdx];
        const b = pts[endIdx];
        this.pushPolygonUndoState();
        const mLng = (a[0] + b[0]) / 2;
        const mLat = (a[1] + b[1]) / 2;
        const insertIdx = isClosingEdge ? n : edgeStartIdx + 1;
        pts.splice(insertIdx, 0, [mLng, mLat]);
        this.updatePreviewGeometry();
        const vertexIdx = insertIdx;
        const onDrag = () => {
          const lngLat = marker.getLngLat();
          if (this.currentPolygonPoints[vertexIdx]) {
            this.currentPolygonPoints[vertexIdx] = [lngLat.lng, lngLat.lat];
            this.updatePreviewGeometry();
            this.repositionEdgeMidpointMarkers();
          }
        };
        marker.on('drag', onDrag);
        marker.once('dragend', () => {
          marker.off('drag', onDrag);
          this.zone.run(() => {
            this.syncEditPointMarkers();
          });
        });
      });
    });

    this.edgeMidpointMarkers.push(marker);
  }

  /** Keeps edge midpoint dots aligned with vertices while dragging a vertex (no marker rebuild). */
  private repositionEdgeMidpointMarkers(): void {
    const pts = this.currentPolygonPoints;
    const n = pts.length;
    if (n < 2 || this.edgeMidpointMarkers.length === 0) {
      return;
    }
    let expected = n - 1;
    if (n >= 3) {
      expected += 1;
    }
    if (this.edgeMidpointMarkers.length !== expected) {
      return;
    }
    let k = 0;
    for (let i = 0; i < n - 1; i++) {
      const midLng = (pts[i][0] + pts[i + 1][0]) / 2;
      const midLat = (pts[i][1] + pts[i + 1][1]) / 2;
      this.edgeMidpointMarkers[k]?.setLngLat([midLng, midLat]);
      k++;
    }
    if (n >= 3) {
      const midLng = (pts[n - 1][0] + pts[0][0]) / 2;
      const midLat = (pts[n - 1][1] + pts[0][1]) / 2;
      this.edgeMidpointMarkers[k]?.setLngLat([midLng, midLat]);
    }
  }

  private isPreviewFillHit(e: maplibregl.MapMouseEvent): boolean {
    if (!this.map) {
      return false;
    }
    const layerId = this.CUSTOM_AREA_PREVIEW_SOURCE_ID + '-fill';
    if (!this.map.getLayer(layerId)) {
      return false;
    }
    try {
      const feats = this.map.queryRenderedFeatures(e.point, { layers: [layerId] });
      return feats.length > 0;
    } catch {
      return false;
    }
  }

  private screenEventToLngLat(clientX: number, clientY: number): maplibregl.LngLat | null {
    if (!this.map) {
      return null;
    }
    const canvas = this.map.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return this.map.unproject([x, y]);
  }

  private beginPolygonBodyDrag(startLngLat: maplibregl.LngLat): void {
    if (!this.map || this.polygonBodyDragActive) {
      return;
    }
    this.polygonBodyDragSnapshot = this.clonePolygonPoints(this.currentPolygonPoints);
    this.polygonBodyDragStartLngLat = { lng: startLngLat.lng, lat: startLngLat.lat };
    this.polygonBodyDragActive = true;
    this.polygonBodyDragMoved = false;
    this.polygonBodyDragUndoPushed = false;
    this.map.dragPan.disable();
    this.map.getCanvas().style.cursor = 'grabbing';
    this.polygonBodyDragMoveListener = (ev: MouseEvent) => this.onPolygonBodyDragMove(ev);
    this.polygonBodyDragUpListener = (ev: MouseEvent) => this.onPolygonBodyDragUp(ev);
    document.addEventListener('mousemove', this.polygonBodyDragMoveListener, { capture: true });
    document.addEventListener('mouseup', this.polygonBodyDragUpListener, { capture: true });
  }

  private onPolygonBodyDragMove(ev: MouseEvent): void {
    if (
      !this.polygonBodyDragActive ||
      !this.map ||
      !this.polygonBodyDragStartLngLat ||
      !this.polygonBodyDragSnapshot
    ) {
      return;
    }
    const cur = this.screenEventToLngLat(ev.clientX, ev.clientY);
    if (!cur) {
      return;
    }
    const dLng = cur.lng - this.polygonBodyDragStartLngLat.lng;
    const dLat = cur.lat - this.polygonBodyDragStartLngLat.lat;
    if (Math.abs(dLng) > 1e-12 || Math.abs(dLat) > 1e-12) {
      if (!this.polygonBodyDragUndoPushed) {
        this.pushPolygonUndoState();
        this.polygonBodyDragUndoPushed = true;
      }
      this.polygonBodyDragMoved = true;
    }
    const snap = this.polygonBodyDragSnapshot;
    for (let i = 0; i < snap.length; i++) {
      const p = snap[i];
      this.currentPolygonPoints[i] = [p[0] + dLng, p[1] + dLat];
    }
    this.updatePreviewGeometry();
    this.repositionEdgeMidpointMarkers();
    for (let i = 0; i < this.editPointMarkers.length; i++) {
      const pt = this.currentPolygonPoints[i];
      if (pt) {
        this.editPointMarkers[i]?.setLngLat([pt[0], pt[1]]);
      }
    }
  }

  private onPolygonBodyDragUp(_ev: MouseEvent): void {
    if (!this.polygonBodyDragActive) {
      return;
    }
    const moved = this.polygonBodyDragMoved;
    this.endPolygonBodyDragIfActive();
    this.zone.run(() => {
      this.syncEditPointMarkers();
    });
    if (moved) {
      this.suppressNextMapClick = true;
    }
  }

  /** Release listeners and re-enable map pan (e.g. on destroy or cancel). */
  private endPolygonBodyDragIfActive(): void {
    if (!this.polygonBodyDragActive) {
      return;
    }
    this.polygonBodyDragActive = false;
    this.polygonBodyDragStartLngLat = null;
    this.polygonBodyDragSnapshot = null;
    this.polygonBodyDragMoved = false;
    this.polygonBodyDragUndoPushed = false;
    if (this.map) {
      this.map.dragPan.enable();
      const canvas = this.map.getCanvas();
      if (canvas) {
        canvas.style.cursor = this.editMode ? 'crosshair' : '';
      }
    }
    if (this.polygonBodyDragMoveListener) {
      document.removeEventListener('mousemove', this.polygonBodyDragMoveListener, { capture: true });
      this.polygonBodyDragMoveListener = null;
    }
    if (this.polygonBodyDragUpListener) {
      document.removeEventListener('mouseup', this.polygonBodyDragUpListener, { capture: true });
      this.polygonBodyDragUpListener = null;
    }
  }

  private syncEditPointMarkers(): void {
    if (!this.map || !this.editMode) return;
    this.clearEditPointMarkers();
    const pts = this.currentPolygonPoints;
    const n = pts.length;

    if (n >= 2) {
      for (let i = 0; i < n - 1; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        const midLng = (a[0] + b[0]) / 2;
        const midLat = (a[1] + b[1]) / 2;
        this.addEdgeMidpointMarker(i, false, midLng, midLat);
      }
      if (n >= 3) {
        const a = pts[n - 1];
        const b = pts[0];
        const midLng = (a[0] + b[0]) / 2;
        const midLat = (a[1] + b[1]) / 2;
        this.addEdgeMidpointMarker(n - 1, true, midLng, midLat);
      }
    }

    for (let i = 0; i < pts.length; i++) {
      const idx = i;
      const el = this.createEditPointElement(idx);
      const marker = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat([pts[idx][0], pts[idx][1]])
        .addTo(this.map);

      marker.on('dragstart', () => {
        this.pushPolygonUndoState();
      });

      marker.on('drag', () => {
        const lngLat = marker.getLngLat();
        if (this.currentPolygonPoints[idx]) {
          this.currentPolygonPoints[idx] = [lngLat.lng, lngLat.lat];
          this.updatePreviewGeometry();
          this.repositionEdgeMidpointMarkers();
        }
      });

      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        if (this.currentPolygonPoints[idx]) {
          this.currentPolygonPoints[idx] = [lngLat.lng, lngLat.lat];
          this.updatePreviewGeometry();
          this.repositionEdgeMidpointMarkers();
        }
      });

      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.zone.run(() => {
          if (this.currentPolygonPoints.length > 0 && idx >= 0 && idx < this.currentPolygonPoints.length) {
            this.pushPolygonUndoState();
            this.currentPolygonPoints.splice(idx, 1);
            this.updatePreviewGeometry();
            this.syncEditPointMarkers();
          }
        });
      });

      this.editPointMarkers.push(marker);
    }
  }

  setPin(event: any) {
    if (this.isClickable) {
      const lat = event.lat || (Array.isArray(event) ? event[1] : event.lat);
      const lng = event.lng || (Array.isArray(event) ? event[0] : event.lng);
      const latlng = { lat, lng };
      this.marker = {
        position: latlng,
        title: this.translate.instant('Pin'),
        info: 'Marker info',
        hideInfo: true
      };

      if (this.defaultMarkerIcon) {
        this.marker.icon = this.defaultMarkerIcon;
      }
      this.showMarkersIfReady();
    }
  }

  /**
   * Clears the standalone location pin marker rendered by setPin/addPin.
   */
  clearLocationMarker(): void {
    this.marker = {};
    this.showMarkersIfReady();
  }

  private easeToPinLocation(): void {
    if (this.map && this.currentGeoLocation && this.goToLocationMarker) {
      setTimeout(() => {
        const center = this.normalizeCenter(this.currentGeoLocation);
        this.map.easeTo({
          center,
          duration: 500
        });
      }, 1000);

    }
  }
  addPin(event: any) {
    const lat = event.lat || (Array.isArray(event) ? event[1] : event.lat);
    const lng = event.lng || (Array.isArray(event) ? event[0] : event.lng);
    const latlng = { lat, lng };
    this.marker = {
      position: latlng,
      title: this.translate.instant('Pin'),
      info: 'Marker info',
      hideInfo: true
    };

    if (this.defaultMarkerIcon) {
      this.marker.icon = this.defaultMarkerIcon;
    }
    this.showMarkersIfReady();
  }

  geocodeLatLng(geocoder: any, lat: any, lng: any) {
    // For offline maps, geocoding is not available
    // This is a placeholder to match the interface
    const latlng = { lat: lat, lng: lng };
    this.geoLocation.emit(latlng);
  }

  getHideInfo(mark: any): boolean {
    let status = false;
    if (mark.hideInfo) {
      return true;
    }
    if (mark.markerType === 'document') {
      let description = mark.description ? mark.description : mark.notes ? mark.notes : '';
      if (description === '') {
        return true;
      }
    } else if (mark.markerType === 'note') {
      let description = mark.description ? mark.description : '';
      if (description == '' && !(mark.person_of_interest || mark.car_of_interest)) {
        return true;
      }
    } else if (mark.markerType === 'location' || mark.markerType === 'building') {
      return true;
    } else if (mark.markerType === 'squad') {
      return true;
    }
    return status;
  }

  prepareMarker(mark: any): any {
    let icon = mark.icon ? mark.icon : '';
    if (icon.hasOwnProperty('url')) {
      icon = icon.url;
    }
    let marker = {
      position: {
        lat: mark.lat,
        lng: mark.lng,
      },
      geolocation: mark.geolocation,
      email: mark.email,
      lat: mark.lat,
      lng: mark.lng,
      delete: mark.delete,
      dataType: mark.type ? mark.type : mark.dataType,
      investigator: mark.investigator ? mark.investigator : undefined,
      investigators: mark.investigators ? mark.investigators : undefined,
      investigatorgroup: mark.investigatorgroup ? mark.investigatorgroup : undefined,
      cameras: mark.cameras ? mark.cameras : undefined,
      markerType: mark.markerType,
      _id: mark._id,
      /** Needed for tune-panel icon scale (car vs person vs generic note) after full `addMarkers` refresh. */
      car_of_interest: !!mark.car_of_interest,
      person_of_interest: !!mark.person_of_interest,
      title: mark.name,
      hideInfo: this.getHideInfo(mark),
      showLabel: mark.showLabel,
      label: this.prepareLabel(mark),
      labelColor: mark.labelColor ? mark.labelColor : undefined,
      draggable: mark.draggable || false,
      isGroup: mark.isGroup || false,
      inLiveStreaming: mark.inLiveStreaming || false,
      alternative_name: mark.alternative_name || "",
      name: mark['alternative_name'] ? mark['alternative_name'] : mark['name'],
      camera_id: mark.camera_id,
      case_id: mark.case_id,
      case: mark.case,
      status: mark.status,
      pictureLink: mark.pictureLink ? mark.pictureLink : mark.picture ? mark.picture : 'assets/images/error-default-image.png',
      picture: mark.picture ? mark.picture : mark.pictureLink ? mark.pictureLink : 'assets/images/error-default-image.png',
      picture_icon: mark.picture_icon ? mark.picture_icon : mark.picture ? mark.picture : mark.pictureLink ? mark.pictureLink : 'assets/images/error-default-image.png',
      icon: icon,
      description: mark.description ? mark.description : mark.notes ? mark.notes : '',
      rank: mark.rank ? mark.rank : '',
      years_in_service: mark.years_in_service ? mark.years_in_service : '',
      info: 'Marker info ' + (this.markersOriginal.length + 1),
      createdAt: mark.createdAt,
      note: mark,
      mapCameraFilterHidden: mark.mapCameraFilterHidden === true,
      /**
       * Investigators / groups: hide static pin until a live position arrives (enableTrackMarkers).
       * Cameras: always show the fixed geolocation; live NATS/API updates still move the same marker.
       * If cameras were included here, fixed (non-live) installs stayed hideIt=true and never rendered.
       */
      excludeFromMap: mark.excludeFromMap || false,
      hideIt:
        (this.enableTrackMarkers &&
          (mark.markerType === 'investigator' || mark.markerType === 'investigator-group')
          ? true
          : false) || mark.excludeFromMap,
      isInCall: mark.isInCall || false,
      isLiveStreaming: mark.isLiveStreaming || false,
      shapeLength: mark.shape_length || undefined,
      shapeRotationStart: mark.rotation_start_at != undefined ? mark.rotation_start_at * 30 : undefined,
      shapeRotationEnd: mark.rotation_end_at != undefined ? mark.rotation_end_at * 30 : undefined,
      shapeColor: mark.camera_field_view_color || 'red',
      cameraType: mark.camera_sub_type || mark.cameraType || mark.camera_type || 'default',
      camera_type: mark.camera_type,
      camera_sub_type: mark.camera_sub_type || 'default',
    };

    const hasPosition =
      marker.lat !== undefined &&
      marker.lat !== null &&
      marker.lat !== '' &&
      marker.lng !== undefined &&
      marker.lng !== null &&
      marker.lng !== '';

    // Hide markers with no usable geolocation so they are not rendered.
    if (!hasPosition) {
      marker.hideIt = true;
    }

    // const cameraTypeRaw = String(mark.camera_type ?? '').trim();
    // if (marker.markerType === 'camera' && hasPosition && cameraTypeRaw === 'police-cams') {
    //   /** Do not draw a pin at stored geolocation; live updates still set hideIt false when tracking applies. */
    //   marker.hideIt = true;
    // }

    const isSquadOrTeammemberMarker = mark.markerType === 'squad' || mark.markerType === 'teammember';
    const canDragMarker = isSquadOrTeammemberMarker ? this.canManageSquadMarkers() : true;
    const shouldBeDraggable = mark.draggable === true && canDragMarker;

    const defaultIconUrl = getDefaultIconForMarkerType(mark.markerType);

    if (mark.markerType === 'camera') {
      const cameraIcon = getCameraMarkerIconUrl(mark.camera_sub_type);
      mark['icon'] = cameraIcon;
      marker['icon'] = cameraIcon;
    }
    let iconUrlToUse = mark.icon || defaultIconUrl;
    let iconObject = {
      url: iconUrlToUse
    };

    if (mark.icon) {
      const img = new Image();
      img.onload = () => {
        // Icon exists and loaded successfully
      };
      img.onerror = () => {
        const defaultIconObject = { url: defaultIconUrl };
        const updateMarkerIcon = (markerToUpdate: any) => {
          if (markerToUpdate) {
            markerToUpdate['icon'] = defaultIconObject;
            if (markerToUpdate['options']) {
              markerToUpdate['options'].icon = defaultIconObject;
            } else {
              markerToUpdate['options'] = {
                icon: defaultIconObject,
                draggable: shouldBeDraggable
              };
            }
          }
        };

        const markerInOriginal = this.markersOriginal.find(m => m._id === marker._id);
        updateMarkerIcon(markerInOriginal);

        const markerInAdjusted = this.markersOriginalAdjusted.find(m => m._id === marker._id);
        updateMarkerIcon(markerInAdjusted);

        const markerInDisplay = this.markers.find(m => m._id === marker._id);
        updateMarkerIcon(markerInDisplay);
      };
      img.src = mark.icon;
    }
    marker['iconObject'] = iconObject;
    marker['options'] = {
      icon: iconObject,
      draggable: shouldBeDraggable
    };

    return marker;
  }

  prepareLabel(mark: any): any {
    let label: any = {};
    if (typeof mark.label === "object") {
      label = {
        ...mark.label,
        text: this.transform(mark.label?.text ?? '', ['50', '...']),
        color: (mark.label as any)?.labelColor ?? 'black',
        fontSize: '12px',
        fontWeight: (mark.label as any)?.fontWeight ?? 'bold',
        className: ((mark.label as any)?.className ? (mark.label as any)?.className + ' ' : '') + 'titleMarkClass'
      };
    } else {
      label = mark.label ? {
        text: this.transform(mark.label, ['50', '...']),
        color: mark.labelColor ? mark.labelColor : 'black',
        fontSize: '12px',
        fontWeight: 'bold',
        className: 'titleMarkClass'
      } : mark.name ? {
        text: this.transform(mark.name, ['50', '...']),
        color: mark.labelColor ? mark.labelColor : 'black',
        fontSize: '12px',
        fontWeight: 'bold',
        className: 'titleMarkClass'
      } : mark.case_id ? {
        text: this.transform(mark.case_id, ['50', '...']),
        color: mark.labelColor ? mark.labelColor : 'black',
        fontSize: '12px',
        fontWeight: 'bold',
        className: 'titleMarkClass'
      } : undefined;
    }
    return label;
  }

  transform(value: string, args: string[]): string {
    let limit = args.length > 0 ? parseInt(args[0], 10) : 10;
    let trail = args.length > 1 ? args[1] : '...';
    return value.length > limit ? value.substring(0, limit) + trail : value;
  }

  moveMarkerRandomly(mark: any): any {
    const dx = Math.random() * 0.0001;
    const dy = Math.random() * 0.0001;
    const newLat = mark.position.lat + dx;
    const newLng = mark.position.lng + dy;
    mark.position.lat = newLat;
    mark.position.lng = newLng;
    mark.lat = newLat;
    mark.lng = newLng;
    return mark;
  }

  markersCoincide(marker1: any, marker2: any): boolean {
    // Simple distance check for MapLibre
    const lat1 = marker1.position.lat;
    const lng1 = marker1.position.lng;
    const lat2 = marker2.position.lat;
    const lng2 = marker2.position.lng;
    const distance = this.calculateDistance(lat1, lng1, lat2, lng2);
    return (lat1 === lat2 && lng1 === lng2) || distance < 4;
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private dedupeMarkers(list: any[]): any[] {
    const seen = new Set<string>();
    const result: any[] = [];
    for (const mark of list) {
      if (!mark) continue;
      const lat = mark.position?.lat ?? mark.lat;
      const lng = mark.position?.lng ?? mark.lng;
      const roundedLat = typeof lat === 'number' ? lat.toFixed(6) : lat;
      const roundedLng = typeof lng === 'number' ? lng.toFixed(6) : lng;
      const key = mark._id
        ? `id:${mark._id}`
        : `${mark.markerType || 'unknown'}:${roundedLat}:${roundedLng}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(mark);
    }
    return result;
  }

  addMarkers(markers: any[], options?: { calculateCenterZoom?: boolean }): void {
    this.markersOriginal = [];
    this.hasRenderedDataMarkers = false;
    this.initialMarkerRenderRetryCount = 0;
    if (this.initialMarkerRenderRetryTimer) {
      clearTimeout(this.initialMarkerRenderRetryTimer);
      this.initialMarkerRenderRetryTimer = null;
    }
    for (let mark of markers) {
      if (mark?.delete) {
        continue;
      }
      let marker = this.prepareMarker(mark);
      this.markersOriginal.push(marker);
    }
    this.markersOriginal = this.dedupeMarkers(this.markersOriginal);
    const calculateCenterZoom = options?.calculateCenterZoom ?? true;
    this.showMarkersIfReady([], calculateCenterZoom);
  }

  /**
   * Log markers currently added to the offline map.
   * If an ID is provided, only markers matching that ID are logged.
   */
  public logMarkersToConsole(id: string): any {
    const allMarkers = this.markers || [];
    const markersToLog = id
      ? allMarkers.filter((m) => m && (m._id === id || m.id === id) && !m.hideIt)
      : [];

    if (!markersToLog.length) {
      // console.log(`[OfflineMap] No markers found with id "${id}".`);
      return undefined;
    }
    const summary = markersToLog.map((m, index) => ({
      index,
      id: m._id,
      type: m.markerType,
      title: m.title,
      lat: m.lat,
      lng: m.lng,
      geolocation: m.geolocation,
      hidden: m.hideIt,
      delete: m.delete,
      caseId: m.case_id,
      status: m.status,
      createdAt: m.createdAt
    }));

    return summary[0];
  }

  updateMarkers(newMarkers: any[] = [], calculateCenterZoom: boolean = true, callback: (markers: any[]) => void = undefined): void {
    for (let mark of newMarkers) {
      let marker = this.prepareMarker(mark);
      this.updateOriginalMarker(marker);
    }
    this.markersOriginal = this.dedupeMarkers(this.markersOriginal);
    if (callback) {
      callback(this.markersOriginal);
    }
    this.showMarkersIfReady([], calculateCenterZoom);
  }

  setMarkerVisibilityByIds(ids: string[] = [], visible: boolean = true, callback: (markers: any[]) => void = undefined): void {
    const markerIds = new Set((ids || []).filter(Boolean));
    if (!markerIds.size) return;

    const setVisibility = (marker: any) => {
      if (!marker?._id || !markerIds.has(marker._id)) return marker;
      return { ...marker, hideIt: !visible, mapToolsHidden: !visible };
    };

    this.markersOriginal = this.markersOriginal.map(setVisibility);
    this.markersOriginalAdjusted = this.markersOriginalAdjusted.map(setVisibility);
    this.markers = this.markers.map(setVisibility);

    if (callback) {
      callback(this.markersOriginal);
    }
    this.updateMarkersVisual();
  }

  updateOriginalMarker(marker: any): void {
    if (marker.delete) {
      this.markersOriginal = this.markersOriginal.filter(mark => (mark._id !== marker._id) || (mark._id === marker._id && !marker.delete));
    } else {
      let isFound = false;
      this.markersOriginal = this.markersOriginal.map((mark) => {
        if (mark._id === marker._id) {
          // Track manual updates for squad and teammember markers to prevent Firebase flicker
          if ((marker.markerType === 'squad' || marker.markerType === 'teammember') && marker.lat && marker.lng) {
            this.recentlyUpdatedMarkers.set(marker._id, {
              timestamp: Date.now(),
              position: { lat: marker.lat, lng: marker.lng }
            });
          }
          mark = marker;
          isFound = true;
        }
        return mark;
      });
      if (!isFound) {
        // Track manual updates for new markers too
        if ((marker.markerType === 'squad' || marker.markerType === 'teammember') && marker.lat && marker.lng) {
          this.recentlyUpdatedMarkers.set(marker._id, {
            timestamp: Date.now(),
            position: { lat: marker.lat, lng: marker.lng }
          });
        }
        this.markersOriginal.push(marker);
      }
    }
    this.markersOriginal = this.dedupeMarkers(this.markersOriginal);
  }

  async showMarkers(typeToHide: string[] = [], calculateCenterZoom: boolean = true): Promise<void> {
    // Ensure we only render markers after the map has fully loaded once.
    // This prevents showMarkers from running during ngOnChanges before the style is ready.
    if (!this.map || !this.mapLoaded) {
      return;
    }
    this.markersOriginal = this.dedupeMarkers(this.markersOriginal);
    this.adjustPoints();
    // Ensure all previous tracking subscriptions are fully torn down before re-tracking
    await this.unTrackMarkersAsync(this.trackingMarkers);
    this.markers = [];
    let markersOriginal = this.cloneMarkersForDisplay(this.markersOriginalAdjusted);
    if (typeToHide.length > 0) {
      markersOriginal = markersOriginal.filter((itm) => !typeToHide.includes(itm.markerType));
    }
    this.markers = markersOriginal;
    if (!this.markers?.length) {
      this.hasRenderedDataMarkers = false;
      this.initialMarkerRenderRetryCount = 0;
      if (this.initialMarkerRenderRetryTimer) {
        clearTimeout(this.initialMarkerRenderRetryTimer);
        this.initialMarkerRenderRetryTimer = null;
      }
    }

    let markersToCalculate = this.cloneMarkersForDisplay(markersOriginal);
    markersToCalculate = markersToCalculate.filter((mark) => mark.lat && mark.lat !== '' && mark.lng && mark.lng !== '');
    /** While editing/creating a footprint draft, keep camera focus on draft-only fit logic. */
    const hasFootprintsForFit =
      this.forBuildingFlow &&
      !this.buildingFootprintDraftActive &&
      this.customMapAreasData?.length > 0;
    if (
      calculateCenterZoom &&
      (markersToCalculate.length > 0 || hasFootprintsForFit)
    ) {
      // createan attribute filtering the markers hideIt == true
      let markersToCalculateFiltered = markersToCalculate.filter((mark) => !mark.hideIt);
      this.calculateZoomLevel(markersToCalculateFiltered);
    }
    // Clean up old entries from recentlyUpdatedMarkers
    this.cleanupRecentlyUpdatedMarkers();
    // Start tracking again AFTER unTrack has fully completed
    this.trackMarkers(this.trackingMarkers);
    this.clearAllMarkerRefs();
    this.updateMarkersVisual();
  }

  /** Full data/filter refresh: drop every HTML marker before incremental sync. */
  private clearAllMarkerRefs(): void {
    this.markerRefs.forEach((m) => {
      try {
        m.remove();
      } catch (err) {
        console.warn('Error removing marker:', err);
      }
    });
    this.markerRefs.clear();
    this.markerRenderedFingerprints.clear();
  }

  private cleanupRecentlyUpdatedMarkers(): void {
    const now = Date.now();
    for (const [markerId, updateInfo] of this.recentlyUpdatedMarkers.entries()) {
      if (now - updateInfo.timestamp > this.MANUAL_UPDATE_TIMEOUT) {
        this.recentlyUpdatedMarkers.delete(markerId);
      }
    }
  }

  /** Shallow copy per marker for showMarkers; deep JSON clone is costly on large dashboards. */
  private cloneMarkersForDisplay(markers: any[]): any[] {
    if (!markers?.length) {
      return [];
    }
    if (markers.length > this.adjustPointsSkipThreshold) {
      return markers.map((m) => ({
        ...m,
        position: m.position ? { ...m.position } : m.position,
      }));
    }
    return JSON.parse(JSON.stringify(markers));
  }

  adjustPoints(): void {
    this.markersOriginal = this.dedupeMarkers(this.markersOriginal);
    if (this.markersOriginal.length > this.adjustPointsSkipThreshold) {
      this.markersOriginalAdjusted = this.markersOriginal.slice();
      return;
    }
    this.markersOriginalAdjusted = [];
    const minDistance = 4;
    for (let i = 0; i < this.markersOriginal.length; i++) {
      if (this.markersOriginal[i].lat && this.markersOriginal[i].lat !== '' && this.markersOriginal[i].lng && this.markersOriginal[i].lng !== '') {
        let adjusted = false;
        do {
          adjusted = false;
          for (let j = 0; j < this.markersOriginalAdjusted.length; j++) {
            const distance = this.calculateDistance(
              this.markersOriginal[i].position.lat,
              this.markersOriginal[i].position.lng,
              this.markersOriginalAdjusted[j].position.lat,
              this.markersOriginalAdjusted[j].position.lng
            );
            if (distance < minDistance) {
              adjusted = true;
              this.markersOriginal[i] = this.moveMarkerRandomly(this.markersOriginal[i]);
              break;
            }
          }
        } while (adjusted);
      }
      this.markersOriginalAdjusted.push(this.markersOriginal[i]);
    }
  }

  trackMarkers(markerTypes: string[] = []): void {
    // Fetch initial live locations
    if (this.enableTrackMarkers) {
      if (markerTypes.length > 0) {
        let markersToAdd: any[] = this.markers.filter(mark => markerTypes.includes(mark.markerType)).map(mark => mark._id);
        if (markersToAdd.length == 0)
          return;
        this.natsMessaging.getUserLive(markersToAdd, (liveLocations, error) => {
          if (liveLocations && liveLocations.length > 0) {
            let updated = false;
            // let addedNewMarkers = false;
            this.markers.forEach(mark => {
              if (markerTypes.includes(mark.markerType)) {
                const itemId = mark._id ? mark._id.toString() : '';
                const live = liveLocations.find(l => {
                  const lId = l._id ? l._id.toString() : '';
                  return !(!l || l.isActive !== true || !l.geolocation) && (lId === itemId);
                });

                if (live && live.geolocation) {
                  // Ensure geolocation is an object (it might be a string from some APIs)
                  let pos = live.geolocation;
                  if (typeof pos === 'string') {
                    try { pos = JSON.parse(pos); } catch (e) { }
                  }

                  if (pos && (pos.lat || pos.lat === 0) && (pos.lng || pos.lng === 0)) {
                    this.zone.run(() => {
                      mark.position = pos;
                      mark.lat = pos.lat;
                      mark.lng = pos.lng;
                      mark['hideIt'] = false;
                      if (mark.markerType === 'camera') {
                        mark['cameraType'] = live.camera_type || 'default';
                        mark['icon'] = getCameraMarkerIconUrl(mark['cameraType']);
                      }
                      mark.geolocation = JSON.stringify(pos);
                      // this.updateCaseServiceGeolocation(mark.markerType, mark._id, pos);
                      this.updateMarkerPosition(mark);
                    });
                    updated = true;
                  }
                }
              }
            });
            // const existingMarkerIds = new Set(
            //   (this.markers || [])
            //     .map(m => (m && m._id !== undefined && m._id !== null) ? m._id.toString() : '')
            //     .filter(v => v !== '')
            // );

            // liveLocations.forEach((live) => {
            //   if (!live || live.isActive !== true || !live.geolocation) return;

            //   const liveId = live.userId || live.groupId || live._id;
            //   if (!liveId) return;

            //   const liveIdStr = liveId.toString();
            //   if (existingMarkerIds.has(liveIdStr)) return;

            //   let pos = live.geolocation;
            //   if (typeof pos === 'string') {
            //     try { pos = JSON.parse(pos); } catch (e) { return; }
            //   }

            //   if (!pos || !(pos.lat || pos.lat === 0) || !(pos.lng || pos.lng === 0)) return;

            //   const markerType = live.userId ? 'investigator' : (live.groupId ? 'investigator-group' : 'investigator');
            //   const newMarker = this.prepareMarker({
            //     _id: liveId,
            //     markerType,
            //     lat: pos.lat,
            //     lng: pos.lng,
            //     geolocation: pos,
            //     name: live.name || liveIdStr,
            //     showLabel: true
            //   });

            //   this.markers.push(newMarker);
            //   existingMarkerIds.add(liveIdStr);
            //   addedNewMarkers = true;
            // });

            // if (updated) {
            //   console.log(`[OfflineMap] Updated markers with live locations`);
            // }
            // if (addedNewMarkers) {
            this.scheduleMarkersVisualRefresh(0);
            // }
          }
        });
        this.markers.forEach(mark => {
          if (markerTypes.includes(mark.markerType)) {
            let positionPath = undefined;
            if (mark.markerType === 'investigator-group') {
              positionPath = 'users-group';
            } else if (mark.markerType === 'investigator') {
              positionPath = 'users';
            } else if (mark.markerType === 'camera') {
              positionPath = 'cameras';
            }

            if (mark._id && positionPath) {
              const markerId = mark._id.toString();
              const listenerKey = `${positionPath}/${markerId}`;
              const streamingListenerKey = `streaming/${markerId}`;

              // Subscribe to live position once per marker
              if (!this.natsListeners.has(listenerKey)) {
                // Build NATS subject for this marker.
                // NOTE: Adjust this format if your backend uses a different subject for live positions.
                const subject = `live.${positionPath}.${markerId}`;
                this.natsListeners.set(listenerKey, subject);
                this.natsMessaging.subscribe('', subject, (payload: any) => {
                  // console.log('[OfflineMap] Received NATS message');
                  // console.log(`[OfflineMap] Received NATS message for ${subject}`, payload);
                  const data = payload?.data || payload;
                  const mainData = data;

                  // Direct update for debugging
                  // const currentMarkId = mark._id ? mark._id.toString() : '';
                  // if (currentMarkId === markerId && mainData && mainData.position && mainData.active) {
                  //   mark.position = mainData.position;
                  //   mark.lat = mainData.position.lat;
                  //   mark.lng = mainData.position.lng;
                  // }

                  if (mainData && mainData.position && mainData.active) {
                    const positionData = mainData.position;

                    if (positionData && positionData.lat && positionData.lat !== '' && positionData.lng && positionData.lng !== '') {
                      const recentUpdate = this.recentlyUpdatedMarkers.get(mark._id);
                      const shouldIgnoreUpdate = recentUpdate &&
                        (Date.now() - recentUpdate.timestamp < this.MANUAL_UPDATE_TIMEOUT) &&
                        mark.position &&
                        mark.position.lat &&
                        mark.position.lng;

                      if (shouldIgnoreUpdate) {
                        const distance = this.calculateDistance(
                          mark.position.lat,
                          mark.position.lng,
                          positionData.lat,
                          positionData.lng
                        );

                        if (distance <= 10) {
                          // Ignore small echo updates
                          return;
                        }
                      }

                      this.zone.run(() => {
                        mark.position = positionData;
                        mark.geolocation = JSON.stringify(positionData);
                        mark.lat = positionData.lat;
                        mark.lng = positionData.lng;
                        if (mark.markerType === 'camera') {
                          mark['cameraType'] = mainData.camera_type || 'default';
                          mark['icon'] = getCameraMarkerIconUrl(mark['cameraType']);
                        }
                        this.updateMarkerPosition(mark);
                        // this.updateMarkersVisual(); // Optimization: updateMarkerPosition already updates the specific marker
                      });
                    }
                    if (mark.markerType === 'squad') {
                      mark.name = mainData.name || "";
                      mark.label = mainData.name || "";
                      mark.label = this.prepareLabel(mark);
                      mark.description = mainData.description || "";
                    }
                    mark.hideIt = false;
                  } else {
                    mark.hideIt = true;
                    this.scheduleMarkersVisualRefresh();
                  }
                  this.updateCaseServiceGeolocation(mark.markerType, mark._id, mainData);
                });
              }

              // Subscribe to streaming/in-call state once per marker (tracked for cleanup)
              if (!this.natsListeners.has(streamingListenerKey)) {
                const streamingSubject = `streaming.${markerId}`;
                this.natsListeners.set(streamingListenerKey, streamingSubject);
                this.natsMessaging.subscribe('', streamingSubject, (payload: any) => {
                  // console.log('[OfflineMap] Received NATS streaming message', payload);
                  const userId = payload?.data?.userId;
                  if (userId) {
                    this.applyMarkerInCallState(userId, payload.data.isCall);
                  }
                  this.scheduleMarkersVisualRefresh();
                });
              }
            }
          }
        });
      }
      // Subscribe once — trackMarkers() is called on every marker refresh
      if (!this.cameraStreamChangedSubscribed) {
        this.cameraStreamChangedSubscribed = true;
        this.subscriptions.push(this.caseService.cameraStreamChanged.subscribe((data) => {
          if (!data?.investigatorId) {
            return;
          }
          this.applyMarkerStreamingState(data.investigatorId, data.visible);
        }));
      }
    }
  }

  /**
   * Sync live position and active flag into the matching caseService collection.
   * When mainData.active is false, clears geolocation and camera_sub_type.
   */
  private updateCaseServiceGeolocation(markerType: string, markerId: any, mainData: any): void {
    if (!markerType || markerId == null || !mainData) {
      return;
    }

    let collection: any[] | undefined;
    switch (markerType) {
      case 'camera':
        collection = this.caseService.cameras;
        break;
      case 'investigator':
        collection = this.caseService.investigators;
        break;
      case 'investigator-group':
        collection = this.caseService.investigatorGroups;
        break;
      case 'squad':
        collection = this.caseService.squads;
        break;
      case 'teammember':
        collection = this.caseService.teammembers;
        break;
      default:
        return;
    }

    if (!Array.isArray(collection) || collection.length === 0) {
      return;
    }

    const idStr = markerId.toString();
    const isActive = !!mainData.active;
    const positionData = mainData.position;

    for (const item of collection) {
      if (item?._id != null && item._id.toString() === idStr) {
        item.active = isActive;
        if (!isActive) {
          item.geolocation = '';
          item.lat = undefined;
          item.lng = undefined;
          if (markerType === 'camera') {
            item['camera_sub_type'] = '';
          }
        } else if (positionData) {
          item.geolocation = JSON.stringify(positionData);
          if (positionData.lat != null) {
            item.lat = positionData.lat;
          }
          if (positionData.lng != null) {
            item.lng = positionData.lng;
          }
          if (markerType === 'camera') {
            item['camera_sub_type'] = mainData.camera_type || '';
          }
        }
        break;
      }
    }
  }

  /** 
   * Synchronous wrapper kept for existing call sites (e.g. ngOnDestroy).
   * For strict ordering (untrack before track), prefer unTrackMarkersAsync + await.
   */
  unTrackMarkers(markerTypes: string[] = []): void {
    void this.unTrackMarkersAsync(markerTypes);
  }

  /**
   * Async variant that fully waits for NATS unsubscribe/drain to complete.
   */
  private async unTrackMarkersAsync(markerTypes: string[] = []): Promise<void> {
    if (!this.enableTrackMarkers || markerTypes.length === 0) {
      return;
    }

    const unsubscribePromises: Promise<void>[] = [];

    this.markers.forEach(mark => {
      if (markerTypes.includes(mark.markerType)) {
        let positionPath: string | undefined;
        if (mark.markerType === 'investigator-group') {
          positionPath = 'users-group';
        } else if (mark.markerType === 'investigator') {
          positionPath = 'users';
        } else if (mark.markerType === 'camera') {
          positionPath = 'cameras';
        }
        if (mark._id && positionPath) {
          const markerId = mark._id.toString();
          const listenerKey = `${positionPath}/${markerId}`;
          const streamingListenerKey = `streaming/${markerId}`;
          if (this.natsListeners.has(listenerKey)) {
            const subject = this.natsListeners.get(listenerKey) as string;
            // Unsubscribe using the same parameters pattern used in subscribe
            unsubscribePromises.push(this.natsMessaging.unsubscribe('', subject));
            this.natsListeners.delete(listenerKey);
          }
          if (this.natsListeners.has(streamingListenerKey)) {
            const streamingSubject = this.natsListeners.get(streamingListenerKey) as string;
            unsubscribePromises.push(this.natsMessaging.unsubscribe('', streamingSubject));
            this.natsListeners.delete(streamingListenerKey);
          }
        }
      }
    });

    if (unsubscribePromises.length > 0) {
      try {
        await Promise.all(unsubscribePromises);
      } catch (e) {
        console.warn('Error while unsubscribing tracking markers', e);
      }
    }
  }

  hideMark(markId: any): void {
    this.markers.map((mark) => {
      if (mark.markerType === 'investigator' && mark._id == markId) {
        mark.hideIt = true;
      } else if (mark.markerType === 'camera' && mark._id == markId) {
        mark.hideIt = true;
      }
    });
    this.updateMarkersVisual();
  }

  hideMarkGroup(markId: any): void {
    this.markers.map((mark) => {
      if (mark.markerType === 'investigator-group' && mark['loggedInUser'] && mark['loggedInUser'] == markId) {
        mark.hideIt = true;
      }
    });
    this.updateMarkersVisual();
  }

  centerUpdatedMarkers(): void {
    let markersToCalculate = JSON.parse(JSON.stringify(this.markers));
    markersToCalculate = markersToCalculate.filter((mark) => mark.lat && mark.lat !== '' && mark.lng && mark.lng !== '');
    if (markersToCalculate.length > 0) {
      this.calculateZoomLevel(markersToCalculate);
    }
  }

  private updateMarkerPosition(mark: any): void {
    // Update the marker position on the map
    if (!mark || !mark.position) return;

    // Try to find the marker by _id first
    let markerId = mark._id ? `marker_${mark._id.toString()}` : null;

    // If not found by _id, search by markerType and index
    if (!markerId || !this.markerRefs.has(markerId)) {
      const markerIndex = this.markers.findIndex(m =>
        (m._id && mark._id && m._id.toString() === mark._id.toString()) ||
        (!m._id && !mark._id && m.markerType === mark.markerType)
      );
      if (markerIndex >= 0) {
        markerId = mark._id ? `marker_${mark._id.toString()}` : `marker_${mark.markerType}_${markerIndex}`;
      }
    }

    if (markerId && this.markerRefs.has(markerId)) {
      const marker = this.markerRefs.get(markerId);
      if (marker) {
        const position = this.normalizeCenter(mark.position);
        marker.setLngLat(position);
        return;
      }
    }

    // If marker doesn't exist in refs, refresh all markers
    this.updateMarkersVisual();
  }

  calculateCenter(markersToCalculate: { position: { lat: number, lng: number } }[]): void {
    if (!markersToCalculate?.length) return;

    const latSum = markersToCalculate.map(({ position: { lat } }) => lat).reduce((sum, lat) => sum + lat, 0);
    const lngSum = markersToCalculate.map(({ position: { lng } }) => lng).reduce((sum, lng) => sum + lng, 0);

    this.center = {
      lat: latSum / markersToCalculate.length,
      lng: lngSum / markersToCalculate.length
    };

    this.mapDragGeo.emit(this.center);
  }

  calculateZoomLevel(markersToCalculate: { position: { lat: number, lng: number } }[]): void {
    if (!this.map) return;

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    markersToCalculate.forEach(({ position }) => {
      if (position?.lat !== undefined && position?.lng !== undefined) {
        minLat = Math.min(minLat, position.lat);
        maxLat = Math.max(maxLat, position.lat);
        minLng = Math.min(minLng, position.lng);
        maxLng = Math.max(maxLng, position.lng);
      }
    });

    /** Site mapping: include full footprint rings so corners stay in view (not only marker/centroid). */
    if (this.forBuildingFlow && !this.buildingFootprintDraftActive && this.customMapAreasData?.length) {
      for (const area of this.customMapAreasData) {
        const ring = area.coordinates;
        if (!ring?.length) {
          continue;
        }
        for (const pt of ring) {
          if (pt.length >= 2 && typeof pt[0] === 'number' && typeof pt[1] === 'number') {
            const lng = pt[0];
            const lat = pt[1];
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          }
        }
      }
    }

    if (minLat === Infinity) return;

    const bounds = [[minLng, minLat], [maxLng, maxLat]] as [[number, number], [number, number]];
    this.map.fitBounds(bounds, { padding: 75, duration: 500, maxZoom: 15 });
    this.refreshViewportAfterCameraMove();
  }

  /**
   * Fit the map view to a list of geolocations (center and zoom so all points are visible).
   * @param geolocations Array of points with lat/lng or lat/lon
   * @param options Optional zoom (used as exact zoom for single point, or maxZoom when fitting bounds) and padding in pixels
   */
  fitToGeolocations(
    geolocations: Array<{ lat: number; lng?: number; lon?: number }>,
    options?: { zoom?: number; padding?: number }
  ): void {
    if (!this.map || !geolocations?.length) return;

    const points = geolocations
      .map((g) => {
        const lng = g.lng ?? g.lon;
        if (g.lat == null || lng == null) return null;
        return { lat: g.lat, lng };
      })
      .filter((p): p is { lat: number; lng: number } => p != null);

    if (!points.length) return;

    const padding = options?.padding ?? 75;
    const zoomOption = options?.zoom != null ? Math.min(options.zoom, this.MAX_ZOOM) : undefined;

    if (points.length === 1) {
      const [p] = points;
      const center: [number, number] = [p.lng, p.lat];
      this.center = { lat: p.lat, lng: p.lng };
      this.map.easeTo({
        center,
        zoom: zoomOption ?? Math.min(16, this.MAX_ZOOM),
        duration: 500
      });
      this.refreshViewportAfterCameraMove();
      return;
    }

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    points.forEach((p) => {
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lng);
      maxLng = Math.max(maxLng, p.lng);
    });
    const bounds: [[number, number], [number, number]] = [[minLng, minLat], [maxLng, maxLat]];
    this.map.fitBounds(bounds, { padding, maxZoom: zoomOption, duration: 500 });
    this.refreshViewportAfterCameraMove();
  }

  private getPreferredLanguage(): string {
    const storedLanguage = typeof localStorage !== 'undefined' ? localStorage.getItem('languageSelected') : null;
    if (storedLanguage) {
      return storedLanguage;
    }

    if (this.translate?.currentLang) {
      return this.translate.currentLang;
    }

    if (this.requestService?.lang) {
      return this.requestService.lang;
    }

    return 'en';
  }

  /** English labels use yards (yd); other languages use meters/km. */
  private usesYardsForMapDistance(): boolean {
    return this.getPreferredLanguage() === 'en';
  }

  private createMapScaleControl(): maplibregl.IControl {
    if (this.usesYardsForMapDistance()) {
      return new YardsScaleControl({ maxWidth: 80 });
    }
    return new maplibregl.ScaleControl({ maxWidth: 80, unit: 'metric' });
  }

  private addMapScaleControl(): void {
    if (!this.map || !this.options.scaleControl) {
      return;
    }
    this.removeMapScaleControl();
    this.mapScaleControl = this.createMapScaleControl();
    this.map.addControl(this.mapScaleControl, this.controlPosition);
  }

  private removeMapScaleControl(): void {
    if (this.map && this.mapScaleControl) {
      this.map.removeControl(this.mapScaleControl);
      this.mapScaleControl = null;
    }
  }

  private refreshMapScaleControl(): void {
    if (!this.map || !this.mapLoaded || !this.options.scaleControl) {
      return;
    }
    this.addMapScaleControl();
  }

  private async nominatimReverseLookup(lat: number, lng: number, language: string): Promise<string | null> {
    return this.geocodingService.reverseLookup(lat, lng, language);
  }

  private async nominatimSearch(text: string, language: string): Promise<{ lat: number, lng: number, displayName?: string } | null> {
    return this.geocodingService.search(text, language);
  }

  reverseGeocode(lat: number, lng: number): void {
    if (lat === undefined || lng === undefined || lat === null || lng === null) {
      console.warn('reverseGeocode called with invalid coordinates', { lat, lng });
      return;
    }

    const languages = new Set<string>(['en', this.getPreferredLanguage()]);

    languages.forEach((language) => {
      this.nominatimReverseLookup(lat, lng, language)
        .then((address) => {
          if (!address) {
            console.warn(`No address found for language ${language}`);
            return;
          }

          if (language === 'ja') {
            this.addressJPPinData.emit(address);
          } else {
            this.addressPinData.emit(address);
          }
        })
        .catch((error) => {
          console.error(`Reverse geocode failed for language ${language}`, error);
        });
    });
  }

  markerHover(event: any, mark: any, marker: any): void {
    if (this.isHoverable) {
      this.selectedMarker = mark;
      // For offline maps, info window is not implemented
      // This is a placeholder to match the interface
    }
  }

  markerHoverOut(event: any): void {
    if (this.isHoverable) {
      // For offline maps, info window is not implemented
      // This is a placeholder to match the interface
    }
  }

  markerClick(event: any, mark: any, marker: any): void {
    if (mark?._isCluster && this.map) {
      this.zoomMapOnClusterClick(mark, marker);
      return;
    }
    if ((this.forBuildingFlow || this.isCasePage) && mark?.markerType === 'building') {
      const buildingId = this.getBuildingIdFromMarker(mark);
      if (buildingId) {
        this.openFloorPlansByBuildingId(buildingId);
        return;
      }
    }
    if (this.isClickable) {
      this.markerSelect.emit(mark);
      this.selectedMarker = mark;
    }
  }

  /**
   * Zoom to the first level where this cluster splits (same logic as {@link zoomForFirstDecluster}).
   * Keeps the map centered on the cluster pin.
   */
  private zoomMapOnClusterClick(mark: any, marker: any): void {
    if (!this.map || !mark?._isCluster) {
      return;
    }
    const members = mark._clusterMembers;
    if (!Array.isArray(members) || members.length < 2) {
      return;
    }
    const targetZoom = this.zoomForFirstDecluster(members);
    let lng: number | undefined;
    let lat: number | undefined;
    try {
      if (marker && typeof marker.getLngLat === 'function') {
        const ll = marker.getLngLat();
        if (ll && Number.isFinite(ll.lng) && Number.isFinite(ll.lat)) {
          lng = ll.lng;
          lat = ll.lat;
        }
      }
    } catch {
      /* ignore */
    }
    if (lng === undefined || lat === undefined) {
      const c = mark.position;
      if (c) {
        lat = Number(c.lat);
        lng = Number(c.lng ?? c.lon);
      }
    }
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    try {
      this.map.easeTo({
        center: [lng, lat],
        zoom: targetZoom,
        duration: 480,
        essential: true,
      });
    } catch (err) {
      console.warn('Cluster zoom easeTo failed:', err);
    }
  }

  selectMarkerInfo(): void {
    if (this.isClickable) {
      this.markerInfoSelect.emit(this.selectedMarker);
    }
  }

  deleteMarker(marker: any): void {
    if (!this.canManageSquadMarkers()) {
      return;
    }
    if (marker && (marker.markerType === 'squad' || marker.markerType === 'teammember')) {
      this.markerDelete.emit(marker);
    }
  }

  onMarkerRightClick(event: any, mark: any, marker: any): void {
    if (event?.domEvent) {
      event.domEvent.preventDefault();
      event.domEvent.stopPropagation();
    }
    if (this.forBuildingFlow && mark && (mark.markerType === 'building')) {
      let latLng: { lat: number; lng: number } | undefined;
      if (mark.lat != null && mark.lng != null) {
        latLng = { lat: Number(mark.lat), lng: Number(mark.lng) };
      }
      if (marker && typeof marker.getLngLat === 'function') {
        try {
          const ll = marker.getLngLat();
          if (ll && Number.isFinite(ll.lat) && Number.isFinite(ll.lng)) {
            latLng = { lat: ll.lat, lng: ll.lng };
          }
        } catch {
          /* keep latLng from mark */
        }
      }
      this.markerRightClick.emit({
        marker: mark,
        event: event?.domEvent || event,
        latLng
      });
    }
  }

  onMarkerDragEnd(event: any, mark: any, marker: any): void {
    if (!this.canManageSquadMarkers()) {
      return;
    }
    if (mark && (mark.markerType === 'squad' || mark.markerType === 'teammember')) {
      const latLng = event.latLng || { lat: event.lat, lng: event.lng };
      this.markerDragEnd.emit({
        marker: mark,
        latLng: latLng
      });
    }
  }

  private canManageSquadMarkers(): boolean {
    //  return this.isAdmin || this.isSuperAdmin || this.isCommander;
    return true;
  }

  /** Snap scale to 0–100 (match map filter sliders: step 5). */
  private clampIconScalePercent(n: number): number {
    if (!Number.isFinite(n)) {
      return this.markerIconScaleMax;
    }
    const v = Math.round(Number(n));
    const step = 5;
    return Math.min(this.markerIconScaleMax, Math.max(0, Math.round(v / step) * step));
  }

  private clampOpacityPercent(n: number): number {
    if (!Number.isFinite(n)) {
      return 100;
    }
    const v = Math.round(Number(n));
    const step = 5;
    return Math.min(100, Math.max(0, Math.round(v / step) * step));
  }

  private cameraSectorFillOpacity(): number {
    return (this.clampOpacityPercent(Number(this.cameraSectorOpacityPercent)) / 100) * this.sectorOpacityMax;
  }

  private updateCameraSectorOpacity(): void {
    if (!this.map) {
      return;
    }

    const fillOpacity = this.cameraSectorFillOpacity();
    this.sectors.forEach((sector, id) => {
      if (String(sector.data?.markerType ?? '').toLowerCase() !== 'camera') {
        return;
      }

      const layerId = `sector-layer-${id}`;
      if (this.map.getLayer(layerId)) {
        this.map.setPaintProperty(
          layerId,
          'fill-opacity',
          sector.data?.mapCameraFilterHidden ? 0 : fillOpacity
        );
      }
    });
  }

  /**
   * Which icon-scale slider applies (dashboard: notes / POI / location / threat; case map: camera / inv / group).
   * Plain `note` markers with car/person POI flags use car/person sliders; other notes use note slider.
   * `case` markers (case-sign.png) use the same scale as notes.
   */
  private iconScaleSliderCategoryForMark(mark: any): string | null {
    if (!mark) {
      return null;
    }
    const t = String(mark.markerType ?? '').toLowerCase().trim();
    if (t === 'case') {
      return 'note';
    }
    if (t === 'camera') {
      return 'camera';
    }
    if (t === 'investigator') {
      return 'investigator';
    }
    if (t === 'investigator-group') {
      return 'investigator-group';
    }
    if (t === 'building') {
      return 'building';
    }
    if (t === 'location') {
      return 'location';
    }
    if (t === 'threat') {
      return 'threat';
    }
    if (t === 'squad' || t === 'teammember') {
      return 'customMarker';
    }
    if (t === 'note') {
      const note = mark.note;
      if (mark.car_of_interest || note?.car_of_interest) {
        return 'carOfInterest';
      }
      if (mark.person_of_interest || note?.person_of_interest) {
        return 'personOfInterest';
      }
      return 'note';
    }
    if (t === 'annotation') {
      return 'annotation';
    }
    return null;
  }

  private iconScalePercentForMark(mark: any): number {
    const cat = this.iconScaleSliderCategoryForMark(mark);
    return this.iconScalePercentForCategory(cat);
  }

  private iconScalePercentForCategory(cat: string | null): number {
    if (!cat) {
      return this.markerIconScaleMax;
    }
    switch (cat) {
      case 'camera':
        return this.clampIconScalePercent(Number(this.cameraIconScalePercent));
      case 'investigator':
        return this.clampIconScalePercent(Number(this.investigatorIconScalePercent));
      case 'investigator-group':
        return this.clampIconScalePercent(Number(this.investigatorGroupIconScalePercent));
      case 'note':
        return this.clampIconScalePercent(Number(this.noteIconScalePercent));
      case 'carOfInterest':
        return this.clampIconScalePercent(Number(this.carOfInterestIconScalePercent));
      case 'annotation':
        return this.clampIconScalePercent(Number(this.annotationIconScalePercent));
      case 'personOfInterest':
        return this.clampIconScalePercent(Number(this.personOfInterestIconScalePercent));
      case 'location':
        return this.clampIconScalePercent(Number(this.locationIconScalePercent));
      case 'threat':
        return this.clampIconScalePercent(Number(this.threatIconScalePercent));
      case 'customMarker':
        return this.clampIconScalePercent(Number(this.customMarkerIconScalePercent));
      case 'building':
        return this.clampIconScalePercent(Number(this.buildingIconScalePercent));
      default:
        return this.markerIconScaleMax;
    }
  }

  private getChangedIconSliderCategories(changes: SimpleChanges): Set<string> {
    const sliderToCategory: Record<string, string> = {
      cameraIconScalePercent: 'camera',
      investigatorIconScalePercent: 'investigator',
      investigatorGroupIconScalePercent: 'investigator-group',
      noteIconScalePercent: 'note',
      carOfInterestIconScalePercent: 'carOfInterest',
      annotationIconScalePercent: 'annotation',
      personOfInterestIconScalePercent: 'personOfInterest',
      locationIconScalePercent: 'location',
      threatIconScalePercent: 'threat',
      customMarkerIconScalePercent: 'customMarker',
      buildingIconScalePercent: 'building'
    };

    const changedCategories = new Set<string>();
    Object.entries(sliderToCategory).forEach(([sliderKey, category]) => {
      if (changes[sliderKey] && !changes[sliderKey].firstChange) {
        changedCategories.add(category);
      }
    });
    return changedCategories;
  }

  /** True when the icon filter slider is at 0 for this marker (pin fully hidden). */
  private isMarkerIconSliderHidden(mark: any): boolean {
    if (!this.iconScaleSliderCategoryForMark(mark)) {
      return false;
    }
    return this.iconScalePercentForMark(mark) <= 0;
  }

  /** Scale icon wrapper relative to intrinsic image size (no px width/height on img). */
  private applyMarkerIconSliderScale(mark: any, imgWrapper: HTMLElement): void {
    if (!this.iconScaleSliderCategoryForMark(mark)) {
      return;
    }
    const pct = this.iconScalePercentForMark(mark);
    if (pct <= 0) {
      return;
    }
    imgWrapper.style.transformOrigin = 'bottom center';
    if (pct >= this.markerIconScaleMax) {
      imgWrapper.style.transform = '';
      this.applyClusterBadgeScale(imgWrapper, pct);
    } else {
      imgWrapper.style.transform = `scale(${pct / 100})`;
      this.applyClusterBadgeScale(imgWrapper, pct);
    }
  }

  /**
   * Cluster count badge stays readable even when icon slider scales pin graphics.
   * Keep badge in icon wrapper (so it follows icon position) but counter-scale it.
   */
  private applyClusterBadgeScale(imgWrapper: HTMLElement, pct: number): void {
    const badges = imgWrapper.querySelectorAll('.offline-marker__cluster-badge');
    if (!badges?.length) {
      return;
    }
    const ratio = Math.max(0.01, pct / 100);
    const inverseScale = ratio >= 1 ? 1 : (1 / ratio);
    badges.forEach((el) => {
      const badge = el as HTMLElement;
      badge.style.transformOrigin = 'top right';
      badge.style.transform = inverseScale === 1 ? '' : `scale(${inverseScale})`;
    });
  }

  /**
   * Updates icon scale on existing MapLibre markers immediately (slider drag).
   * Returns true if a full marker rebuild is required (e.g. 0% to above 0% when DOM has no icon yet).
   */
  private applyIconScaleToRenderedMarkers(changedCategories?: Set<string>): boolean {
    let needsFullRefresh = false;

    const visit = (mark: any, idx: number | 'single'): void => {
      if (!mark?.markerType) {
        return;
      }
      const sliderCategory = this.iconScaleSliderCategoryForMark(mark);
      if (!sliderCategory) {
        return;
      }
      if (changedCategories?.size && !changedCategories.has(sliderCategory)) {
        return;
      }
      const pinHidden =
        mark.hideIt ||
        (mark.markerType === 'camera' && mark.mapCameraFilterHidden);
      if (pinHidden || !mark.position) {
        return;
      }

      const id =
        idx === 'single'
          ? 'single'
          : mark._id
            ? `marker_${mark._id}`
            : `marker_${mark.markerType}_${idx}`;

      const mapMarker = this.markerRefs.get(id);
      if (!mapMarker) {
        return;
      }

      const el = mapMarker.getElement();
      if (!el) {
        return;
      }

      const wrap = el.querySelector('.offline-marker__icon-scale') as HTMLElement | null;
      const pct = this.iconScalePercentForMark(mark);

      if (pct <= 0) {
        if (wrap) {
          el.style.display = 'none';
          el.style.pointerEvents = 'none';
          el.setAttribute('aria-hidden', 'true');
        } else {
          needsFullRefresh = true;
        }
        return;
      }

      if (!wrap) {
        needsFullRefresh = true;
        return;
      }

      el.style.display = '';
      el.style.removeProperty('pointer-events');
      el.removeAttribute('aria-hidden');
      wrap.style.transformOrigin = 'bottom center';
      wrap.style.transform = pct >= this.markerIconScaleMax ? '' : `scale(${pct / 100})`;
      this.applyClusterBadgeScale(wrap, pct);
    };

    if (this.marker && this.marker.position) {
      visit(this.marker, 'single');
    }
    if (this.markers?.length) {
      this.markers.forEach((mark, idx) => visit(mark, idx));
    }

    return needsFullRefresh;
  }

  /**
   * Slider updates in-place on currently rendered marker elements (works for clustered pins too).
   * Returns true if a full rebuild is needed (e.g. icon wrapper missing because marker was hidden at 0%).
   */
  private applyIconScaleToRenderedMarkerElements(changedCategories: Set<string>): boolean {
    let needsFullRefresh = false;
    this.markerRefs.forEach((mapMarker) => {
      const el = mapMarker?.getElement?.();
      if (!el) {
        return;
      }
      const category = el.getAttribute('data-icon-scale-category');
      if (!category || !changedCategories.has(category)) {
        return;
      }

      const wrap = el.querySelector('.offline-marker__icon-scale') as HTMLElement | null;
      const pct = this.iconScalePercentForCategory(category);

      if (pct <= 0) {
        if (wrap) {
          el.style.display = 'none';
          el.style.pointerEvents = 'none';
          el.setAttribute('aria-hidden', 'true');
        } else {
          needsFullRefresh = true;
        }
        return;
      }

      if (!wrap) {
        needsFullRefresh = true;
        return;
      }

      el.style.display = '';
      el.style.removeProperty('pointer-events');
      el.removeAttribute('aria-hidden');
      wrap.style.transformOrigin = 'bottom center';
      wrap.style.transform = pct >= this.markerIconScaleMax ? '' : `scale(${pct / 100})`;
      this.applyClusterBadgeScale(wrap, pct);
    });
    return needsFullRefresh;
  }

  private addMarker(mark: any, id: string): void {
    if (!this.map || !mark.position) return;
    try {
      // Remove any existing marker with the same id before adding a new one
      if (id && this.markerRefs.has(id)) {
        try {
          this.markerRefs.get(id)?.remove();
        } catch (err) {
          console.warn('Error removing existing marker before add:', err);
        }
        this.markerRefs.delete(id);
      }

      const position = this.normalizeCenter(mark.position);
      if (position[0] === 0 && position[1] === 0) {
        return;
      }
      const markerElement = this.createMarkerElementWithImageIcon(mark);
      this.applySelectedCameraStyle(markerElement, mark);
      if (mark.selected) {
        markerElement.classList.add('offline-marker--selected');
      }
      if (mark._listHover) {
        markerElement.classList.add('offline-marker--list-hover');
      }
      const labelAnchorOffsetY = this.getMarkerLabelAnchorOffsetY(mark);
      const marker = new maplibregl.Marker({
        draggable: mark.options?.draggable || false,
        element: markerElement,
        // Same as single markers: tip/foot of the pin sits on the coordinate.
        // Cluster position is the computed centroid; mixed anchors made clusters look wrong.
        anchor: 'bottom',
        offset: [0, labelAnchorOffsetY],
        pitchAlignment: 'map'

      }).setLngLat(position);

      marker.addTo(this.map);

      // Dragging a marker can emit a synthetic click on mouseup.
      // Suppress that post-drag click so "edit popup on click" only happens on intentional clicks.
      let hasDraggedSinceStart = false;
      let suppressClickUntil = 0;

      const markerEl = marker.getElement();
      if (markerEl) {
        const interactionEl =
          (markerEl.querySelector('.offline-marker__icon-scale') as HTMLElement | null) || markerEl;
        interactionEl.addEventListener('click', (e) => {
          if (Date.now() < suppressClickUntil) {
            e.stopPropagation();
            return;
          }
          e.stopPropagation(); // Prevent event from bubbling to map
          this.zone.run(() => {
            this.markerClick({}, mark, marker);
          });
        });

        if (this.isHoverable) {
          interactionEl.addEventListener('mouseenter', () => {
            this.zone.run(() => {
              if (this.isHoverable) {
                this.markerHover({}, mark, marker);
                if (mark.markerType !== 'squad') {
                  this.openInfoPopup(mark, position);
                }
              }
            });
          });

          interactionEl.addEventListener('mouseleave', () => {
            // Add a small delay before closing to allow mouse to move to popup
            this.schedulePopupClose();
          });
        }

        interactionEl.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          this.zone.run(() => {
            this.onMarkerRightClick({ domEvent: e }, mark, marker);
          });
        });
      }

      if (mark.options?.draggable) {
        marker.on('dragstart', () => {
          hasDraggedSinceStart = false;
        });
        marker.on('drag', () => {
          hasDraggedSinceStart = true;
        });
        marker.on('dragend', () => {
          try {
            if (hasDraggedSinceStart) {
              suppressClickUntil = Date.now() + 300;
            }
            const pos = marker.getLngLat();
            this.zone.run(() => {
              this.onMarkerDragEnd({ latLng: { lat: pos.lat, lng: pos.lng } }, mark, marker);
            });
          } catch (err) {
            console.warn('Marker dragend error:', err);
          }
        });
      }

      this.markerRefs.set(id, marker);
    } catch (err) {
      console.warn('Error adding marker:', err);
    }
  }

  private createMarkerElement(mark: any): HTMLElement {
    const el = document.createElement('div');
    el.className = 'offline-marker';
    el.style.position = 'relative';
    el.style.display = 'block';

    //vertical-align: bottom;
    el.style.verticalAlign = 'bottom';

    let iconUrl = '';
    if (mark.icon) {
      if (typeof mark.icon === 'string') {
        iconUrl = mark.icon;
      } else if (mark.icon.url) {
        iconUrl = mark.icon.url;
      }
    }

    const defaultIconUrl = getDefaultIconForMarkerType(mark?.markerType);
    const finalIconUrl = iconUrl || defaultIconUrl;

    // Use img element instead of background-image for better error handling
    const img = document.createElement('img');
    img.src = finalIconUrl;
    img.style.width = '32px';
    img.style.height = '32px';
    img.style.objectFit = 'contain';
    img.style.display = 'block';
    img.style.cursor = 'pointer';
    img.alt = mark.title || 'marker';

    // Add error handler to fallback to default icon
    img.onerror = () => {
      if (img.src !== defaultIconUrl) {
        img.src = defaultIconUrl;
      }
    };

    el.appendChild(img);
    el.style.width = '32px';
    el.style.height = '32px';
    el.style.cursor = 'pointer';

    if (this.isIconLabelsOn && mark.showLabel && mark.label) {
      const label = document.createElement('div');
      label.textContent = mark.label.text || mark.label;
      label.style.position = 'absolute';
      label.style.top = '100%';
      label.style.left = '50%';
      label.style.transform = 'translateX(-50%)';
      label.style.marginTop = '-5px';
      label.style.lineHeight = '1';
      label.style.background = 'transparent';
      label.style.padding = '0 4px';
      label.style.borderRadius = '2px';
      label.style.fontSize = '12px';
      label.style.whiteSpace = 'nowrap';
      label.style.pointerEvents = 'none';
      if (mark.label.color) {
        label.style.color = mark.label.color;
      }
      el.appendChild(label);
    }

    return el;
  }

  /**
   * Create a marker element that uses the provided icon (or default icon)
   * without adding inline styling. Keeps the original method intact.
   */
  private createMarkerElementWithImageIcon(mark: any): HTMLElement {
    const el = document.createElement('div');
    el.className = 'offline-marker';
    const iconScaleCategory = this.iconScaleSliderCategoryForMark(mark);
    if (iconScaleCategory) {
      el.setAttribute('data-icon-scale-category', iconScaleCategory);
    }
    if (mark?.markerType) {
      el.setAttribute('data-marker-type', String(mark.markerType));
    }
    if (mark?._id) {
      el.setAttribute('data-marker-id', String(mark._id));
    }
    if (mark?._isCluster) {
      el.setAttribute('data-marker-cluster', 'true');
      const clusterCameraIds = this.extractClusterCameraIds(mark);
      if (clusterCameraIds.length > 0) {
        el.setAttribute('data-cluster-camera-ids', clusterCameraIds.join(','));
      }
    }

    if (this.isMarkerIconSliderHidden(mark)) {
      el.style.display = 'none';
      el.style.pointerEvents = 'none';
      el.setAttribute('aria-hidden', 'true');
      return el;
    }

    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'offline-marker__icon-scale';
    imgWrapper.style.position = 'relative';
    imgWrapper.style.display = 'inline-block';
    imgWrapper.style.lineHeight = '0';

    const img = document.createElement('img');
    img.alt = mark.title || 'marker';
    const initialIconUrl = this.resolveIconUrl(mark);
    img.src = initialIconUrl;
    img.style.display = 'block';
    img.style.objectFit = 'contain';
    img.style.maxHeight = 'none';
    img.style.maxWidth = 'none';

    img.onerror = () => {
      const defaultIconUrl = getDefaultIconForMarkerType(mark?.markerType);
      if (img.src !== defaultIconUrl) {
        img.src = defaultIconUrl;
      }
    };

    imgWrapper.appendChild(img);

    if (mark.isInCall || mark.isLiveStreaming) {
      const dot = document.createElement('div');
      dot.className = mark.isLiveStreaming ? 'blinking-dot blinking-dot-green' : 'blinking-dot';
      imgWrapper.appendChild(dot);
    }

    if (mark._isCluster && Number(mark._clusterCount) > 1) {
      const count = Math.min(999, Math.max(2, Number(mark._clusterCount) || 0));
      const badge = document.createElement('span');
      badge.textContent = String(count);
      badge.className = 'offline-marker__cluster-badge';
      badge.style.position = 'absolute';
      badge.style.right = '-10px';
      badge.style.top = '-8px';
      badge.style.minWidth = '18px';
      badge.style.height = '18px';
      badge.style.borderRadius = '999px';
      badge.style.padding = '0 5px';
      badge.style.display = 'inline-flex';
      badge.style.alignItems = 'center';
      badge.style.justifyContent = 'center';
      badge.style.fontSize = '13px';
      badge.style.fontWeight = '700';
      badge.style.color = '#fff';
      badge.style.background = this.getClusterBadgeBackgroundForMarkerCategory(
        this.getMarkerClusterCategory(mark)
      );
      badge.style.border = '1px solid rgba(255,255,255,0.85)';
      badge.style.boxShadow = '0 1px 3px rgba(0,0,0,0.45)';
      imgWrapper.appendChild(badge);
    }

    this.applyMarkerIconSliderScale(mark, imgWrapper);

    el.appendChild(imgWrapper);

    if (mark.showLabel && mark.label) {
      const label = document.createElement('div');
      label.className = 'offline-marker__label';
      label.textContent = mark.label.text || mark.label;
      label.style.fontWeight = 'bold';
      // label.style.marginTop = '-10px';
      // if (mark.markerType === 'building') {
      //   /**
      //    * Pin stack height = icon only (label is absolutely positioned), so MapLibre
      //    * `anchor: 'bottom'` does not shift when Icon Labels is toggled.
      //    */
      //   const stack = document.createElement('div');
      //   stack.className = 'offline-marker__pin-stack';
      //   stack.style.position = 'relative';
      //   stack.style.display = 'inline-block';
      //   stack.style.lineHeight = '0';
      //   stack.appendChild(imgWrapper);
      //   label.style.position = 'absolute';
      //   label.style.top = '100%';
      //   label.style.left = '50%';
      //   label.style.transform = 'translateX(-50%)';
      //   label.style.marginTop = '4px';
      //   label.style.textAlign = 'center';
      //   label.style.maxWidth = '140px';
      //   label.style.whiteSpace = 'normal';
      //   label.style.lineHeight = '1.2';
      //   label.style.zIndex = '1';
      //   stack.appendChild(label);
      //   el.appendChild(stack);
      //   return el;
      // } else {
      //   label.style.marginTop = '-10px';
      //   el.appendChild(imgWrapper);
      //   el.appendChild(label);
      //   return el;
      // }
      if (!this.isIconLabelsOn) {
        label.style.visibility = 'hidden';
      } else {
        label.style.visibility = 'visible';
      }
      el.appendChild(label);
    }

    return el;
  }

  /**
   * Keep icon tip anchored to coordinates when labels are rendered in normal flow.
   * Positive Y offset moves the marker element down on screen.
   */
  private getMarkerLabelAnchorOffsetY(mark: any): number {
    const hasVisibleLabel = !!(this.isIconLabelsOn && mark?.showLabel && mark?.label);
    if (!hasVisibleLabel) {
      return 0;
    }
    return 16;
  }

  private extractClusterCameraIds(mark: any): string[] {
    const items = Array.isArray(mark?._clusterItems) ? mark._clusterItems : [];
    const ids: string[] = items
      .filter((item: any) => item?.markerType === 'camera' && item?._id != null && item?._id !== '')
      .map((item: any) => String(item._id));
    return Array.from(new Set<string>(ids));
  }

  private isSelectedCameraMarker(mark: any): boolean {
    const selectedIds = new Set((this.selectedCamsIds || []).map((id) => String(id)));
    if (!selectedIds.size) {
      return false;
    }
    if (mark?.markerType !== 'camera') {
      return false;
    }
    if (mark?._isCluster) {
      return this.extractClusterCameraIds(mark).some((id) => selectedIds.has(id));
    }
    return mark?._id != null && selectedIds.has(String(mark._id));
  }

  private applySelectedCameraStyle(markerElement: HTMLElement, mark: any): void {
    if (!markerElement) {
      return;
    }
    if (this.isSelectedCameraMarker(mark)) {
      markerElement.classList.add('offline-marker--camera-selected');
    } else {
      markerElement.classList.remove('offline-marker--camera-selected');
    }
  }

  private applySelectedCameraStylesToRenderedMarkers(): void {
    if (!this.markerRefs || this.markerRefs.size === 0) {
      return;
    }
    const selectedIds = new Set((this.selectedCamsIds || []).map((id) => String(id)));
    this.markerRefs.forEach((mapMarker) => {
      const el = mapMarker?.getElement?.() as HTMLElement | undefined;
      if (!el) {
        return;
      }
      const markerType = String(el.getAttribute('data-marker-type') || '');
      if (markerType !== 'camera') {
        el.classList.remove('offline-marker--camera-selected');
        return;
      }
      const isCluster = el.getAttribute('data-marker-cluster') === 'true';
      let shouldSelect = false;
      if (isCluster) {
        const clusterIdsRaw = el.getAttribute('data-cluster-camera-ids') || '';
        const clusterIds = clusterIdsRaw.split(',').map((id) => id.trim()).filter(Boolean);
        shouldSelect = clusterIds.some((id) => selectedIds.has(id));
      } else {
        const markerId = String(el.getAttribute('data-marker-id') || '');
        shouldSelect = markerId !== '' && selectedIds.has(markerId);
      }
      if (shouldSelect) {
        el.classList.add('offline-marker--camera-selected');
      } else {
        el.classList.remove('offline-marker--camera-selected');
      }
    });
  }

  private resolveIconUrl(mark: any): string {
    if (mark?.icon) {
      if (typeof mark.icon === 'string') {
        return mark.icon;
      }
      if (mark.icon.url) {
        return mark.icon.url;
      }
    }
    return getDefaultIconForMarkerType(mark?.markerType);
  }

  /**
   * Sectors are keyed like marker FOV overlays (sector_mark_*). When markers are removed from
   * the rendered list via typeToHide (e.g. camera toggle) they are not marked hideIt — they are
   * filtered out entirely — so their sectors would otherwise stay on the map. Remove any sector
   * that does not correspond to a currently visible marker with shape/FOV data.
   */
  private removeOrphanMarkerSectors(visibleMarks: any[] = this.markers): void {
    const keepIds = new Set<string>();
    if (visibleMarks?.length) {
      visibleMarks.forEach((mark, idx) => {
        if (
          mark &&
          mark.position &&
          mark.shapeLength &&
          mark.shapeRotationStart !== undefined &&
          mark.shapeRotationEnd !== undefined &&
          (!mark.hideIt || (mark.markerType === 'camera' && mark.mapCameraFilterHidden))
        ) {
          const sectorId = mark._id ? `sector_mark_${mark._id}` : `sector_mark_${mark.markerType}_${idx}`;
          keepIds.add(sectorId);
        }
      });
    }
    const toRemove: string[] = [];
    this.sectors.forEach((_, id) => {
      if (id.startsWith('sector_mark_') && !keepIds.has(id)) {
        toRemove.push(id);
      }
    });
    toRemove.forEach((id) => this.removeSector(id));
  }

  private shouldClusterPins(): boolean {
    return !!(
      this.combineIconsOnOfflineMap &&
      this.clusterMarkersByCategory &&
      this.map &&
      this.mapLoaded &&
      this.map.getZoom() < this.markerClusterMaxZoom
    );
  }

  private getMarkerClusterCategory(mark: any): string {
    const t = mark?.markerType || 'unknown';
    if (t === 'note') {
      if (mark.car_of_interest || mark.note?.car_of_interest) return 'note:car';
      if (mark.person_of_interest || mark.note?.person_of_interest) return 'note:person';
      return 'note:other';
    }
    return t;
  }

  /**
   * Cluster count badge fill color by {@link getMarkerClusterCategory} key (same grouping as clustering).
   */
  private getClusterBadgeBackgroundForMarkerCategory(category: string): string {
    const c = (category || 'unknown').trim();
    const palette: Record<string, string> = {
      camera: '#efcb00',
      annotation: '#940bf5',
      location: '#3ac63d',
      building: '#5c6bc0',
      note: '#2d465d',
      'note:other': '#2d465d',
      'note:person': '#186dbf',
      'note:car': '#186dbf',
      threat: '#dc440f',
      unknown: '#000000',
    };
    return palette[c] ?? palette['unknown'];
  }

  private getMarkerLngLat(mark: any): { lat: number; lng: number } | null {
    if (!mark) {
      return null;
    }
    // Match {@link addMarker}: visible pins use normalizeCenter(mark.position).
    // Clustering previously preferred geolocation first, so centroids could sit
    // away from the pins users actually see when the two fields diverged.
    const [lngPos, latPos] = this.normalizeCenter(mark.position);
    if (
      Number.isFinite(latPos) &&
      Number.isFinite(lngPos) &&
      !(lngPos === 0 && latPos === 0)
    ) {
      return { lat: latPos, lng: lngPos };
    }
    let src: any = mark.geolocation;
    if (typeof src === 'string') {
      try {
        src = JSON.parse(src);
      } catch {
        src = null;
      }
    }
    const lat = Number(src?.lat ?? mark.lat);
    const lng = Number(src?.lng ?? src?.lon ?? mark.lng ?? mark.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  }

  private effectiveClusterRadiusPx(): number {
    if (!this.map) {
      return Math.max(8, Number(this.markerClusterRadiusPx) || 56);
    }
    return this.effectiveClusterRadiusPxAtZoom(Number(this.map.getZoom()));
  }

  /** Same pixel-radius curve as live clustering, for a hypothetical zoom level. */
  private effectiveClusterRadiusPxAtZoom(z: number): number {
    const base = Math.max(8, Number(this.markerClusterRadiusPx) || 56);
    if (!Number.isFinite(z)) {
      return base;
    }
    const startSplitZoom = Math.max(0, this.markerClusterMaxZoom - 4);
    if (z <= startSplitZoom) {
      return base;
    }
    const denom = Math.max(0.0001, this.markerClusterMaxZoom - startSplitZoom);
    const t = Math.max(0, Math.min(1, (z - startSplitZoom) / denom));
    return Math.max(6, base * (1 - 0.55 * t));
  }

  /** Screen position at `targetZoom` with map center fixed (matches zoom scaling of `project`). */
  private projectScaledToZoom(lng: number, lat: number, targetZoom: number): { x: number; y: number } | null {
    if (!this.map) {
      return null;
    }
    const z0 = this.map.getZoom();
    const c = this.map.getCenter();
    const centerPt = this.map.project([c.lng, c.lat]);
    const p = this.map.project([lng, lat]);
    const scale = Math.pow(2, targetZoom - z0);
    return {
      x: centerPt.x + (p.x - centerPt.x) * scale,
      y: centerPt.y + (p.y - centerPt.y) * scale,
    };
  }

  /**
   * Inverse of {@link projectScaledToZoom}: converts the cluster centroid in scaled screen space
   * (same space used to merge nearby pins) back to lng/lat. Keeps cluster pins aligned with the
   * clustering math and stable when zooming; previously we used only a bbox center in lat/lng,
   * which could sit away from the merged pin stack (noticeable for building clusters).
   */
  private getLngLatFromScaledClusterPixel(
    cx: number,
    cy: number,
    targetZoom: number
  ): { lat: number; lng: number } | null {
    if (!this.map || !Number.isFinite(cx) || !Number.isFinite(cy)) {
      return null;
    }
    const z0 = this.map.getZoom();
    const c = this.map.getCenter();
    const centerPt = this.map.project([c.lng, c.lat]);
    const scale = Math.pow(2, targetZoom - z0);
    if (!Number.isFinite(scale) || Math.abs(scale) < 1e-10) {
      return null;
    }
    const px = centerPt.x + (cx - centerPt.x) / scale;
    const py = centerPt.y + (cy - centerPt.y) / scale;
    const ll = this.map.unproject([px, py]);
    return { lat: ll.lat, lng: ll.lng };
  }

  private clusterMemberIdSet(members: any[]): Set<string> {
    const s = new Set<string>();
    for (const m of members || []) {
      if (m?._id != null && m._id !== '') {
        s.add(String(m._id));
      }
    }
    return s;
  }

  private clusterMemberIdsEqual(a: any[], b: any[]): boolean {
    const sa = this.clusterMemberIdSet(a);
    const sb = this.clusterMemberIdSet(b);
    if (sa.size !== sb.size) {
      return false;
    }
    for (const id of sa) {
      if (!sb.has(id)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Same clustering as `computeDisplayMarkers`, but for an arbitrary zoom (map center unchanged).
   * Used to find the zoom level where a clicked cluster first splits.
   */
  private computeDisplayMarkersForVirtualZoom(input: any[], z: number): any[] {
    if (!this.map || !input?.length) {
      return input || [];
    }
    if (z >= this.markerClusterMaxZoom) {
      return input;
    }
    const eligibleByCat = new Map<string, any[]>();
    const passthrough: any[] = [];
    for (const mark of input) {
      const hidden = mark?.hideIt || (mark?.markerType === 'camera' && mark?.mapCameraFilterHidden);
      const pos = this.getMarkerLngLat(mark);
      if (!mark || hidden || !pos) {
        passthrough.push(mark);
        continue;
      }
      const cat = this.getMarkerClusterCategory(mark);
      if (!eligibleByCat.has(cat)) {
        eligibleByCat.set(cat, []);
      }
      eligibleByCat.get(cat)!.push(mark);
    }

    const radius = this.effectiveClusterRadiusPxAtZoom(z);
    const r2 = radius * radius;
    const output: any[] = [...passthrough];

    for (const [cat, marks] of eligibleByCat.entries()) {
      const clusters: Array<{ members: any[]; cx: number; cy: number }> = [];
      for (const mark of marks) {
        const c = this.getMarkerLngLat(mark);
        if (!c) continue;
        const p = this.projectScaledToZoom(c.lng, c.lat, z);
        if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;

        let chosen = -1;
        let chosenD2 = Infinity;
        for (let i = 0; i < clusters.length; i++) {
          const dx = p.x - clusters[i].cx;
          const dy = p.y - clusters[i].cy;
          const d2 = dx * dx + dy * dy;
          if (d2 <= r2 && d2 < chosenD2) {
            chosen = i;
            chosenD2 = d2;
          }
        }

        if (chosen < 0) {
          clusters.push({ members: [mark], cx: p.x, cy: p.y });
        } else {
          const cl = clusters[chosen];
          cl.members.push(mark);
          const n = cl.members.length;
          cl.cx = ((cl.cx * (n - 1)) + p.x) / n;
          cl.cy = ((cl.cy * (n - 1)) + p.y) / n;
        }
      }

      for (const cl of clusters) {
        if (cl.members.length === 1) {
          output.push(cl.members[0]);
        } else {
          const anchor = this.getLngLatFromScaledClusterPixel(cl.cx, cl.cy, z);
          output.push(this.buildClusterDisplayMark(cat, cl.members, anchor));
        }
      }
    }
    return output;
  }

  private isClusterIntactAtVirtualZoom(members: any[], z: number): boolean {
    if (!members?.length) {
      return false;
    }
    if (this.clusterMemberIdSet(members).size === 0) {
      return false;
    }
    const display = this.computeDisplayMarkersForVirtualZoom(this.markers || [], z);
    for (const d of display) {
      if (d._isCluster && d._clusterMembers && this.clusterMemberIdsEqual(d._clusterMembers, members)) {
        return true;
      }
    }
    return false;
  }

  /** Minimum zoom at which this cluster no longer appears as a single grouped pin (or max zoom if it cannot split). */
  private zoomForFirstDecluster(members: any[]): number {
    if (!this.map) {
      return this.markerClusterMaxZoom;
    }
    const z0 = this.map.getZoom();
    const maxZ = this.markerClusterMaxZoom;
    if (!members || members.length < 2 || this.clusterMemberIdSet(members).size === 0) {
      return Math.min(maxZ, z0 + 1.25);
    }
    if (!this.isClusterIntactAtVirtualZoom(members, z0)) {
      return z0;
    }
    let lo = z0;
    let hi = maxZ;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (this.isClusterIntactAtVirtualZoom(members, mid)) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    return Math.min(maxZ, Math.max(z0 + 0.02, hi));
  }

  /**
   * Geometric center of the cluster’s lat/lng bounding box (midpoint of extent).
   * Not the coordinate mean / centroid (which can sit off-center when points are uneven).
   */
  private getClusterExtentCenter(
    points: { lat: number; lng: number }[]
  ): { lat: number; lng: number } | null {
    if (!points.length) {
      return null;
    }
    let minLat = points[0].lat;
    let maxLat = points[0].lat;
    let minLng = points[0].lng;
    let maxLng = points[0].lng;
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lng);
      maxLng = Math.max(maxLng, p.lng);
    }
    return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  }

  private buildClusterDisplayMark(
    cat: string,
    members: any[],
    anchorFromClusterPixels?: { lat: number; lng: number } | null
  ): any {
    const first = members[0];
    const points = members
      .map((m) => this.getMarkerLngLat(m))
      .filter((p): p is { lat: number; lng: number } => !!p);
    const center =
      anchorFromClusterPixels &&
        Number.isFinite(anchorFromClusterPixels.lat) &&
        Number.isFinite(anchorFromClusterPixels.lng)
        ? anchorFromClusterPixels
        : this.getClusterExtentCenter(points) ?? this.getMarkerLngLat(first);
    const ids = members
      .map((m) => (m?._id != null && m._id !== '' ? String(m._id) : ''))
      .filter(Boolean)
      .sort();
    const clusterId = ids.length ? `cluster_${cat}_${ids.join('~')}` : `cluster_${cat}_${++this.markerClusterSerial}`;
    return {
      ...first,
      _id: clusterId,
      position: { lat: center?.lat ?? first.position?.lat, lng: center?.lng ?? first.position?.lng },
      lat: center?.lat ?? first.lat,
      lng: center?.lng ?? first.lng,
      _isCluster: true,
      _clusterCount: members.length,
      _clusterMembers: members,
      shapeLength: undefined,
      shapeRotationStart: undefined,
      shapeRotationEnd: undefined,
      options: { draggable: false },
      showLabel: false,
    };
  }

  private computeDisplayMarkers(input: any[]): any[] {
    if (!this.shouldClusterPins() || !input?.length || !this.map) {
      return input || [];
    }
    return this.computeDisplayMarkersForVirtualZoom(input, this.map.getZoom());
  }

  private capDisplayMarkers(marks: any[]): any[] {
    if (!marks?.length || marks.length <= this.maxRenderedMarkers) {
      return marks || [];
    }
    const priority = (m: any): number => {
      const t = m?.markerType || '';
      if (t === 'case') return 0;
      if (t === 'camera') return 1;
      if (t === 'building') return 2;
      if (t === 'threat') return 3;
      if (t === 'investigator' || t === 'investigator-group' || t === 'teammember' || t === 'squad') {
        return 4;
      }
      if (m?._isCluster) return 5;
      return 6;
    };
    return [...marks].sort((a, b) => priority(a) - priority(b)).slice(0, this.maxRenderedMarkers);
  }

  private getMarkerDomId(mark: any, idx: number): string {
    return mark._id ? `marker_${mark._id}` : `marker_${mark.markerType}_${idx}`;
  }

  private applyMarkerStreamingState(investigatorId: string, isLiveStreaming: boolean): void {
    let changed = false;
    for (const collection of [this.markers, this.markersOriginal]) {
      const targetMarker = collection.find(m => m._id && m._id == investigatorId);
      if (targetMarker && targetMarker.isLiveStreaming !== isLiveStreaming) {
        targetMarker.isLiveStreaming = isLiveStreaming;
        changed = true;
      }
    }
    if (changed) {
      this.scheduleMarkersVisualRefresh();
    }
  }

  private applyMarkerInCallState(userId: string, isInCall: boolean): void {
    for (const collection of [this.markers, this.markersOriginal]) {
      const targetMarker = collection.find(m => m._id && m._id == userId);
      if (targetMarker) {
        targetMarker.isInCall = isInCall;
      }
    }
  }

  /** Fingerprint for incremental marker sync when cluster membership, pin position, or status dot changes. */
  private getDisplayedMarkerFingerprint(mark: any): string {
    const pos = mark?.position;
    const lat = pos?.lat ?? '';
    const lng = pos?.lng ?? '';
    const statusDot = `${mark?.isLiveStreaming ? 'live' : ''}${mark?.isInCall ? 'call' : ''}`;
    if (mark?._isCluster) {
      const memberIds = (mark._clusterMembers || [])
        .map((m: any) => (m?._id != null && m._id !== '' ? String(m._id) : ''))
        .filter(Boolean)
        .sort()
        .join('~');
      return `cluster:${mark._clusterCount}:${memberIds}:${lat}:${lng}:${statusDot}`;
    }
    return `pin:${lat}:${lng}:${statusDot}`;
  }

  /**
   * Add/remove only markers that changed — avoids destroying every MapLibre HTML marker on pan/zoom.
   */
  private syncDisplayedMarkers(displayMarks: any[], drawCameraSectors: boolean): void {
    const desiredMarkerIds = new Set<string>();
    if (this.marker?.position) {
      desiredMarkerIds.add('single');
    }

    displayMarks?.forEach((mark, idx) => {
      const pinHidden =
        mark.hideIt ||
        (mark.markerType === 'camera' && mark.mapCameraFilterHidden);
      if (mark?.position && !pinHidden) {
        desiredMarkerIds.add(this.getMarkerDomId(mark, idx));
      }
    });

    for (const id of [...this.markerRefs.keys()]) {
      if (!desiredMarkerIds.has(id)) {
        try {
          this.markerRefs.get(id)?.remove();
        } catch (err) {
          console.warn('Error removing marker:', err);
        }
        this.markerRefs.delete(id);
        this.markerRenderedFingerprints.delete(id);
      }
    }

    if (this.marker?.position && !this.markerRefs.has('single')) {
      this.addMarker(this.marker, 'single');
    }

    displayMarks?.forEach((mark, idx) => {
      const sectorId = mark._id ? `sector_mark_${mark._id}` : `sector_mark_${mark.markerType}_${idx}`;
      const pinHidden =
        mark.hideIt ||
        (mark.markerType === 'camera' && mark.mapCameraFilterHidden);
      const hasFov =
        mark.shapeLength &&
        mark.shapeRotationStart !== undefined &&
        mark.shapeRotationEnd !== undefined;
      const cameraFilterTransparentSector =
        mark.markerType === 'camera' && mark.mapCameraFilterHidden && hasFov;

      if (mark && mark.position && !pinHidden) {
        const id = this.getMarkerDomId(mark, idx);
        const fingerprint = this.getDisplayedMarkerFingerprint(mark);
        const prevFingerprint = this.markerRenderedFingerprints.get(id);
        if (!this.markerRefs.has(id) || prevFingerprint !== fingerprint) {
          this.addMarker(mark, id);
          this.markerRenderedFingerprints.set(id, fingerprint);
        }

        if (hasFov && drawCameraSectors) {
          if (!this.sectors.has(sectorId)) {
            this.addSector(sectorId, {
              center: mark.position,
              radius: mark.shapeLength,
              startAngle: mark.shapeRotationStart,
              endAngle: mark.shapeRotationEnd,
              color: mark.shapeColor || 'red',
              editable: false,
              data: mark,
              showTooltip: false,
              clickable: false,
              showBorder: false,
              fillOpacity: this.cameraSectorFillOpacity(),
            });
          }
        } else if (hasFov && this.sectors.has(sectorId)) {
          this.removeSector(sectorId);
        }
      } else if (mark && mark.position && cameraFilterTransparentSector && drawCameraSectors) {
        if (!this.sectors.has(sectorId)) {
          this.addSector(sectorId, {
            center: mark.position,
            radius: mark.shapeLength,
            startAngle: mark.shapeRotationStart,
            endAngle: mark.shapeRotationEnd,
            color: mark.shapeColor || 'red',
            editable: false,
            data: mark,
            showTooltip: false,
            clickable: false,
            showBorder: false,
            fillOpacity: 0,
          });
        }
      } else {
        if (mark.hideIt && !(mark.markerType === 'camera' && mark.mapCameraFilterHidden)) {
          if (this.sectors.has(sectorId)) {
            this.removeSector(sectorId);
          }
        } else if (!mark.position) {
          console.warn(`[OfflineMap] Skipping marker ${mark._id} due to missing position`);
        }
      }
    });
  }

  /**
   * Render only markers around the current viewport to keep drag/pan smooth with large datasets.
   * A small viewport expansion reduces edge pop-in while still capping DOM marker count.
   */
  private getMarkersNearViewport(input: any[]): any[] {
    if (!this.map || !input?.length) {
      return input || [];
    }
    const bounds = this.map.getBounds();
    if (!bounds) {
      return input;
    }
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const lngSpan = Math.max(0, ne.lng - sw.lng);
    const latSpan = Math.max(0, ne.lat - sw.lat);
    const lngPad = Math.max(0.02, lngSpan * 0.35);
    const latPad = Math.max(0.02, latSpan * 0.35);
    const minLat = sw.lat - latPad;
    const maxLat = ne.lat + latPad;
    const minLng = sw.lng - lngPad;
    const maxLng = ne.lng + lngPad;

    const isLngInRange = (lng: number): boolean => {
      if (minLng >= -180 && maxLng <= 180) {
        return lng >= minLng && lng <= maxLng;
      }
      const normalizedLng = ((((lng + 180) % 360) + 360) % 360) - 180;
      const normalizedMinLng = ((((minLng + 180) % 360) + 360) % 360) - 180;
      const normalizedMaxLng = ((((maxLng + 180) % 360) + 360) % 360) - 180;
      return normalizedMinLng <= normalizedMaxLng
        ? normalizedLng >= normalizedMinLng && normalizedLng <= normalizedMaxLng
        : normalizedLng >= normalizedMinLng || normalizedLng <= normalizedMaxLng;
    };

    return input.filter((mark) => {
      const pos = this.getMarkerLngLat(mark);
      if (!pos) {
        return false;
      }
      if (pos.lat < minLat || pos.lat > maxLat) {
        return false;
      }
      return isLngInRange(pos.lng);
    });
  }

  private updateMarkersVisual(): void {
    if (!this.map) {
      if (this.map) {
        this.map.once('load', () => {
          requestAnimationFrame(() => {
            this.updateMarkersVisual();
          });
        });
      }
      return;
    }
    if (this.markerVisualRefreshRaf != null) {
      cancelAnimationFrame(this.markerVisualRefreshRaf);
      this.markerVisualRefreshRaf = null;
    }
    // Final guard: dedupe the rendered markers list in case upstream callers
    // produced transient duplicates with different object references.
    this.markers = this.dedupeMarkers(this.markers);

    if (this.popupCloseTimeout) {
      clearTimeout(this.popupCloseTimeout);
      this.popupCloseTimeout = null;
    }
    if (this.infoPopup) {
      this.infoPopup.remove();
      this.infoPopup = null;
    }
    this.currentPopupMarker = null;

    this.markerVisualRefreshRaf = requestAnimationFrame(() => {
      this.markerVisualRefreshRaf = null;
      if (!this.map) {
        return;
      }

      // If viewport filtering yields empty due transient bounds/layout timing,
      // keep the loaded marker set instead of clearing pins after fitBounds.
      const allMarkers = this.markers || [];
      const nearViewport = this.filterMarkersToViewport ? this.getMarkersNearViewport(allMarkers) : allMarkers;
      const displayInput = nearViewport.length > 0 ? nearViewport : allMarkers;
      const clustered = this.computeDisplayMarkers(displayInput);
      const displayMarks = this.capDisplayMarkers(clustered);
      const drawCameraSectors =
        !this.map || this.map.getZoom() >= this.cameraSectorMinZoom;
      this.removeOrphanMarkerSectors(displayMarks);
      this.syncDisplayedMarkers(displayMarks, drawCameraSectors);

      const renderedDataMarkers = Array.from(this.markerRefs.keys()).some((id) => id !== 'single');
      if (renderedDataMarkers) {
        this.hasRenderedDataMarkers = true;
        this.initialMarkerRenderRetryCount = 0;
        if (this.initialMarkerRenderRetryTimer) {
          clearTimeout(this.initialMarkerRenderRetryTimer);
          this.initialMarkerRenderRetryTimer = null;
        }
      } else if (
        !this.hasRenderedDataMarkers &&
        allMarkers.length > 0 &&
        this.initialMarkerRenderRetryCount < this.maxInitialMarkerRenderRetries &&
        !this.initialMarkerRenderRetryTimer
      ) {
        const attempt = this.initialMarkerRenderRetryCount + 1;
        const delayMs = 120 * attempt;
        this.initialMarkerRenderRetryCount = attempt;
        this.initialMarkerRenderRetryTimer = setTimeout(() => {
          this.initialMarkerRenderRetryTimer = null;
          if (!this.map || !this.mapLoaded || this.hasRenderedDataMarkers) {
            return;
          }
          try {
            this.map.resize();
          } catch {
            /* ignore */
          }
          this.updateMarkersVisual();
        }, delayMs);
      }
    });
  }

  private openInfoPopup(mark: any, position: [number, number]): void {
    if (!this.map) return;
    const html = this.buildInfoPopupHtml(mark);
    if (!html) return;

    // Cancel any pending close timeout
    if (this.popupCloseTimeout) {
      clearTimeout(this.popupCloseTimeout);
      this.popupCloseTimeout = null;
    }

    if (this.infoPopup) {
      this.infoPopup.remove();
    }

    this.currentPopupMarker = mark;
    this.infoPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: '260px',
      className: 'offline-map-popup',
      // Marker anchor is bottom; lift popup above the icon so it does not overlap.
      offset: this.getInfoPopupOffset(mark)
    })
      .setLngLat(position)
      .setHTML(html)
      .addTo(this.map);

    // Add event handlers to the popup element to prevent flickering
    const popupElement = this.infoPopup.getElement();
    if (popupElement) {
      popupElement.addEventListener('mouseenter', () => {
        // Cancel close timeout when mouse enters popup
        if (this.popupCloseTimeout) {
          clearTimeout(this.popupCloseTimeout);
          this.popupCloseTimeout = null;
        }
      });

      popupElement.addEventListener('mouseleave', () => {
        // Schedule close when mouse leaves popup
        this.schedulePopupClose();
      });
    }
  }

  private getInfoPopupOffset(mark: any): [number, number] {
    // Base marker icon is ~32px; account for slider scaling and keep a small gap.
    const scalePercent = this.clampIconScalePercent(this.iconScalePercentForMark(mark));
    const scaledHeight = Math.round(32 * (Math.max(scalePercent, 20) / 100));
    const yOffset = -(scaledHeight + 10);
    return [0, yOffset];
  }

  private schedulePopupClose(): void {
    // Clear any existing timeout
    if (this.popupCloseTimeout) {
      clearTimeout(this.popupCloseTimeout);
    }

    // Schedule close after a short delay
    this.popupCloseTimeout = setTimeout(() => {
      this.closeInfoPopup();
    }, 150); // 150ms delay to allow mouse to move between marker and popup
  }

  private closeInfoPopup(): void {
    if (this.popupCloseTimeout) {
      clearTimeout(this.popupCloseTimeout);
      this.popupCloseTimeout = null;
    }

    if (this.infoPopup) {
      this.infoPopup.remove();
      this.infoPopup = null;
    }

    this.currentPopupMarker = null;
    this.zone.run(() => {
      this.markerHoverOut({});
    });
  }

  private buildInfoPopupHtml(mark: any): string {
    if (!mark) return '';

    const safeText = (val?: any) => (val === undefined || val === null ? '' : String(val));
    const imageTemplate = (src?: string) =>
      src ? `<img style="max-height:80px;max-width:100px;" src="${src}" onerror="this.src='assets/images/error-default-image.png'">` : '';
    const textDivStyle = 'word-wrap:break-word;overflow-wrap:break-word;white-space:normal;';

    // Format date as yy-mm-dd HH:MM in user's local timezone
    const formatDateLocal = (dateValue: any): string => {
      if (!dateValue) return '';
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (e) {
        return '';
      }
    };

    let content = '';
    let defTitle = '';
    switch (mark.markerType) {
      case 'camera':
        const camId = safeText(mark.camera_id);
        // const camImg = imageTemplate(mark.pictureLink);
        if (!camId) return '';
        content = `
          <div style="text-align:center;cursor:pointer;">
            ${camId ? `<div style="${textDivStyle}">${camId}</div>` : ''}
          </div>`;
        break;
      case 'investigator':
        const invName = safeText(mark.name);
        const invRank = safeText(mark.rank);
        const invYears = mark.years_in_service ? `<div>${mark.years_in_service} ${this.translate.instant('Years')}</div>` : '';
        if (!invName && !invRank && !invYears) return '';
        content = `
          <div style="text-align:center;cursor:pointer;">
            ${invName ? `<div style="${textDivStyle}">${invName}</div>` : ''}
            ${invRank ? `<div style="${textDivStyle}">${invRank}</div>` : ''}
            ${invYears ? `<div style="${textDivStyle}">${invYears}</div>` : ''}
          </div>`;
        break;
      case 'investigator-group':
        const groupName = safeText(mark.name);
        if (!groupName) return '';
        content = `
          <div style="text-align:center;cursor:pointer;">
            <div style="${textDivStyle}">${groupName}</div>
          </div>`;
        break;
      case 'case':
        const caseId = safeText(mark.case_id);
        const caseType = safeText(mark.dataType);
        const caseDesc = safeText(this.transform(mark.description || '', ['100', '...']));
        if (!caseId && !caseType && !caseDesc) return '';
        content = `
          <div style="text-align:center;cursor:pointer;">
            ${caseId ? `<div style="${textDivStyle}">${caseId}</div>` : ''}
            ${caseType ? `<div style="${textDivStyle}">${caseType}</div>` : ''}
            ${caseDesc ? `<div style="${textDivStyle}">${caseDesc}</div>` : ''}
          </div>`;
        break;
      case 'tracking':
        const trackDate = mark.createdAt ? new Date(mark.createdAt).toLocaleString() : '';
        const trackCam = safeText(mark.camera_id);
        if (!trackDate && !trackCam) return '';
        content = `
          <div style="text-align:center;">
            ${trackDate ? `<div style="${textDivStyle}">${trackDate}</div>` : ''}
            ${trackCam ? `<div style="${textDivStyle}">${trackCam}</div>` : ''}
          </div>`;
        break;
      case 'annotation':
        const annImg = imageTemplate(mark.pictureLink);
        if (!annImg) return '';
        content = `<div style="text-align:center;">${annImg}</div>`;
        break;
      case 'note':
        defTitle = safeText(mark.title || mark.name || mark.label?.text);
        const noteDateValue = mark.note?.action_date || mark.note?.createdAt;
        const noteDate = (mark.note?.person_of_interest || mark.note?.car_of_interest) && noteDateValue
          ? `<div style="${textDivStyle}">${formatDateLocal(noteDateValue)}</div>` : '';
        const noteDesc = safeText(this.transform(mark.description || '', ['100', '...']));
        if (!noteDate && !noteDesc && !defTitle) return '';
        content = `
          <div style="text-align:center;cursor:pointer;">
            ${noteDate}
            ${defTitle ? `<div style="${textDivStyle}">${defTitle}</div>` : ''}
          </div>`;
        break;
      case 'document':
        const docDesc = safeText(this.transform(mark.description || '', ['100', '...']));
        if (!docDesc) return '';
        content = `
          <div style="text-align:center;">
            <div style="${textDivStyle}">${docDesc}</div>
          </div>`;
        break;
      case 'squad':
        const squadName = safeText(mark.name);
        if (!squadName) return '';
        content = `
          <div style="text-align:center;">
            <div style="${textDivStyle}">${squadName}</div>
          </div>`;
        break;
      case 'teammember':
        const tmName = safeText(mark.name || mark.alternative_name);
        const tmRank = mark.rank ? `<div style="${textDivStyle}">${safeText(mark.rank)}</div>` : '';
        const tmYears = mark.years_in_service ? `<div style="${textDivStyle}">${mark.years_in_service} ${this.translate.instant('Years')}</div>` : '';
        if (!tmName && !tmRank && !tmYears) return '';
        content = `
          <div style="text-align:center;cursor:pointer;">
            ${tmName ? `<div style="${textDivStyle}">${tmName}</div>` : ''}
            ${tmRank}
            ${tmYears}
          </div>`;
        break;
      default:
        defTitle = safeText(mark.title || mark.name || mark.label?.text);
        const defDesc = safeText(this.transform(mark.description || '', ['100', '...']));
        if (!defTitle && !defDesc) return '';
        content = `
          <div style="text-align:center;">
            ${defTitle ? `<div style="${textDivStyle}">${defTitle}</div>` : ''}
            ${defDesc ? `<div style="${textDivStyle}">${defDesc}</div>` : ''}
          </div>`;
    }

    if (!content || !content.trim()) return '';
    return `<div style="max-width:240px;word-wrap:break-word;overflow-wrap:break-word;white-space:normal;">${content}</div>`;
  }

  togglePath(id: string, visible: boolean = true): void {
    if (!this.map) return;

    // Use the map's setLayoutProperty to show/hide the layer
    if (this.map.getLayer(id)) {
      this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
    const pointsLayerId = `${id}-points`;
    if (this.map.getLayer(pointsLayerId)) {
      this.map.setLayoutProperty(pointsLayerId, 'visibility', visible ? 'visible' : 'none');
    }
  }

  /**
   * PMTiles basemap symbol layers for building names (not the case building pins).
   * Respect the same Icon Labels toggle as DOM marker labels.
   */
  private syncBasemapBuildingLabelLayersVisibility(): void {
    if (!this.map) {
      return;
    }
    const visible = this.isIconLabelsOn;
    const ids = ['building-labels', 'building-labels-fallback'];
    for (const id of ids) {
      if (!this.map.getLayer(id)) {
        continue;
      }
      try {
        this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      } catch {
        /* ignore */
      }
    }
  }

  private bindSafeRouteClickListeners(id: string, coordinates: [number, number][], color: string): void {
    if (!this.map || !id?.startsWith('safe-route-')) {
      return;
    }
    this.unbindSafeRouteClickListeners(id);
    const pointsLayerId = `${id}-points`;
    const payload = {
      type: 'safe-route',
      id,
      coordinates: coordinates.map((coord) => ({ lat: coord[1], lng: coord[0] })),
      color
    };
    const lineHandler = () => this.shapeClick.emit(payload);
    const pointsHandler = () => this.shapeClick.emit(payload);
    if (this.map.getLayer(id)) {
      this.map.on('click', id, lineHandler);
    }
    if (this.map.getLayer(pointsLayerId)) {
      this.map.on('click', pointsLayerId, pointsHandler);
    }
    this.polylineClickListeners.set(id, { line: lineHandler, points: pointsHandler });
  }

  private unbindSafeRouteClickListeners(id: string): void {
    if (!this.map) {
      return;
    }
    const listeners = this.polylineClickListeners.get(id);
    if (!listeners) {
      return;
    }
    const pointsLayerId = `${id}-points`;
    if (listeners.line) {
      try {
        this.map.off('click', id, listeners.line);
      } catch {
        /* ignore */
      }
    }
    if (listeners.points) {
      try {
        this.map.off('click', pointsLayerId, listeners.points);
      } catch {
        /* ignore */
      }
    }
    this.polylineClickListeners.delete(id);
  }

  private clearDraftPolylineEditHandles(id: string, retryCount: number = 0): void {
    const activeDrags = this.polylineDraftActiveDrags.get(id) || 0;
    if (activeDrags > 0) {
      if (retryCount > 30) {
        return;
      }
      setTimeout(() => this.clearDraftPolylineEditHandles(id, retryCount + 1), 16);
      return;
    }
    const handles = this.polylineDraftEditHandles.get(id);
    if (!handles?.length) {
      this.polylineDraftEditHandles.delete(id);
      return;
    }
    handles.forEach((handle) => {
      try {
        handle.remove();
      } catch {
        /* ignore */
      }
    });
    this.polylineDraftEditHandles.delete(id);
  }

  private incrementDraftPolylineActiveDrags(id: string): void {
    const current = this.polylineDraftActiveDrags.get(id) || 0;
    this.polylineDraftActiveDrags.set(id, current + 1);
  }

  private decrementDraftPolylineActiveDrags(id: string): void {
    const current = this.polylineDraftActiveDrags.get(id) || 0;
    if (current <= 1) {
      this.polylineDraftActiveDrags.delete(id);
      return;
    }
    this.polylineDraftActiveDrags.set(id, current - 1);
  }

  private isDraftSafeRoutePolyline(id: string): boolean {
    return this.draftSafeRoutePolylineIds.has(id);
  }

  /** Vertex dots are for drawing/saved safe routes only — not car/person-of-interest trails. */
  private shouldShowPathVertexCircles(id: string): boolean {
    return this.isDraftSafeRoutePolyline(id) || (typeof id === 'string' && id.startsWith('safe-route-'));
  }

  private removePathVertexCircles(id: string): void {
    if (!this.map) {
      return;
    }
    const pointsSourceId = `${id}-points-source`;
    const pointsLayerId = `${id}-points`;
    if (this.map.getLayer(pointsLayerId)) {
      this.map.removeLayer(pointsLayerId);
    }
    if (this.map.getSource(pointsSourceId)) {
      this.map.removeSource(pointsSourceId);
    }
  }

  private getSafeRouteLinePaint(id: string, color: string): any {
    const selected = this.isDraftSafeRoutePolyline(id);
    return {
      'line-color': color,
      'line-width': selected ? 7 : 4,
      'line-opacity': selected ? 1 : 0.8,
      'line-dasharray': selected ? [1, 0] : [2, 2]
    };
  }

  private syncDraftPolylineEditHandles(id: string, coordinates: [number, number][], color: string): void {
    if (!this.map || !this.isDraftSafeRoutePolyline(id)) {
      this.clearDraftPolylineEditHandles(id);
      return;
    }

    if ((this.polylineDraftActiveDrags.get(id) || 0) > 0) {
      return;
    }

    this.clearDraftPolylineEditHandles(id);
    if (!coordinates.length) {
      return;
    }

    const handles: maplibregl.Marker[] = [];
    const buildCoordsFromHandles = (): [number, number][] => handles.map((handle) => {
      const lngLat = handle.getLngLat();
      return [Number(lngLat.lng), Number(lngLat.lat)] as [number, number];
    }).filter((coord) => Number.isFinite(coord[0]) && Number.isFinite(coord[1]));
    const emitInteraction = (interaction: 'vertex-drag-end' | 'vertex-contextmenu', pointIndex: number): void => {
      this.shapeClick.emit({
        type: 'polyline-draft',
        id,
        interaction,
        pointIndex,
        coordinates: buildCoordsFromHandles().map((coord) => ({ lat: coord[1], lng: coord[0] }))
      });
    };

    coordinates.forEach((coord, pointIndex) => {
      const handleEl = document.createElement('div');
      handleEl.style.width = '14px';
      handleEl.style.height = '14px';
      handleEl.style.borderRadius = '50%';
      handleEl.style.backgroundColor = color;
      handleEl.style.border = '2px solid #ffffff';
      handleEl.style.boxSizing = 'border-box';
      handleEl.style.cursor = 'move';

      const marker = new maplibregl.Marker({ element: handleEl, draggable: true })
        .setLngLat(coord)
        .addTo(this.map!);
      marker.on('dragstart', () => {
        this.incrementDraftPolylineActiveDrags(id);
      });
      marker.on('dragend', () => {
        const nextCoordinates = buildCoordsFromHandles();
        this.updatePolylinePathInPlace(id, nextCoordinates, color);
        emitInteraction('vertex-drag-end', pointIndex);
        setTimeout(() => this.decrementDraftPolylineActiveDrags(id), 0);
      });
      handleEl.addEventListener('contextmenu', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        emitInteraction('vertex-contextmenu', pointIndex);
      });
      handles.push(marker);
    });

    this.polylineDraftEditHandles.set(id, handles);
  }

  clearPaths(): void {
    this.polylines.forEach((value, id) => {
      const polyline = this.polylines.get(id);
      if (polyline && this.map) {
        this.unbindSafeRouteClickListeners(id);
        this.clearDraftPolylineEditHandles(id);
        this.polylineDraftActiveDrags.delete(id);
        if (this.map.getLayer(id)) {
          this.map.removeLayer(id);
        }
        if (this.map.getSource(id)) {
          this.map.removeSource(id);
        }
        const pointsLayerId = `${id}-points`;
        const pointsSourceId = `${id}-points-source`;
        if (this.map.getLayer(pointsLayerId)) {
          this.map.removeLayer(pointsLayerId);
        }
        if (this.map.getSource(pointsSourceId)) {
          this.map.removeSource(pointsSourceId);
        }
        this.polylines.delete(id);
      }
    });
    this.polylines = new Map();
    this.polylineClickListeners.clear();
  }

  addPath(paths: any[], id: string, color: string = '#11345a'): void {
    if (!this.map) return;
    // Check if style is loaded, if not wait for it
    if (!this.isMapLoaded()) {
      console.log('map not loaded, waiting for style.load');
      this.map.once('style.load', () => {
        this.addPath(paths, id, color);
      });
      return;
    }

    const coordinates = paths.map(p => {
      if (Array.isArray(p)) {
        return [p[0], p[1]] as [number, number];
      }
      return [p.lng || p.lon, p.lat] as [number, number];
    });

    if (this.polylines.get(id)) {
      this.updatePolylinePathInPlace(id, coordinates, color);
      return;
    }

    if (coordinates.length >= 2 && !this.map.getSource(id)) {
      this.map.addSource(id, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          }
        }
      });
    }

    if (coordinates.length >= 2 && !this.map.getLayer(id)) {
      this.map.addLayer({
        id: id,
        type: 'line',
        source: id,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: this.getSafeRouteLinePaint(id, color)
      });
    }

    const pointsSourceId = `${id}-points-source`;
    const pointsLayerId = `${id}-points`;
    if (this.shouldShowPathVertexCircles(id)) {
      if (!this.map.getSource(pointsSourceId)) {
        this.map.addSource(pointsSourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: coordinates.map((coord) => ({
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Point',
                coordinates: coord
              }
            }))
          }
        });
      }

      if (!this.map.getLayer(pointsLayerId)) {
        this.map.addLayer({
          id: pointsLayerId,
          type: 'circle',
          source: pointsSourceId,
          paint: {
            'circle-radius': 6,
            'circle-color': color,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2
          }
        });
      }
    } else {
      this.removePathVertexCircles(id);
    }

    this.bindSafeRouteClickListeners(id, coordinates, color);
    this.syncDraftPolylineEditHandles(id, coordinates, color);

    this.polylines.set(id, { id, source: id });
  }

  /** Updates safe-route / polyline geometry without removing layers (avoids undo flicker). */
  private updatePolylinePathInPlace(
    id: string,
    coordinates: [number, number][],
    color: string
  ): void {
    const pointsSourceId = `${id}-points-source`;
    const pointsLayerId = `${id}-points`;

    if (coordinates.length >= 2) {
      const lineFeature: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates
        }
      };
      if (this.map.getSource(id)) {
        (this.map.getSource(id) as maplibregl.GeoJSONSource).setData(lineFeature);
      } else {
        this.map.addSource(id, {
          type: 'geojson',
          data: lineFeature
        });
      }
      if (!this.map.getLayer(id)) {
        this.map.addLayer({
          id: id,
          type: 'line',
          source: id,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: this.getSafeRouteLinePaint(id, color)
        });
      } else {
        try {
          this.map.setPaintProperty(id, 'line-color', color);
          this.map.setPaintProperty(id, 'line-width', this.isDraftSafeRoutePolyline(id) ? 7 : 4);
          this.map.setPaintProperty(id, 'line-opacity', this.isDraftSafeRoutePolyline(id) ? 1 : 0.8);
          this.map.setPaintProperty(id, 'line-dasharray', this.isDraftSafeRoutePolyline(id) ? [1, 0] : [2, 2]);
        } catch {
          /* ignore */
        }
      }
    } else {
      if (this.map.getLayer(id)) {
        this.map.removeLayer(id);
      }
      if (this.map.getSource(id)) {
        this.map.removeSource(id);
      }
    }

    const features: GeoJSON.Feature<GeoJSON.Point>[] = coordinates.map((coord) => ({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: coord
      }
    }));
    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features
    };

    if (!coordinates.length) {
      this.unbindSafeRouteClickListeners(id);
      this.clearDraftPolylineEditHandles(id);
      this.removePathVertexCircles(id);
      this.polylines.delete(id);
      return;
    }

    if (this.shouldShowPathVertexCircles(id)) {
      if (this.map.getSource(pointsSourceId)) {
        (this.map.getSource(pointsSourceId) as maplibregl.GeoJSONSource).setData(fc);
      } else {
        this.map.addSource(pointsSourceId, {
          type: 'geojson',
          data: fc
        });
      }

      if (!this.map.getLayer(pointsLayerId)) {
        this.map.addLayer({
          id: pointsLayerId,
          type: 'circle',
          source: pointsSourceId,
          paint: {
            'circle-radius': 6,
            'circle-color': color,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2
          }
        });
      } else {
        try {
          this.map.setPaintProperty(pointsLayerId, 'circle-color', color);
        } catch {
          /* ignore */
        }
      }
    } else {
      this.removePathVertexCircles(id);
    }

    this.bindSafeRouteClickListeners(id, coordinates, color);
    this.syncDraftPolylineEditHandles(id, coordinates, color);
  }

  removePath(id: string): void {
    if (!this.map) return;
    this.unbindSafeRouteClickListeners(id);
    this.clearDraftPolylineEditHandles(id);
    this.polylineDraftActiveDrags.delete(id);
    if (this.map.getLayer(id)) {
      this.map.removeLayer(id);
    }
    if (this.map.getSource(id)) {
      this.map.removeSource(id);
    }
    const pointsLayerId = `${id}-points`;
    const pointsSourceId = `${id}-points-source`;
    if (this.map.getLayer(pointsLayerId)) {
      this.map.removeLayer(pointsLayerId);
    }
    if (this.map.getSource(pointsSourceId)) {
      this.map.removeSource(pointsSourceId);
    }
    this.polylines.delete(id);
  }

  dragToLocation(markers: any[], geolocation: { lat: any, lng: any }): void {
    if (!this.map || !geolocation || geolocation.lat === undefined || geolocation.lng === undefined) return;

    const latLng: [number, number] = [geolocation.lng, geolocation.lat];
    // Use easeTo to set both center and zoom atomically, clamped to MAX_ZOOM
    this.map.easeTo({
      center: latLng,
      zoom: Math.min(16, this.MAX_ZOOM),
      duration: 500
    });
    this.refreshViewportAfterCameraMove();

    if (markers) {
      this.markersOriginal = [];

      for (let mark of markers) {
        let marker = this.prepareMarker(mark);
        this.markersOriginal.push(marker);
      }

      this.adjustPoints();

      // Ensure untrack fully completes before re-tracking
      this.unTrackMarkersAsync(['investigator', 'investigator-group', 'camera']).then(() => {
        this.markers = [];
        let markersOriginal = JSON.parse(JSON.stringify(this.markersOriginalAdjusted));
        this.markers = markersOriginal;
        this.trackMarkers(['investigator', 'investigator-group', 'camera']);
        this.updateMarkersVisual();
      });
    }
  }

  /**
   * Programmatically move the offline map to a specific center and zoom.
   */
  goToCenter(geolocation: { lat: any, lng: any }, zoom: number = 15): void {
    console.log('goToCenter', geolocation, zoom);
    if (!this.map || !geolocation || geolocation.lat === undefined || geolocation.lng === undefined) {
      return;
    }
    const latLng: [number, number] = [geolocation.lng, geolocation.lat];
    this.map.easeTo({
      center: latLng,
      zoom: Math.min(zoom ?? 16, this.MAX_ZOOM),
      duration: 500
    });
    this.refreshViewportAfterCameraMove();
  }

  /** Pan to coordinates without changing zoom (repeat focus on same building). */
  panToCenter(geolocation: { lat: any, lng: any }): void {
    if (!this.map || !geolocation || geolocation.lat === undefined || geolocation.lng === undefined) {
      return;
    }
    const latLng: [number, number] = [geolocation.lng, geolocation.lat];
    this.map.easeTo({
      center: latLng,
      duration: 500
    });
    this.refreshViewportAfterCameraMove();
  }

  updateAndDragMarkers(newMarkers: any[] = []): void {
    for (let mark of newMarkers) {
      let marker = this.prepareMarker(mark);
      this.updateOriginalMarker(marker);
    }

    this.adjustPoints();
    this.unTrackMarkersAsync(['investigator', 'investigator-group', 'camera']).then(() => {
      this.markers = [];
      let markersOriginal = JSON.parse(JSON.stringify(this.markersOriginalAdjusted));
      this.markers = markersOriginal;
      this.updateMarkersVisual();

      if (this.map) {
        this.map.setZoom(Math.min(16, this.MAX_ZOOM));
      }
    });
  }

  searchByText(text: string, options?: { emitMapSelect?: boolean }): void {
    const trimmedText = text?.trim();
    if (!trimmedText) {
      console.warn('searchByText called with empty text');
      return;
    }

    const language = this.getPreferredLanguage();
    const emitMapSelect = options?.emitMapSelect !== false;

    this.nominatimSearch(trimmedText, language)
      .then((result) => {
        if (!result) {
          console.warn('No location found for searched text', trimmedText);
          this.layoutUtilsService.showNotification(this.translate.instant('No location found for searched text.'), this.translate.instant('Dismiss'));
          return;
        }

        this.center = { lat: result.lat, lng: result.lng };

        if (this.map) {
          this.map.setCenter([result.lng, result.lat]);
          this.map.setZoom(Math.min(16, this.MAX_ZOOM));
        }

        if (emitMapSelect) {
          this.mapSelect.emit({ lat: result.lat, lng: result.lng });
        }

        if (result.displayName) {
          if (language === 'ja') {
            this.addressJPPinData.emit(result.displayName);
          } else {
            this.addressPinData.emit(result.displayName);
          }
        }
      })
      .catch((error) => {
        console.error('searchByText failed', error);
      });
  }

  get markersElem(): any {
    return {
      googleMap: this.map,
      getCenter: () => {
        if (this.map) {
          const center = this.map.getCenter();
          return {
            toJSON: () => ({ lat: center.lat, lng: center.lng })
          };
        }
        return null;
      },
      panTo: (latLng: [number, number] | { lat: number, lng: number }) => {
        if (this.map) {
          if (Array.isArray(latLng)) {
            this.map.panTo(latLng);
          } else {
            this.map.panTo([latLng.lng, latLng.lat]);
          }
        }
      },
      setZoom: (zoom: number) => {
        if (this.map) {
          this.map.setZoom(zoom);
        }
      },
      fitBounds: (bounds: [[number, number], [number, number]] | any, padding?: number) => {
        if (this.map) {
          if (Array.isArray(bounds) && bounds.length === 2) {
            const boundsArray: [[number, number], [number, number]] = [
              [bounds[0][0], bounds[0][1]],
              [bounds[1][0], bounds[1][1]]
            ];
            this.map.fitBounds(boundsArray, { padding: padding || 0 });
          } else if (bounds && bounds.getNorthEast && bounds.getSouthWest) {
            // Google Maps bounds object
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const boundsArray: [[number, number], [number, number]] = [
              [sw.lng(), sw.lat()],
              [ne.lng(), ne.lat()]
            ];
            this.map.fitBounds(boundsArray, { padding: padding || 0 });
          }
        }
      }
    };
  }
  toggleRefresh() {
    if (this.map) {
      this.map.resize();
    }
  }

  clearNonExistingMarkers(markerType: string, currentMarkers: any[]): void {
    if (!markerType || !currentMarkers) {
      return;
    }

    // Create a Set of current marker IDs for quick lookup
    const currentMarkerIds = new Set(
      currentMarkers
        .filter(m => m && m._id)
        .map(m => m._id)
    );

    // Filter out markers that match the markerType but are not in the current markers array
    const markersToRemove = this.markersOriginal.filter(marker =>
      marker.markerType === markerType &&
      marker._id &&
      !currentMarkerIds.has(marker._id)
    );

    if (markersToRemove.length > 0) {
      // Remove markers from markersOriginal
      const idsToRemove = new Set(markersToRemove.map(m => m._id));
      this.markersOriginal = this.markersOriginal.filter(marker =>
        !(marker.markerType === markerType && marker._id && idsToRemove.has(marker._id))
      );

      // Remove markers from markerRefs and remove them from the map
      idsToRemove.forEach(id => {
        const markerId = `marker_${id}`;
        if (this.markerRefs.has(markerId)) {
          try {
            const marker = this.markerRefs.get(markerId);
            if (marker) {
              marker.remove();
            }
          } catch (err) {
            console.warn('Error removing marker from map:', err);
          }
          this.markerRefs.delete(markerId);
        }
      });

      // Refresh the display
      this.showMarkersIfReady([], false);
    }
  }

  /**
   * Adds an interactive sector (FOV cone) to the map.
   * @param id Unique identifier for the sector
   * @param options Configuration for the sector (center, radius, azimuth, fov)
   */
  addSector(id: string, options: {
    center: { lat: number, lng: number },
    radius: number,
    azimuth?: number,
    fov?: number,
    startAngle?: number,
    endAngle?: number,
    title?: string,
    description?: string,
    address?: string,
    editable?: boolean,
    color?: string,
    data?: any,
    showDots?: boolean,
    showTooltip?: boolean,
    clickable?: boolean,
    showBorder?: boolean,
    fillOpacity?: number,
    lineOpacity?: number
  }) {
    if (!this.map) return;

    const showTooltip = options.showTooltip !== false;
    const clickable = options.clickable !== false;
    const showBorder = options.showBorder !== false;
    const fillOpacity = options.fillOpacity !== undefined ? options.fillOpacity : 0.2;
    const lineOpacity = options.lineOpacity !== undefined ? options.lineOpacity : 1;

    // Remove existing sector/box with same ID if any
    if (this.sectors.has(id)) {
      this.removeSector(id);
    }
    if (this.boxes.has(id)) {
      this.removeBox(id);
    }

    let azimuth = options.azimuth || 0;
    let fov = options.fov || 60;

    // If start/end angles are provided, calculate azimuth and fov
    if (options.startAngle !== undefined && options.endAngle !== undefined) {
      let diff = options.endAngle - options.startAngle;
      if (diff < 0) diff += 360; // Handle wrapping
      fov = diff;
      azimuth = (options.startAngle + diff / 2) % 360;
    }

    const sector = {
      id: id,
      center: options.center,
      radius: options.radius,
      azimuth: azimuth,
      fov: fov,
      data: options.data,
      centerMarker: undefined as any,
      controlMarker: undefined as any
    };

    this.sectors.set(id, sector);

    // Calculate polygon coordinates
    const coordinates = GeoUtils.calculateSectorPolygon(
      sector.center,
      sector.radius,
      sector.azimuth,
      sector.fov
    );

    const sourceId = `sector-source-${id}`;
    const layerId = `sector-layer-${id}`;
    const outlineLayerId = `sector-outline-layer-${id}`;

    if (!this.map.getSource(sourceId)) {
      const beforeId = this.findFirstLabelLayerId();
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          },
          properties: {}
        }
      });

      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {},
        paint: { 'fill-color': options.color || '#ff0000', 'fill-opacity': fillOpacity }
      }, beforeId);

      if (showBorder) {
        this.map.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          layout: {},
          paint: {
            'line-color': options.color || '#ff0000',
            'line-width': 2,
            'line-opacity': lineOpacity
          }
        }, beforeId);
      }

      const hasTooltip = this.isHoverable && showTooltip && (options.description || options.address);
      if (hasTooltip || clickable) {
        this.map.on('mouseenter', layerId, () => {
          if (clickable || this.isHoverable) {
            this.map.getCanvas().style.cursor = 'pointer';
          }
        });
        this.map.on('mouseleave', layerId, () => {
          this.map.getCanvas().style.cursor = '';
        });
      }

      // Add tooltip
      if (hasTooltip) {
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false
        });

        const tooltipContent = `<div style="color: black; padding: 5px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${options.description || ''}</div>
            <div style="font-size: 12px;">${options.address || ''}</div>
        </div>`;

        this.map.on('mouseenter', layerId, (e) => {
          popup.setLngLat(e.lngLat)
            .setHTML(tooltipContent)
            .addTo(this.map);
        });

        this.map.on('mousemove', layerId, (e) => {
          popup.setLngLat(e.lngLat);
        });

        this.map.on('mouseleave', layerId, () => {
          popup.remove();
        });
      }

      // Add click listener
      if (clickable) {
        // Store/Attach click listener
        const clickListener = () => {
          this.shapeClick.emit(options.data || id);
        };
        this.map.on('click', layerId, clickListener);

        // Store reference for cleanup
        (sector as any).clickListener = clickListener;
      }
    }

    if (options.showDots || options.editable) {
      // Add Center Marker (Draggable if editable)
      const centerEl = document.createElement('div');
      centerEl.className = 'sector-center-handle';
      centerEl.style.width = '24px';
      centerEl.style.height = '24px';
      centerEl.style.backgroundColor = 'transparent';
      centerEl.style.cursor = options.editable ? 'move' : (clickable ? 'pointer' : 'default');
      centerEl.style.zIndex = '1000'; // Make sure handles are above map
      if (options.title) {
        centerEl.title = options.title; // tooltip
      }

      const visualCenter = document.createElement('div');
      visualCenter.style.width = '12px';
      visualCenter.style.height = '12px';
      visualCenter.style.backgroundColor = options.color || 'rgba(255, 0, 0, 0.5)';
      visualCenter.style.border = '2px solid white';
      visualCenter.style.borderRadius = '50%';
      visualCenter.style.margin = '6px auto'; // Center inside the hit area
      visualCenter.style.boxShadow = '0 0 3px rgba(0,0,0,0.3)';
      centerEl.appendChild(visualCenter);

      sector.centerMarker = new maplibregl.Marker({
        element: centerEl,
        draggable: !!options.editable
      })
        .setLngLat([options.center.lng, options.center.lat])
        .addTo(this.map);

      if (clickable) {
        centerEl.addEventListener('click', () => {
          this.shapeClick.emit(options.data || id);
        });
      }

      if (options.editable) {
        sector.centerMarker.on('drag', () => {
          const lngLat = sector.centerMarker!.getLngLat();
          sector.center = { lat: lngLat.lat, lng: lngLat.lng };
          this.updateSector(id);
          this.updateSectorControlMarker(id);
        });
      }

      // Add Control Marker
      const controlEl = document.createElement('div');
      controlEl.className = 'sector-control-handle';
      controlEl.style.width = '16px';
      controlEl.style.height = '16px';
      controlEl.style.backgroundColor = 'white';
      controlEl.style.border = `2px solid ${options.color || 'red'}`;
      controlEl.style.borderRadius = '50%';
      controlEl.style.cursor = options.editable || clickable ? 'pointer' : 'default';
      controlEl.style.zIndex = '1000';

      // Initial position for control handle (at the end of the radius along the azimuth)
      const controlPos = GeoUtils.destinationPoint(
        options.center.lat,
        options.center.lng,
        options.radius,
        sector.azimuth
      );

      sector.controlMarker = new maplibregl.Marker({
        element: controlEl,
        draggable: !!options.editable
      })
        .setLngLat([controlPos.lng, controlPos.lat])
        .addTo(this.map);

      if (clickable) {
        controlEl.addEventListener('click', () => {
          this.shapeClick.emit(options.data || id);
        });
      }

      if (options.editable) {
        sector.controlMarker.on('drag', () => {
          const lngLat = sector.controlMarker!.getLngLat();
          const currentLatLng = { lat: lngLat.lat, lng: lngLat.lng };
          const center = sector.center;

          const distance = GeoUtils.getDistance(center, currentLatLng);
          sector.radius = distance;

          const bearing = GeoUtils.getBearing(center, currentLatLng);
          sector.azimuth = bearing;

          this.updateSector(id);
        });
      }
    }
  }

  private updateSectorIds = new Map<string, number>();

  private updateSector(id: string) {
    if (!this.map) return;
    const sector = this.sectors.get(id);
    if (!sector) return;

    if (this.updateSectorIds.has(id)) cancelAnimationFrame(this.updateSectorIds.get(id)!);

    const rAfId = requestAnimationFrame(() => {
      if (!this.map) return;
      const currentSector = this.sectors.get(id);
      if (!currentSector) return;

      const coordinates = GeoUtils.calculateSectorPolygon(
        currentSector.center,
        currentSector.radius,
        currentSector.azimuth,
        currentSector.fov
      );

      const sourceId = `sector-source-${id}`;
      const source = this.map.getSource(sourceId) as any;
      if (source) {
        source.setData({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          },
          properties: {}
        });
      }
      this.updateSectorIds.delete(id);
    });
    this.updateSectorIds.set(id, rAfId);
  }

  private updateSectorControlMarker(id: string) {
    if (!this.map) return;
    const sector = this.sectors.get(id);
    if (!sector || !sector.controlMarker) return; // If not editable, controlMarker is undefined

    const controlPos = GeoUtils.destinationPoint(
      sector.center.lat,
      sector.center.lng,
      sector.radius,
      sector.azimuth
    );

    sector.controlMarker.setLngLat([controlPos.lng, controlPos.lat]);
  }

  // --- Box Shape Implementation ---

  private updateBoxIds = new Map<string, number>();

  addBox(id: string, options: {
    center: { lat: number, lng: number },
    width: number,
    height: number,
    azimuth: number,
    title?: string,
    description?: string,
    address?: string,
    editable?: boolean,
    color?: string,
    data?: any,
    showDots?: boolean,
    showTooltip?: boolean,
    clickable?: boolean,
    showBorder?: boolean
  }) {
    if (!this.map) return;

    const showTooltip = options.showTooltip !== false;
    const clickable = options.clickable !== false;
    const showBorder = options.showBorder !== false;

    // Remove if exists (either box or sector)
    if (this.boxes.has(id)) {
      this.removeBox(id);
    }
    if (this.sectors.has(id)) {
      this.removeSector(id);
    }

    const box = {
      id: id,
      center: options.center,
      width: options.width,
      height: options.height,
      azimuth: options.azimuth,
      centerMarker: undefined as MapLibreMarker | undefined,
      controlMarker: undefined as MapLibreMarker | undefined
    };

    this.boxes.set(id, box);

    const coordinates = GeoUtils.calculateBoxPolygon(
      box.center,
      box.width,
      box.height,
      box.azimuth
    );

    const sourceId = `box-source-${id}`;
    const layerId = `box-layer-${id}`;
    const outlineLayerId = `box-outline-layer-${id}`;

    if (!this.map.getSource(sourceId)) {
      const beforeId = this.findFirstLabelLayerId();
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          },
          properties: {}
        }
      });

      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {},
        paint: { 'fill-color': options.color || '#0000ff', 'fill-opacity': 0.2 } // Blue for box
      }, beforeId);

      if (showBorder) {
        this.map.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          layout: {},
          paint: { 'line-color': options.color || '#0000ff', 'line-width': 2 }
        }, beforeId);
      }

      const hasTooltip = this.isHoverable && showTooltip && (options.description || options.address);
      if (hasTooltip || clickable) {
        this.map.on('mouseenter', layerId, () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', layerId, () => {
          this.map.getCanvas().style.cursor = '';
        });
      }

      // Add tooltip
      if (hasTooltip) {
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false
        });

        const tooltipContent = `<div style="color: black; padding: 5px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${options.description || ''}</div>
            <div style="font-size: 12px;">${options.address || ''}</div>
        </div>`;

        this.map.on('mouseenter', layerId, (e) => {
          popup.setLngLat(e.lngLat)
            .setHTML(tooltipContent)
            .addTo(this.map);
        });

        this.map.on('mousemove', layerId, (e) => {
          popup.setLngLat(e.lngLat);
        });

        this.map.on('mouseleave', layerId, () => {
          popup.remove();
        });
      }

      if (clickable) {
        // Store/Attach click listener
        const clickListener = () => {
          this.shapeClick.emit(options.data || id);
        };
        this.map.on('click', layerId, clickListener);

        // Store reference for cleanup
        (box as any).clickListener = clickListener;
      }
    } else {
      (this.map.getSource(sourceId) as any).setData({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        properties: {}
      });

      // Update colors for box
      if (this.map.getLayer(layerId)) {
        this.map.setPaintProperty(layerId, 'fill-color', '#0000ff');
      }
      if (this.map.getLayer(outlineLayerId)) {
        this.map.setPaintProperty(outlineLayerId, 'line-color', '#0000ff');
      }
    }

    if (options.showDots || options.editable) {
      // Centeral Handle
      const centerEl = document.createElement('div');
      centerEl.className = 'box-center-handle';
      centerEl.style.width = '24px';
      centerEl.style.height = '24px';
      centerEl.style.backgroundColor = 'transparent';
      centerEl.style.cursor = options.editable ? 'move' : (clickable ? 'pointer' : 'default');
      centerEl.style.zIndex = '1000';
      if (options.title) {
        centerEl.title = options.title; // tooltip
      }

      const visualCenter = document.createElement('div');
      visualCenter.style.width = '12px';
      visualCenter.style.height = '12px';
      visualCenter.style.backgroundColor = 'rgba(0, 0, 255, 0.5)';
      visualCenter.style.border = '2px solid white';
      visualCenter.style.borderRadius = '50%'; // Keep circular handle
      visualCenter.style.margin = '6px auto';
      visualCenter.style.boxShadow = '0 0 3px rgba(0,0,0,0.3)';
      centerEl.appendChild(visualCenter);

      box.centerMarker = new maplibregl.Marker({
        element: centerEl,
        draggable: !!options.editable
      })
        .setLngLat([options.center.lng, options.center.lat])
        .addTo(this.map);

      if (clickable) {
        centerEl.addEventListener('click', () => {
          this.shapeClick.emit(options.data || id);
        });
      }

      if (options.editable) {
        box.centerMarker.on('drag', () => {
          const lngLat = box.centerMarker!.getLngLat();
          box.center = { lat: lngLat.lat, lng: lngLat.lng };
          this.updateBox(id);
          this.updateBoxControlMarker(id);
        });
      }

      // Control Handle (Corner)
      const controlEl = document.createElement('div');
      controlEl.className = 'box-control-handle';
      controlEl.style.width = '16px';
      controlEl.style.height = '16px';
      controlEl.style.backgroundColor = 'white';
      controlEl.style.border = '2px solid blue';
      controlEl.style.borderRadius = '0%'; // Square handle for box
      controlEl.style.cursor = options.editable || clickable ? 'pointer' : 'default';
      controlEl.style.zIndex = '1000';

      // Position at top-right corner initially
      const radius = Math.sqrt(Math.pow(options.width / 2, 2) + Math.pow(options.height / 2, 2));
      const angleOffset = GeoUtils.toDegrees(Math.atan2(options.width, options.height));
      const controlPos = GeoUtils.destinationPoint(options.center.lat, options.center.lng, radius, options.azimuth + angleOffset);

      box.controlMarker = new maplibregl.Marker({
        element: controlEl,
        draggable: !!options.editable
      })
        .setLngLat([controlPos.lng, controlPos.lat])
        .addTo(this.map);

      if (clickable) {
        controlEl.addEventListener('click', () => {
          this.shapeClick.emit(options.data || id);
        });
      }

      if (options.editable) {
        box.controlMarker.on('drag', () => {
          const lngLat = box.controlMarker!.getLngLat();
          const currentLatLng = { lat: lngLat.lat, lng: lngLat.lng };

          const center = box.center;
          const newRadius = GeoUtils.getDistance(center, currentLatLng);

          const oldRadius = Math.sqrt(Math.pow(box.width / 2, 2) + Math.pow(box.height / 2, 2));
          const scale = newRadius / oldRadius;

          box.width *= scale;
          box.height *= scale;

          const currentBearing = GeoUtils.getBearing(center, currentLatLng);
          const cornerAngle = GeoUtils.toDegrees(Math.atan2(box.width, box.height));
          box.azimuth = (currentBearing - cornerAngle + 360) % 360;

          this.updateBox(id);
        });
      }
    }
  }

  private updateBox(id: string) {
    const box = this.boxes.get(id);
    if (!box || !this.map) return;

    if (this.updateBoxIds.has(id)) cancelAnimationFrame(this.updateBoxIds.get(id)!);

    const rAfId = requestAnimationFrame(() => {
      if (!this.map) return;
      const currentBox = this.boxes.get(id);
      if (!currentBox) return;

      const coordinates = GeoUtils.calculateBoxPolygon(
        currentBox.center,
        currentBox.width,
        currentBox.height,
        currentBox.azimuth
      );

      const sourceId = `box-source-${id}`;
      const source = this.map.getSource(sourceId) as any;
      if (source) {
        source.setData({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [coordinates] },
          properties: {}
        });
      }
      this.updateBoxIds.delete(id);
    });
    this.updateBoxIds.set(id, rAfId);
  }

  private updateBoxControlMarker(id: string) {
    const box = this.boxes.get(id);
    if (!box || !box.controlMarker) return;

    const radius = Math.sqrt(Math.pow(box.width / 2, 2) + Math.pow(box.height / 2, 2));
    const angleOffset = GeoUtils.toDegrees(Math.atan2(box.width, box.height));
    const controlPos = GeoUtils.destinationPoint(
      box.center.lat,
      box.center.lng,
      radius,
      box.azimuth + angleOffset
    );
    box.controlMarker.setLngLat([controlPos.lng, controlPos.lat]);
  }

  removeBox(id?: string) {
    if (id) {
      const box = this.boxes.get(id);
      if (box) {
        if (box.centerMarker) box.centerMarker.remove();
        if (box.controlMarker) box.controlMarker.remove();

        if (this.map) {
          const sourceId = `box-source-${id}`;
          const layerId = `box-layer-${id}`;
          const outlineLayerId = `box-outline-layer-${id}`;

          // REMOVE LISTENERS
          if ((box as any).clickListener) {
            this.map.off('click', layerId, (box as any).clickListener);
          }

          if (this.map.getLayer(outlineLayerId)) this.map.removeLayer(outlineLayerId);
          if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
        }
        this.boxes.delete(id);
      }
    } else {
      // Clear all
      this.boxes.forEach((_, key) => this.removeBox(key));
    }
  }

  removeSector(id?: string) {
    if (id) {
      const sector = this.sectors.get(id);
      if (sector) {
        if (sector.centerMarker) sector.centerMarker.remove();
        if (sector.controlMarker) sector.controlMarker.remove();

        if (this.map) {
          const sourceId = `sector-source-${id}`;
          const layerId = `sector-layer-${id}`;
          const outlineLayerId = `sector-outline-layer-${id}`;

          // REMOVE LISTENERS
          if ((sector as any).clickListener) {
            this.map.off('click', layerId, (sector as any).clickListener);
          }

          if (this.map.getLayer(outlineLayerId)) this.map.removeLayer(outlineLayerId);
          if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
        }
        this.sectors.delete(id);
      }
    } else {
      // Clear all
      this.sectors.forEach((_, key) => this.removeSector(key));
    }
  }

  private findFirstLabelLayerId(): string | undefined {
    if (!this.map) return undefined;
    const style = this.map.getStyle();
    if (!style || !style.layers) return undefined;

    // Find first symbol layer (labels) to insert our shapes before it
    const firstSymbolLayer = style.layers.find(l => l.type === 'symbol');
    return firstSymbolLayer ? firstSymbolLayer.id : undefined;
  }

  addNote(type: string) {
    const address = (this.contextMenuAddress || "").trim();
    const lngLat = this.contextMenuLngLat;
    this.clearContextMenuState();
    this.mapService.addFromMap.next({
      type: type,
      note: {
        address,
        address_jp: address,
        geolocation: lngLat ? JSON.stringify({ lat: lngLat.lat, lng: lngLat.lng }) : undefined,
      },
    });
  }

  openAreaOfInterestFromContextMenu(): void {
    const address = (this.contextMenuAddress || "").trim();
    const lngLat = this.contextMenuLngLat;
    this.clearContextMenuState();
    if (!lngLat) {
      return;
    }
    this.mapService.mapContextMenuCommand.next({
      command: "areaOfInterest",
      point: { lat: lngLat.lat, lng: lngLat.lng, address },
    });
  }

  openGoToLocationFromContextMenu(): void {
    const address = (this.contextMenuAddress || "").trim();
    const lngLat = this.contextMenuLngLat;
    this.clearContextMenuState();
    if (!lngLat) {
      return;
    }
    this.mapService.mapContextMenuCommand.next({
      command: "goToLocation",
      point: { lat: lngLat.lat, lng: lngLat.lng, address },
    });
  }

  openMapAnnotationFromContextMenu(): void {
    const address = (this.contextMenuAddress || "").trim();
    const lngLat = this.contextMenuLngLat;
    this.clearContextMenuState();
    if (!lngLat) {
      return;
    }
    this.mapService.mapContextMenuCommand.next({
      command: "mapAnnotation",
      point: { lat: lngLat.lat, lng: lngLat.lng, address },
    });
  }
  openAddPatrolCameraFromContextMenu(): void {
    const address = (this.contextMenuAddress || "").trim();
    const lngLat = this.contextMenuLngLat;
    this.clearContextMenuState();
    if (!lngLat) {
      return;
    }
    this.mapService.mapContextMenuCommand.next({
      command: "addPatrolCamera",
      point: { lat: lngLat.lat, lng: lngLat.lng, address },
    });
  }
}
